/**
 * Translation dictionary for the client. English is the canonical source —
 * other languages fall back to the English value when a key is missing.
 *
 * Convention for keys:
 *   <screen-or-area>.<short-snake_case-handle>
 *   e.g. 'home.quick_match', 'room.invite_friend', 'chat.add_friend'
 *
 * Interpolation: pass an `{ code: 'AB7K9P' }` style vars object to t() and
 * use `{code}` in the string. Missing vars are left as-is.
 */

export type Lang = 'en' | 'vi' | 'zh' | 'ja' | 'ko' | 'th';

export const LANG_LABEL: Record<Lang, string> = {
  en: 'English',
  vi: 'Tiếng Việt',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  th: 'ภาษาไทย',
};

export const LANG_FLAG: Record<Lang, string> = {
  en: '🇬🇧',
  vi: '🇻🇳',
  zh: '🇨🇳',
  ja: '🇯🇵',
  ko: '🇰🇷',
  th: '🇹🇭',
};

// ── Canonical English ────────────────────────────────────────────────
const en = {
  // Common
  'common.cancel': 'CANCEL',
  'common.close': 'CLOSE',
  'common.preview': 'PREVIEW',
  'common.confirm': 'CONFIRM',
  'common.back': 'BACK',
  'common.loading': 'Loading…',
  'common.error': 'Error',
  'common.ok': 'OK',
  'common.online': 'online',
  'common.offline': 'offline',
  'common.send': 'Send',
  'common.edit': 'Edit',
  'common.save': 'Save',
  'common.network_error': 'Network error',
  'chars.idle_preview': 'IDLE + WIN PREVIEW',
  'shop.finisher_preview': 'FINISHER PREVIEW',
  'ads.none_available': 'No ads available',
  'ads.none_available_body': 'Please try again later.',
  'playgate.title': 'Play in the app',
  'playgate.body': 'Matches are available in the Don’t Miss app. Download it free to play.',
  'playgate.ios': 'Download on the App Store',
  'playgate.android': 'Get it on Google Play',
  'update.hard_title': 'Update required',
  'update.hard_body': 'A new version of Don’t Miss is required to keep playing. Please update to continue.',
  'update.soft_title': 'Update available',
  'update.soft_body': 'A new version of Don’t Miss is available with improvements and fixes.',
  'update.cta': 'Update now',
  'update.later': 'Later',
  'app.announcement_title': 'Announcement',
  'app.maintenance_title': 'Under maintenance',
  'app.maintenance_default': 'The app is under maintenance. Please come back later.',
  'onboarding.welcome_title': 'Welcome to Don’t Miss!',
  'onboarding.welcome_body': 'Tap the matching colors to climb the wall and beat your rival. First to the top wins — don’t miss a tap!',
  'onboarding.name_title': 'Choose your name',
  'onboarding.name_body': 'This is how other players see you. Your first change is free.',
  'onboarding.shout_title': 'Set a victory shout',
  'onboarding.shout_body': 'Your rival sees this when you win. Make it count! You can skip and set it later.',
  'onboarding.next': 'Next',
  'onboarding.finish': 'Start playing',
  'onboarding.skip': 'Skip for now',
  'common.home': 'Home',
  'common.back_home': 'Back to home',
  'common.dismiss': 'Dismiss',

  // Home
  'home.quick_match': '⚡ QUICK MATCH',
  'home.daily_ad': 'Daily bonus',
  'quests.title': 'Daily Quests',
  'quests.button': 'Daily Quests',
  'quests.reset_note': 'Resets every day',
  'quests.share': 'Share on social media',
  'quests.play': 'Play 5 matches',
  'quests.win': 'Win 10 matches',
  'quests.claim': 'Claim',
  'quests.claimed': 'Claimed',
  'quests.share_btn': 'Share',
  'quests.share_text': "🧗‍♂️ I'm climbing the ranks in Don't Miss! A lightning-fast 1v1 color-tap duel — tap the right color, race up the wall, crush your rival. Bet you can't beat my time! 🔴🔵🟡🟣 Come duel me 👇",
  'home.daily_ad_watching': 'Loading ad…',
  'home.create_room': '+ CREATE ROOM',
  'home.join_room': '↪ JOIN BY CODE',
  'home.modes_subtitle': '⚡ Solo 1v1 · Duo · Team · Tournament soon',
  'home.avg_wait': 'Avg wait 4s',
  'home.could_not_create_room': 'Could not create room',
  'home.code_too_short': 'Room code is too short',
  'home.could_not_join': 'Could not join room',
  'home.join_room_title': 'JOIN ROOM',
  'home.equipped_skills': 'EQUIPPED SKILLS',
  'home.buy_more': 'BUY MORE ›',
  'home.skill_buy_cta': 'BUY',
  'home.open_profile': 'Open profile',
  'home.role_climber': 'Climber',
  'home.win_streak': 'WIN STREAK',
  'home.join_btn': 'JOIN',
  'home.display_name': 'Display name',
  'home.display_name_ph': 'Your in-game name',
  'home.victory_shout': 'Victory shout',
  'home.victory_shout_ph': 'e.g. GG! · Too easy 💪',
  'home.first_change_free': 'First change free',
  'home.field_none': '— none —',
  'home.not_enough_gems': 'Not enough gems',
  'home.max_chars': 'Max {n} characters',
  'profanity.blocked': "This word isn't allowed. Please choose another.",
  'home.save_cost': 'Save · {cost}💎',

  // Room
  'room.leave': '← LEAVE',
  'room.chat': '◐ CHAT',
  'room.code_label': 'ROOM CODE',
  'room.tap_to_copy': 'tap to copy',
  'room.copied': '✓ COPIED',
  'room.team_a': 'TEAM A',
  'room.team_b': 'TEAM B',
  'room.join_slot': 'JOIN SLOT',
  'room.you_suffix': '(YOU)',
  'room.ready_label': 'READY',
  'room.tap_to_ready': 'TAP TO READY UP',
  'room.invite_friend': '+ INVITE FRIEND',
  'room.share_to_chat': '📢 SHARE TO CHAT',
  'room.shared_to_chat': '✓ SHARED',
  'room.open_chat_tip': 'Open team chat',
  'room.chat_title': 'TEAM CHAT',
  'room.chat_placeholder': 'Message your team…',
  'room.chat_empty': 'No messages yet. Say hi to your team!',
  'room.chat_send': 'SEND',
  'room.chat_expand': 'Expand chat',
  'room.chat_collapse': 'Collapse',
  'room.kick_tip': 'Kick',
  'room.waiting_host': 'Waiting for host to press START…',
  'room.invite_modal_title': 'INVITE FRIENDS',
  'room.no_friends_to_invite': 'No friends to invite yet. Open chat dock → tap an avatar in GLOBAL.',
  'room.in_room': 'IN ROOM',
  'room.invited': '✓ INVITED',
  'room.invite_btn': 'INVITE',
  'room.offline_send_hint': "Friend is offline — won't receive it",
  'room.send_invite': 'Send invite',
  'room.start': '▶ START',
  'room.start_quick': '▶ START · QUICK',
  'room.start_duel': '▶ START · DUEL',
  'room.start_team': '▶ START · TEAM',
  'room.mode.empty_desc': 'Invite a friend to a slot to start',
  'room.mode.quick': 'QUICK MATCH',
  'room.mode.quick_desc': 'Starting will queue you for a random solo match',
  'room.mode.solo_duel': 'SOLO DUEL · 1v1',
  'room.mode.solo_duel_desc': 'Direct duel between two sides — bypasses matchmaking',
  'room.mode.duo_rank': 'DUO RANK · 2v2',
  'room.mode.duo_rank_desc': 'Queue with a partner against 2 random opponents',
  'room.mode.team_duel': 'TEAM DUEL · 2v2',
  'room.mode.team_duel_desc': 'Direct 2v2 between two teams',
  'room.team_mode_label': 'TEAM MODE',
  'room.tmode.alternating': 'ALTERNATING',
  'room.tmode.alternating_desc': 'Teammates take turns every 5 holds. Team combo unlocks skills.',
  'room.tmode.split_color': 'SPLIT COLOR',
  'room.tmode.split_color_desc': 'Each player owns 2 colors. Combo 5 unlocks both skills.',
  'room.tmode.attack_defense': 'ATTACK · DEFENSE',
  'room.tmode.attack_defense_desc': 'One climbs, one casts skills by role.',
  'room.mode.imbalanced': 'IMBALANCED',
  'room.mode.imbalanced_desc': 'Balance both sides (1v1 or 2v2) to start',

  // Team 2v2 in-match status
  'match.team.your_turn': 'YOUR TURN — tap the wall',
  'match.team.teammate_turn': "Teammate's turn",
  'match.team.you_climb': 'YOU CLIMB — tap the wall',
  'match.team.you_cast': 'YOU CAST — support with skills',

  // Match
  'match.you_win': 'YOU WIN!',
  'match.you_lose': 'YOU LOSE',
  'match.summary': '▶ SUMMARY',
  'match.tip_label': 'TIP',
  'match.auto_continue': 'auto-continuing in {secs}s',
  'match.tap_color': 'Tap {color}',
  'match.quit_aria': 'Quit match',
  'match.combo': '×{n} COMBO',
  'match.you': 'YOU',
  'match.rival': 'RIVAL',

  // Result
  'result.match_result': 'MATCH RESULT',
  'result.victory': 'VICTORY!',
  'result.defeat': 'DEFEAT',
  'result.rewards': 'REWARDS',
  'result.x2_button': 'DOUBLE REWARDS',
  'result.x2_watching': 'Loading ad…',
  'result.x2_done': 'Rewards doubled!',
  'result.battle_stats': 'BATTLE STATS',
  'result.rematch': '▶ REMATCH',
  'result.home': 'HOME',
  'result.back_to_room': '▶ BACK TO ROOM',
  'result.back_to_war': '⚔️ BACK TO CLAN WAR',
  'result.leave_room': 'LEAVE ROOM',
  'result.opponent_shouts': 'OPPONENT SHOUTS',
  'result.coins': 'GOLD',
  'result.stat_speed': 'Speed',
  'result.stat_accuracy': 'Accuracy',
  'result.stat_combo_max': 'Combo Max',

  // Chat
  'chat.global': 'Global',
  'chat.clan': 'Clan',
  'chat.placeholder_clan': 'Message your clan…',
  'chat.dms': 'DMs',
  'chat.no_messages': 'No messages yet — send the first one!',
  'chat.no_dms': 'No messages yet. Tap an avatar in Global chat to add a friend or DM them.',
  'chat.copy_tip': 'Double-tap to copy',
  'chat.copied': 'Copied!',
  'chat.copy_hint': '💡 Double-tap any message to copy it',
  'chat.open_chat': 'Open chat',
  'chat.back_to_conversations': 'Back to conversations',
  'chat.share_room_btn': '📍 Share my room {code}',
  'chat.share_room_msg': 'Join my room! Tap to join 👉 [{code}]',
  'chat.join_room': '▶ JOIN ROOM',
  'chat.open_room': '▶ OPEN ROOM',
  'chat.placeholder_global': 'Chat with everyone…',
  'chat.placeholder_dm': 'Message {name}…',
  'chat.minimize': 'Minimize',
  'chat.close': 'Close',
  'chat.add_friend': '+ Add friend',
  'chat.username_placeholder': 'Player username',
  'chat.btn_add': 'Add',
  'chat.section.room_invites': 'Room invites · {n}',
  'chat.section.friend_requests': 'Friend requests · {n}',
  'chat.section.conversations': 'Conversations',
  'chat.invite.tagline': 'invited you to room {code}',
  'chat.invite.join': '▶ Join',
  'chat.invite.decline': 'Decline',
  'chat.request.tagline': 'wants to be your friend',
  'chat.request.accept': 'Accept request',
  'chat.request.decline': 'Decline request',
  'chat.open_dm': 'Open DM',
  'chat.view_profile_tip': 'View profile · add friend · DM',
  'chat.manage_friend_tip': 'View profile / manage friend',
  'chat.you_prefix': 'You: {text}',
  'chat.now_friends': 'You are now friends!',
  'chat.request_sent': 'Friend request sent',
  'chat.send_failed': 'Failed to send request',

  // User quick menu
  'qm.profile_load_failed': 'Could not load profile',
  'qm.rank': 'RANK',
  'qm.mmr': 'MMR',
  'qm.matches': 'MATCHES',
  'qm.win_rate': 'WIN RATE',
  'qm.add_friend': '+ ADD FRIEND',
  'qm.request_sent': '⏳ REQUEST SENT',
  'qm.accept_request': '✓ ACCEPT REQUEST',
  'qm.friends_unfriend': '✓ FRIENDS · unfriend',
  'qm.send_dm': '💬 SEND DM',
  'qm.invite_to_room': '🎮 INVITE TO ROOM {code}',
  'qm.invited_to_room': '✓ INVITED TO {code}',
  'qm.invite_failed': 'Invite failed',
  'qm.invited_toast': 'Invited to room {code}',
  'qm.unfriended': 'Unfriended',
  'qm.block': 'BLOCK',
  'qm.unblock': 'UNBLOCK',
  'qm.report': 'REPORT',
  'qm.blocked_toast': 'User blocked',
  'qm.unblocked_toast': 'User unblocked',
  'qm.reported_toast': 'Report submitted. Thank you.',
  'qm.report_confirm': 'Report this user for inappropriate behavior?',
  'qm.accepted': 'Accepted',

  // Shop / Skills
  'shop.skills.section': 'SKILLS — CONSUMABLES',
  'shop.skills.loading': 'Loading skills…',
  'shop.skills.owned': 'OWNED',
  'shop.skills.unlocks_at': '🔒 Unlocks at Level {level} (you are Lv{my})',
  'shop.skills.bought_today': 'Bought today: {n}/{max}',
  'shop.skills.ad_today': 'AD: {n}/{max}',
  'shop.skills.watching': 'Watching…',
  'shop.skills.purchase_failed': 'Purchase failed',
  'shop.skills.ad_failed': 'Ad reward failed',
  'shop.skills.added': '+1 {name}',
  'shop.skills.added_ad': '+1 {name} (AD)',
  'shop.disrupt.desc': "Scramble the opponent's next 3 hold colors.",
  'shop.shield.desc': '1s shield — cancels incoming disrupt + ignores wrong taps.',

  // TabBar
  'tab.shop': 'SHOP',
  'tab.ranks': 'RANKS',
  'tab.play': 'PLAY',
  'tab.bag': 'BAG',
  'tab.characters': 'HEROES',
  'tab.settings': 'SETTINGS',

  // Login
  'login.title': "DON'T MISS",
  'login.subtitle': 'Sign in to climb',
  'login.guest': 'PLAY AS GUEST',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.username': 'Username',
  'login.signin': 'SIGN IN',
  'login.signup': 'CREATE ACCOUNT',
  'login.switch_to_signup': 'No account yet? Create one',
  'login.switch_to_signin': 'Already have an account? Sign in',
  'login.continue_google': 'Continue with Google',
  'login.continue_apple': 'Sign in with Apple',
  'login.or': 'or',
  'login.error_invalid': 'Invalid credentials',
  'login.error_taken': 'Username or email already taken',
  'login.error_generic': 'Sign in failed',
  'login.oauth_not_configured': '{provider} login isn’t configured yet — try Email or Guest.',
  'login.server_unreachable': 'Server unreachable — check your connection.',
  'login.tagline': 'Speed-climb duels. 4 colors.',
  'login.tagline_winner': 'One winner.',
  'login.continue_email': 'Continue with Email',
  'login.creating_guest': 'Creating guest...',
  'login.play_guest': 'Play as Guest',
  'login.no_rewards': 'NO REWARDS',
  'login.terms_prefix': 'By continuing you agree to our',
  'login.terms': 'Terms',
  'login.privacy': 'Privacy',
  'login.not_configured': 'Not configured',
  'login.email_or_username': 'Email or username',
  'auth.welcome_back': 'Welcome back, climber.',
  'auth.pick_name': 'Pick a name and start climbing.',

  // Leaderboard
  'leaderboard.title': 'LEADERBOARD',
  'leaderboard.tab_mmr': 'MMR',
  'leaderboard.tab_time': 'BEST TIME',
  'leaderboard.tab_tiers': 'TIERS',
  'leaderboard.mode_solo': 'SOLO 1V1',
  'leaderboard.mode_duo': 'DUO 2V2',
  'leaderboard.your_position': 'YOUR POSITION · #{n}',
  'leaderboard.unranked': 'Unranked',
  'leaderboard.empty': 'No entries yet — be the first!',
  'leaderboard.tab_byrank': 'BY RANK',
  'leaderboard.empty_time': 'No completed wins yet. Be the first to set a time!',
  'leaderboard.empty_players': 'No players yet.',
  'leaderboard.you': 'YOU',
  'leaderboard.unranked_yet': '— unranked yet',
  'leaderboard.top': 'TOP {n}',
  'leaderboard.tier_empty': 'No player has reached this tier yet.',

  // Matchmaking
  'mm.searching_tag': '◌ SEARCHING',
  'mm.found_tag': '◉ RIVAL FOUND',
  'mm.ready_tag': '⚡ GET READY',
  'mm.searching_title': 'FINDING WORTHY RIVAL...',
  'mm.found_title': 'DUEL INCOMING',
  'mm.ready_title': 'BATTLE STATIONS',
  'mm.offline': 'OFFLINE · AI RIVAL',
  'mm.stage': 'STAGE',
  'mm.stage_name': 'CHROMA ASCENT WALL',
  'mm.stage_desc': '15.0m · chroma circuit · 4-color sequence',
  'mm.cancel': '× CANCEL SEARCH',
  'mm.scanning': 'SCANNING…',

  // Rank-up modal
  'rankup.promotion': '★ PROMOTION ★',
  'rankup.demotion': '▼ DEMOTION ▼',
  'rankup.rank_up': 'RANK UP!',
  'rankup.demoted': 'DEMOTED',
  'rankup.tap_continue': 'TAP TO CONTINUE',

  // Shop
  'shop.title': 'SHOP',
  'shop.trial': 'TRY',
  'shop.trial_started': 'Trying {name} for {n} matches',
  'shop.subtitle': 'Outfits, hats, capes & flexes. Spend smart — gems are precious.',
  'shop.empty': 'Nothing here yet.',
  'shop.loading': 'Loading…',
  'shop.owned_tag': 'OWNED',
  'shop.buy_coins': 'BUY · {price}',
  'shop.buy_gems': 'BUY · {price} 💎',
  'shop.not_enough': 'NOT ENOUGH',
  'shop.purchased': 'Purchased!',
  'shop.failed': 'Purchase failed',
  'shop.cat_gems': 'Gems',
  'shop.cat_combos': 'Combos',
  'shop.cat_heroes': 'Heroes',
  'shop.cat_skills': 'Skills',
  'shop.cat_holds': 'Holds',
  'shop.cat_frames': 'Frames',
  'shop.cat_finishers': 'Finishers',
  'shop.cat_backgrounds': 'Backgrounds',
  'shop.combos_hint': 'Complete sets — character, avatar frame and 6-second finisher in one purchase.',
  'shop.no_combos': 'No combos available yet.',
  'shop.combo_contains': '3 ITEMS',
  'shop.combo_owned': 'COMBO OWNED',
  'shop.combo_acquired': 'Combo acquired: {name}',
  'shop.acquired': 'Acquired: {name}',
  'shop.frames_hint': 'Avatar frames — show off your style around your photo.',
  'shop.no_frames': 'No frames yet.',
  'shop.finishers_hint': 'Endgame finishers — the flashy effect that plays when you win.',
  'shop.no_finishers': 'No finishers yet.',
  'shop.backgrounds_hint': 'Match backgrounds — customize the arena behind every PvP climb.',
  'shop.no_backgrounds': 'No match backgrounds yet.',
  'shop.background_preview': 'ARENA PREVIEW',
  'shop.claim': 'CLAIM',
  'chars.shop_section': 'CHARACTERS — HEROES',
  'chars.loading': 'Loading heroes…',
  'chars.unlocked': 'Unlocked {name}!',
  'chars.purchase_failed': 'Purchase failed',
  'chars.owned_equip': '✓ OWNED · equip in BAG',
  'chars.win_power': 'WIN POWER ×{power}',
  'chars.free_starter': 'FREE STARTER',
  'chars.bag_empty': 'No heroes yet — buy one in the Shop.',
  'chars.equipped_toast': 'Equipped {name}',
  'chars.equip_failed': 'Equip failed',

  // Hold skins (wall-grip shapes)
  'holds.shop_section': 'HOLD SKINS — WALL GRIPS',
  'holds.bag_section': 'HOLD SKINS — WALL GRIPS',
  'holds.loading': 'Loading hold skins…',
  'holds.unlocked': 'Unlocked {name}!',
  'holds.purchase_failed': 'Purchase failed',
  'holds.owned_equip': '✓ OWNED · equip in BAG',
  'holds.free_default': 'FREE · IN ROTATION',
  'holds.bag_empty': 'No hold skins yet — buy one in the Shop.',
  'holds.equip': 'EQUIP',
  'holds.use_random': 'USE RANDOM',
  'holds.equipped': '✓ EQUIPPED',
  'holds.equipped_toast': 'Equipped {name}',
  'holds.unequipped_toast': 'Back to random rotation',
  'holds.equip_failed': 'Equip failed',
  'holds.random_hint': 'No skin equipped — a random default shape is picked each match.',

  // Inventory
  'inv.title': 'INVENTORY',
  'inv.subtitle': 'Equip what you own. Right-side preview shows the climber.',
  'inv.empty': 'No items in this slot yet.',
  'inv.equipped': 'EQUIPPED',
  'inv.equip': 'EQUIP',
  'inv.trial_left': '{n} left',
  'inv.unequip': 'UNEQUIP',
  'inv.tap_to_equip': 'Tap to equip. One item per slot.',
  'inv.empty_all': 'Empty inventory.',
  'inv.equipped_toast': 'Equipped: {name}',
  'inv.unequipped_toast': '{slot} unequipped',
  'inv.skills_section': 'SKILLS · CONSUMABLES',
  'inv.buy_more': 'BUY MORE ›',
  'inv.skill_locked': '🔒 Unlocks at Lv{level} (you are Lv{my})',
  'inv.skill_empty': 'Out of charges — tap to buy',
  'inv.skill_ready': 'Ready to use in match',
  'inv.owned_label': 'OWNED',

  // Friends screen (legacy — still routable from old places)
  'friends.title': 'FRIENDS',
  'friends.tab_friends': 'FRIENDS',
  'friends.tab_global': 'GLOBAL CHAT',
  'friends.add_placeholder': 'Add by username…',
  'friends.add_btn': 'ADD',
  'friends.friend_added': 'Friend added ✓',
  'friends.request_sent': 'Request sent',
  'friends.incoming_requests': 'INCOMING REQUESTS · {n}',
  'friends.accept': 'ACCEPT',
  'friends.decline': 'DECLINE',
  'friends.pending_sent': 'PENDING SENT · {n}',
  'friends.cancel': 'CANCEL',
  'friends.online_count': 'ONLINE · {n}',
  'friends.none_online': 'None of your friends are online right now.',
  'friends.offline_count': 'OFFLINE · {n}',
  'friends.no_friends': 'No friends yet — search above to add someone.',
  'friends.rank_mmr': '{rank} · {mmr} MMR',
  'friends.no_messages': 'No messages yet. Say hi 👋',
  'friends.say_something': 'Say something…',
  'friends.send': 'SEND',
  'friends.start_conversation': 'Start the conversation 💬',
  'friends.message_placeholder': 'Message {name}…',

  // Boss Break
  'boss.mode_label': 'SOLO PVE',
  'boss.title': 'BOSS BREAK',
  'boss.subtitle': 'Score Perfect taps · survive the short boss rush',
  'boss.loading': 'SUMMONING BOSS…',
  'boss.disabled_title': 'MODE NOT AVAILABLE',
  'boss.disabled_body': 'Admin can enable Boss Break from the admin panel.',
  'boss.load_error': 'Could not load Boss Break. Please try again.',
  'boss.world_boss': 'WORLD BOSS',
  'boss.target': 'TARGET',
  'boss.stone_king': 'STONE KING',
  'boss.stone_king_description':
    'Break the elemental armor before your grip runs out.',
  'boss.frost_empress': 'FROST EMPRESS',
  'boss.frost_empress_description': 'Shatter her ice crown before the fog freezes every grip.',
  'boss.storm_roc': 'STORM ROC',
  'boss.storm_roc_description': 'Hold the rhythm through lightning and stagger the sky tyrant.',
  'boss.abyss_kraken': 'ABYSS KRAKEN',
  'boss.abyss_kraken_description': 'Sever its tentacles before abyssal pressure crushes your grip.',
  'boss.verdant_colossus': 'VERDANT COLOSSUS',
  'boss.verdant_colossus_description': 'Burn the rebirth core and break its endlessly regrowing bark.',
  'boss.inferno_wyrm': 'INFERNO WYRM',
  'boss.inferno_wyrm_description': 'Cross the firestorm, crack molten scales, and silence dragonfire.',
  'boss.eclipse_sovereign': 'ECLIPSE SOVEREIGN',
  'boss.eclipse_sovereign_description': 'Survive the eclipse cycle and destroy the ultimate void core.',
  'boss.weekly_hunt': '7-DAY HARD BOSS HUNT',
  'boss.legendary_frame': 'LEGENDARY FRAME',
  'boss.frame_reward': 'BOSS FRAME',
  'boss.frame_owned': 'FRAME OWNED',
  'boss.frame_requirement': 'HARD · ≥{rate}% PERFECT',
  'boss.frame_need_perfect': 'NEED ≥{rate}% PERFECT FOR FRAME',
  'boss.weekly_bonus': 'WEEKLY MILESTONE',
  'boss.competition.title': 'PERFECT SCORE LADDER',
  'boss.competition.attempts': '{remaining}/{total} RUNS LEFT',
  'boss.competition.daily': 'DAILY SCORE',
  'boss.competition.weekly': 'WEEKLY POINTS',
  'boss.competition.damage': 'SCORE',
  'boss.competition.points': 'PTS',
  'boss.competition.my_rank': 'RANK #{rank}',
  'boss.competition.closes': 'WEEK ENDS {time}',
  'boss.competition.closed': 'WEEKLY SETTLEMENT',
  'boss.competition.empty': 'No scores yet — claim the first rank.',
  'boss.competition.open': 'VIEW LEADERBOARD',
  'boss.competition.close': 'CLOSE',
  'boss.competition.rank': 'RANK',
  'boss.competition.attempts_label': 'RUNS LEFT',
  'boss.wins': '{n} WIN',
  'boss.starting': 'CREATING…',
  'boss.difficulty.normal': 'NORMAL',
  'boss.difficulty.hard': 'HARD',
  'boss.unlock_hard': '🔒 BEAT NORMAL',
  'boss.daily_note':
    'First clear +{gems} 💎 · Gold rewards {limit} runs/day',
  'boss.phase': 'PHASE {phase} · {difficulty}',
  'boss.hp': '{name} · {remaining}/{total}',
  'boss.stunned_break': 'STUNNED · BURST ×2',
  'boss.break_meter': 'BREAK',
  'boss.combo': '{n} COMBO',
  'boss.score_combo': '{score} PTS · {n} COMBO',
  'boss.vulnerable': 'VULNERABLE {seconds}s',
  'boss.burst_window': 'BURST WINDOW ×2',
  'boss.destroy_core': 'BREAK THE ELEMENTAL CORE',
  'boss.attack.slam': '⚠ EARTHSHATTER',
  'boss.attack.shards': '⚠ STONE SHARD RAIN',
  'boss.attack.beam': '⚠ FOUR-COLOR CORE BLAST',
  'boss.current_color_aria': 'Current color: {color}',
  'boss.color.red': 'red',
  'boss.color.blue': 'blue',
  'boss.color.yellow': 'yellow',
  'boss.color.purple': 'purple',
  'boss.now': 'NOW',
  'boss.timing.zone': 'HIT ZONE',
  'boss.timing.hit': 'PERFECT HIT!',
  'boss.timing.early': 'TOO EARLY',
  'boss.timing.late': 'TOO LATE',
  'boss.timing.wrong': 'WRONG COLOR',
  'boss.grip': 'GRIP · {current}/{max}',
  'boss.skill.disrupt': 'DISRUPT',
  'boss.skill.shield': 'SHIELD',
  'boss.tap_color_aria': 'Tap {color}',
  'boss.go': 'GO!',
  'boss.countdown_hint':
    'Tap the matching color inside the hit zone · Q breaks armor · E blocks attacks',
  'boss.verifying': 'VERIFYING…',
  'boss.defeated': 'BOSS DEFEATED',
  'boss.run_complete': 'TIME UP · RUN COMPLETE',
  'boss.run_failed': 'RUN FAILED',
  'boss.finish': 'FINISHED!',
  'boss.score_recorded': 'SCORE RECORDED!',
  'boss.grip_empty': 'OUT OF GRIP!',
  'boss.stat.score': 'SCORE',
  'boss.stat.perfect': 'PERFECT',
  'boss.stat.damage': 'DAMAGE',
  'boss.stat.combo': 'COMBO',
  'boss.stat.time': 'TIME',
  'boss.frame_unlocked': 'FRAME UNLOCKED',
  'boss.daily_cap': 'DAILY CAP',
  'boss.continue': 'CONTINUE',
  'boss.home': 'BACK TO LOBBY',
  'boss.blocked': 'BLOCKED!',
  'boss.interrupted': 'INTERRUPTED!',
  'boss.grip_damage': '-{damage} GRIP',

  // Settings (more)
  'settings.title': 'SETTINGS',
  'settings.language': 'LANGUAGE',
  'settings.audio': 'AUDIO',
  'settings.sfx': 'SFX',
  'settings.music': 'Music',
  'settings.haptic': 'Haptic feedback',
  'settings.gameplay': 'GAMEPLAY',
  'settings.lefty': 'Left-handed layout',
  'settings.colorblind': 'Color-blind palette',
  'settings.tap_zone': 'Tap zone size',
  'settings.avatar': 'AVATAR',
  'settings.account': 'ACCOUNT',
  'settings.guest_id': 'Guest ID',
  'settings.username': 'Username',
  'settings.email': 'Email',
  'settings.region': 'Region',
  'settings.version': 'Version',
  'settings.danger_zone': 'DANGER ZONE',
  'settings.danger_guest': 'Sign out to switch to another account or create one.',
  'settings.danger_user': 'Sign out of this device.',
  'settings.sign_out': 'SIGN OUT',
  'settings.confirm_signout': 'Sign out now?',
  'settings.delete_account': 'DELETE ACCOUNT',
  'settings.confirm_delete_account':
    'Delete your account? This permanently erases your profile, progress, purchases and cannot be undone.',
  'settings.confirm_delete_account_final':
    'This is permanent. Tap OK to delete your account for good.',
  'settings.delete_account_failed': 'Could not delete account. Please try again.',
  'settings.avatar_custom': 'Custom photo',
  'settings.avatar_character': 'Character avatar',
  'settings.avatar_default': 'Default avatar',
  'settings.avatar_formats': 'PNG / JPG / WebP · max 3 MB',
  'settings.avatar_change': 'CHANGE',
  'settings.avatar_upload': 'UPLOAD PHOTO',
  'settings.avatar_remove': 'REMOVE',

  // Toasts (notifier)
  'toast.dm_new': 'New DM',
  'toast.friend_request': '👋 Friend request',
  'toast.friend_request_body': '{name} wants to be your friend',
  'toast.now_friends': '✓ Now friends',
  'toast.now_friends_body': '{name} accepted your request',
  'toast.room_invite': '🎮 {name} invited you',
  'toast.room_invite_body': 'Tap to join room {code}',
  'toast.could_not_join': 'Could not join room',
  'toast.could_not_join_body': 'Invite may have expired or the room is full',

  // ── Clan / Clan War (2.0) ──
  'clan.title': 'CLANS',
  'clan.ranks': '🏆 Ranks',
  'clan.ranks_title': 'CLAN RANKS',
  'clan.find': 'Find a clan',
  'clan.create': 'Create',
  'clan.search_ph': 'Search name or tag…',
  'clan.search': 'Search',
  'clan.none_found': 'No clans found. Be the first — create one!',
  'clan.full': 'Full',
  'clan.join': 'Join',
  'clan.request': 'Request',
  'clan.joined': 'Joined!',
  'clan.welcome': 'Welcome to {name}',
  'clan.request_sent': 'Request sent',
  'clan.request_waiting': 'Waiting for {name} to approve',
  'clan.name_label': 'Clan name (3–20)',
  'clan.tag_label': 'Tag (2–6, A–Z/0–9)',
  'clan.desc_label': 'Description',
  'clan.emblem_label': 'Emblem',
  'clan.emblem_shop': 'PREMIUM LOGOS',
  'clan.emblem_equip': 'Equip',
  'clan.emblem_equipped': 'Equipped',
  'clan.emblem_bought': 'Logo unlocked!',
  'clan.emblem_reward': 'Season reward',
  'clan.policy_label': 'Join policy',
  'clan.policy_open': 'Open — anyone joins',
  'clan.policy_request': 'By request — approve members',
  'clan.policy_invite': 'Invite-only',
  'clan.policy_short_open': 'Open',
  'clan.policy_short_request': 'By request',
  'clan.policy_short_invite': 'Invite-only',
  'clan.create_btn': 'Create clan',
  'clan.creating': 'Creating…',
  'clan.create_note': 'A coin cost + minimum level may apply.',
  'clan.req_title': 'REQUIREMENTS',
  'clan.req_level': 'Level {n}+',
  'clan.req_coins': '{n} coins',
  'clan.req_gems': '{n} gems',
  'clan.created': 'Clan created!',
  'clan.role.leader': 'Leader',
  'clan.role.coLeader': 'Co-leader',
  'clan.role.elder': 'Elder',
  'clan.role.member': 'Member',
  'clan.requests_title': 'Join requests ({n})',
  'clan.members_title': 'Members',
  'clan.leave': 'Leave clan',
  'clan.disband': 'Disband',
  'clan.you': '(you)',
  'clan.confirm_leave': 'Leave this clan?',
  'clan.confirm_leave_leader': 'Leave and pass leadership to the next officer?',
  'clan.confirm_disband': 'Disband {name}? This cannot be undone.',
  'clan.confirm_kick': 'Kick {name}?',
  'clan.confirm_make_leader': 'Make {name} the leader?',
  'clan.left': 'Left clan',
  'clan.disbanded': 'Clan disbanded',
  'clan.toast_title': 'Clan',
  'clan.err_generic': 'Something went wrong',
  'clan.err_profanity': 'Name, tag or description contains banned words',
  'clan.now': 'now',
  'clan.notif.inbox_title': 'CLAN NEWS',
  'clan.notif.empty': 'No clan news yet.',
  'clan.notif.join_request': '{name} asked to join {clan}',
  'clan.notif.join_approved': 'You joined {clan}!',
  'clan.notif.join_rejected': 'Your request to join {clan} was declined',
  'clan.notif.kicked': 'You were removed from {clan}',
  'clan.notif.role_changed': 'Your role in {clan} is now {role}',
  'clan.notif.war_scheduled': 'Clan war scheduled — get ready!',
  'clan.notif.war_open': 'Clan war is LIVE — play your attacks!',
  'clan.notif.war_ended': 'Clan war ended — {result}',
  'clan.notif.emblem_awarded': 'Top {rank}! Your clan earned the {name} logo for this week 🏆',
  'clan.notif.result_win': 'Victory 🏆',
  'clan.notif.result_lose': 'Defeat',
  'clan.notif.result_draw': 'Draw',
  'clan.war.title': 'CLAN WAR',
  'clan.war.daily_at': 'Daily {t}',
  'clan.war.registered': 'Registered for today',
  'clan.war.registered_ok': 'Your clan is in today\u2019s war!',
  'clan.war.battle_at': 'Battle at {t}',
  'clan.war.register_btn': 'Register for today\u2019s war',
  'clan.war.need_members': 'Need at least {n} members',
  'clan.war.await_leader': 'Waiting for the leader to register',
  'clan.war.reg_opens': 'Registration opens at {t}',
  'clan.war.prep': 'Prep · battle in {t}',
  'clan.war.battle': 'Battle · ends in {t}',
  'clan.war.vs': 'vs',
  'clan.war.find': 'Find war match ({n} left)',
  'clan.war.no_attacks': 'No attacks left',
  'clan.war.not_open': 'Battle not open',
  'clan.war.prep_note': 'Prepare your roster — battle opens soon. {n} matches each.',
  'clan.war.top': 'Top contributors',
  'clan.war.stat': '{pt} pt · {wins}W',
  'clan.war.title_toast': 'Clan War',
  'clan.season': 'Season {n}',
  'clan.season_left': '{t} left',
  'clan.season_ending': 'ending',
  'clan.lb_empty': 'No clans ranked yet.',
} as const;

export type I18nKey = keyof typeof en;

// ── Vietnamese ───────────────────────────────────────────────────────
const vi: Partial<Record<I18nKey, string>> = {
  'common.cancel': 'HUỶ',
  'common.close': 'ĐÓNG',
  'common.preview': 'XEM TRƯỚC',
  'common.confirm': 'XÁC NHẬN',
  'common.back': 'QUAY LẠI',
  'common.loading': 'Đang tải…',
  'common.error': 'Lỗi',
  'common.online': 'online',
  'common.offline': 'offline',
  'common.send': 'Gửi',
  'common.edit': 'Sửa',
  'common.save': 'Lưu',
  'common.network_error': 'Lỗi mạng',
  'chars.idle_preview': 'XEM IDLE + WIN',
  'shop.finisher_preview': 'XEM FINISHER',
  'ads.none_available': 'Không có quảng cáo',
  'ads.none_available_body': 'Vui lòng thử lại sau.',
  'playgate.title': 'Chơi trong ứng dụng',
  'playgate.body': 'Tính năng đấu trận chỉ có trên ứng dụng Don’t Miss. Tải miễn phí để chơi.',
  'playgate.ios': 'Tải trên App Store',
  'playgate.android': 'Tải trên Google Play',
  'update.hard_title': 'Cần cập nhật',
  'update.hard_body': 'Đã có phiên bản mới bắt buộc để tiếp tục chơi Don’t Miss. Vui lòng cập nhật.',
  'update.soft_title': 'Có bản cập nhật',
  'update.soft_body': 'Đã có phiên bản mới của Don’t Miss với nhiều cải tiến và sửa lỗi.',
  'update.cta': 'Cập nhật ngay',
  'update.later': 'Để sau',
  'app.announcement_title': 'Thông báo',
  'app.maintenance_title': 'Đang bảo trì',
  'app.maintenance_default': 'Ứng dụng đang bảo trì. Vui lòng quay lại sau.',
  'onboarding.welcome_title': 'Chào mừng đến Don’t Miss!',
  'onboarding.welcome_body': 'Chạm đúng màu để leo tường và vượt đối thủ. Ai lên đỉnh trước thì thắng — đừng chạm trượt!',
  'onboarding.name_title': 'Chọn tên của bạn',
  'onboarding.name_body': 'Đây là tên người chơi khác nhìn thấy. Lần đổi đầu tiên miễn phí.',
  'onboarding.shout_title': 'Đặt câu nói chiến thắng',
  'onboarding.shout_body': 'Đối thủ sẽ thấy câu này khi bạn thắng. Đặt cho ấn tượng! Có thể bỏ qua và đặt sau.',
  'onboarding.next': 'Tiếp',
  'onboarding.finish': 'Bắt đầu chơi',
  'onboarding.skip': 'Bỏ qua',
  'common.home': 'Trang chủ',
  'common.back_home': 'Về trang chủ',
  'common.dismiss': 'Đóng',

  'home.quick_match': '⚡ TÌM TRẬN NHANH',
  'home.daily_ad': 'Quà hôm nay',
  'quests.title': 'Nhiệm vụ hàng ngày',
  'quests.button': 'Nhiệm vụ hàng ngày',
  'quests.reset_note': 'Làm mới mỗi ngày',
  'quests.share': 'Chia sẻ mạng xã hội',
  'quests.play': 'Chơi 5 trận',
  'quests.win': 'Thắng 10 trận',
  'quests.claim': 'Nhận',
  'quests.claimed': 'Đã nhận',
  'quests.share_btn': 'Chia sẻ',
  'quests.share_text': "🧗‍♂️ Mình đang đua top trong Don't Miss! Đấu 1v1 bấm màu siêu tốc — bấm đúng màu, leo tường, hạ đối thủ. Cá là bạn không phá nổi thời gian của mình đâu! 🔴🔵🟡🟣 Vào đấu với mình 👇",
  'home.daily_ad_watching': 'Đang tải quảng cáo…',
  'home.create_room': '+ TẠO PHÒNG',
  'home.join_room': '↪ NHẬP MÃ PHÒNG',
  'home.modes_subtitle': '⚡ Solo 1v1 · Duo · Team · Giải đấu sắp ra mắt',
  'home.avg_wait': 'Trung bình 4s',
  'home.could_not_create_room': 'Không tạo được phòng',
  'home.code_too_short': 'Mã phòng quá ngắn',
  'home.could_not_join': 'Không vào được phòng',
  'home.join_room_title': 'VÀO PHÒNG',
  'home.equipped_skills': 'KỸ NĂNG MANG THEO',
  'home.buy_more': 'MUA THÊM ›',
  'home.skill_buy_cta': 'MUA',
  'home.open_profile': 'Mở hồ sơ',
  'home.role_climber': 'Người leo',
  'home.win_streak': 'CHUỖI THẮNG',
  'home.join_btn': 'VÀO',
  'home.display_name': 'Tên hiển thị',
  'home.display_name_ph': 'Tên trong game của bạn',
  'home.victory_shout': 'Câu nói chiến thắng',
  'home.victory_shout_ph': 'vd. GG! · Quá dễ 💪',
  'home.first_change_free': 'Đổi lần đầu miễn phí',
  'home.field_none': '— chưa có —',
  'home.not_enough_gems': 'Không đủ ngọc',
  'home.max_chars': 'Tối đa {n} ký tự',
  'profanity.blocked': 'Từ này không được phép. Hãy chọn từ khác.',
  'home.save_cost': 'Lưu · {cost}💎',

  'room.leave': '← RỜI',
  'room.chat': '◐ CHAT',
  'room.code_label': 'MÃ PHÒNG',
  'room.tap_to_copy': 'tap để copy',
  'room.copied': '✓ ĐÃ COPY',
  'room.team_a': 'ĐỘI A',
  'room.team_b': 'ĐỘI B',
  'room.join_slot': 'VÀO SLOT',
  'room.you_suffix': '(BẠN)',
  'room.ready_label': 'SẴN SÀNG',
  'room.tap_to_ready': 'TAP ĐỂ SẴN SÀNG',
  'room.invite_friend': '+ MỜI BẠN BÈ',
  'room.share_to_chat': '📢 ĐĂNG LÊN CHAT',
  'room.shared_to_chat': '✓ ĐÃ ĐĂNG',
  'room.open_chat_tip': 'Mở chat đội',
  'room.chat_title': 'CHAT ĐỘI',
  'room.chat_placeholder': 'Nhắn cho đồng đội…',
  'room.chat_empty': 'Chưa có tin nhắn. Chào đồng đội nào!',
  'room.chat_send': 'GỬI',
  'room.chat_expand': 'Phóng to chat',
  'room.chat_collapse': 'Thu nhỏ',
  'room.kick_tip': 'Đuổi',
  'room.waiting_host': 'Đợi host bấm START…',
  'room.invite_modal_title': 'MỜI BẠN BÈ',
  'room.no_friends_to_invite': 'Chưa có bạn bè để mời. Mở dock chat → tap avatar trong GLOBAL.',
  'room.in_room': 'ĐÃ TRONG PHÒNG',
  'room.invited': '✓ ĐÃ MỜI',
  'room.invite_btn': 'MỜI',
  'room.offline_send_hint': 'Bạn offline — sẽ không nhận được',
  'room.send_invite': 'Gửi lời mời',
  'room.start': '▶ BẮT ĐẦU',
  'room.start_quick': '▶ BẮT ĐẦU · NHANH',
  'room.start_duel': '▶ BẮT ĐẦU · ĐẤU',
  'room.mode.empty_desc': 'Mời bạn vào slot để bắt đầu',
  'room.mode.quick': 'TÌM TRẬN NHANH',
  'room.mode.quick_desc': 'Bấm bắt đầu sẽ tìm trận solo ngẫu nhiên',
  'room.mode.solo_duel': 'SOLO DUEL · 1v1',
  'room.mode.solo_duel_desc': 'Đấu trực tiếp giữa 2 bên — không qua matchmaking',
  'room.mode.duo_rank': 'DUO RANK · 2v2',
  'room.mode.duo_rank_desc': 'Tìm 2 đối thủ ngẫu nhiên (sắp ra mắt)',
  'room.mode.team_duel': 'TEAM DUEL · 2v2',
  'room.mode.team_duel_desc': 'Đấu trực tiếp giữa 2 đội (sắp ra mắt)',
  'room.mode.imbalanced': 'KHÔNG CÂN',
  'room.mode.imbalanced_desc': 'Cân bằng 2 bên (1v1 hoặc 2v2) để bắt đầu',

  'room.tmode.alternating': 'LUÂN PHIÊN',
  'room.tmode.alternating_desc': 'Đồng đội đổi lượt mỗi 5 mốc. Combo đội mở khoá kỹ năng.',
  'room.tmode.split_color': 'CHIA MÀU',
  'room.tmode.split_color_desc': 'Mỗi người giữ 2 màu. Combo 5 mở khoá cả hai kỹ năng.',
  'room.tmode.attack_defense': 'CÔNG · THỦ',
  'room.tmode.attack_defense_desc': 'Một người leo, một người tung kỹ năng theo vai trò.',

  'match.you_win': 'BẠN THẮNG!',
  'match.you_lose': 'BẠN THUA',
  'match.summary': '▶ TỔNG KẾT',
  'match.tip_label': 'MẸO',
  'match.auto_continue': 'tự chuyển sau {secs}s',
  'match.tap_color': 'Chạm {color}',
  'match.quit_aria': 'Thoát trận',
  'match.combo': '×{n} COMBO',
  'match.you': 'BẠN',
  'match.rival': 'ĐỐI THỦ',
  'match.team.your_turn': 'LƯỢT CỦA BẠN — chạm vào tường',
  'match.team.teammate_turn': 'Lượt của đồng đội',
  'match.team.you_climb': 'BẠN LEO — chạm vào tường',
  'match.team.you_cast': 'BẠN HỖ TRỢ — dùng kỹ năng',

  'result.match_result': 'KẾT QUẢ TRẬN',
  'result.victory': 'CHIẾN THẮNG!',
  'result.defeat': 'THUA RỒI',
  'result.rewards': 'PHẦN THƯỞNG',
  'result.x2_button': 'X2 PHẦN THƯỞNG',
  'result.x2_watching': 'Đang tải quảng cáo…',
  'result.x2_done': 'Đã nhân đôi phần thưởng!',
  'result.battle_stats': 'CHỈ SỐ TRẬN',
  'result.rematch': '▶ ĐẤU LẠI',
  'result.home': 'TRANG CHỦ',
  'result.back_to_room': '▶ QUAY LẠI PHÒNG',
  'result.back_to_war': '⚔️ VỀ CLAN WAR',
  'result.leave_room': 'RỜI PHÒNG',
  'result.opponent_shouts': 'ĐỐI THỦ HÉT',
  'result.coins': 'VÀNG',
  'result.stat_speed': 'Tốc độ',
  'result.stat_accuracy': 'Độ chính xác',
  'result.stat_combo_max': 'Combo tối đa',

  'chat.global': 'Chung',
  'chat.clan': 'Clan',
  'chat.placeholder_clan': 'Nhắn cho clan…',
  'chat.dms': 'Tin riêng',
  'chat.no_messages': 'Chưa có tin nhắn — gửi câu đầu tiên đi!',
  'chat.no_dms': 'Chưa có tin nhắn. Tap avatar trong Chung để kết bạn / nhắn tin.',
  'chat.copy_tip': 'Chạm 2 lần để sao chép',
  'chat.copied': 'Đã sao chép!',
  'chat.copy_hint': '💡 Chạm 2 lần vào tin nhắn để sao chép',
  'chat.open_chat': 'Mở chat',
  'chat.back_to_conversations': 'Quay lại danh sách',
  'chat.share_room_btn': '📍 Chia sẻ phòng {code}',
  'chat.share_room_msg': 'Vào phòng của tôi! Chạm để vào 👉 [{code}]',
  'chat.join_room': '▶ VÀO PHÒNG',
  'chat.open_room': '▶ MỞ PHÒNG',
  'chat.placeholder_global': 'Chat với mọi người…',
  'chat.placeholder_dm': 'Nhắn {name}…',
  'chat.add_friend': '+ Thêm bạn',
  'chat.username_placeholder': 'Username người chơi',
  'chat.btn_add': 'Thêm',
  'chat.section.room_invites': 'Lời mời vào phòng · {n}',
  'chat.section.friend_requests': 'Lời mời kết bạn · {n}',
  'chat.section.conversations': 'Cuộc trò chuyện',
  'chat.invite.tagline': 'mời vào phòng {code}',
  'chat.invite.join': '▶ Vào',
  'chat.invite.decline': 'Từ chối',
  'chat.request.tagline': 'muốn kết bạn với bạn',
  'chat.request.accept': 'Chấp nhận',
  'chat.request.decline': 'Từ chối',
  'chat.open_dm': 'Mở DM',
  'chat.view_profile_tip': 'Xem profile · kết bạn · nhắn riêng',
  'chat.manage_friend_tip': 'Xem profile / quản lí bạn bè',
  'chat.you_prefix': 'Bạn: {text}',
  'chat.now_friends': 'Đã trở thành bạn!',
  'chat.request_sent': 'Đã gửi lời mời',
  'chat.send_failed': 'Không gửi được lời mời',

  'qm.profile_load_failed': 'Không lấy được profile',
  'qm.rank': 'HẠNG',
  'qm.mmr': 'MMR',
  'qm.matches': 'TRẬN',
  'qm.win_rate': 'TỈ LỆ THẮNG',
  'qm.add_friend': '+ KẾT BẠN',
  'qm.request_sent': '⏳ ĐÃ GỬI LỜI MỜI',
  'qm.accept_request': '✓ CHẤP NHẬN LỜI MỜI',
  'qm.friends_unfriend': '✓ ĐÃ LÀ BẠN · huỷ',
  'qm.send_dm': '💬 NHẮN RIÊNG',
  'qm.invite_to_room': '🎮 MỜI VÀO PHÒNG {code}',
  'qm.invited_to_room': '✓ ĐÃ MỜI VÀO {code}',
  'qm.invite_failed': 'Mời thất bại',
  'qm.invited_toast': 'Đã mời vào phòng {code}',
  'qm.unfriended': 'Đã huỷ kết bạn',
  'qm.block': 'CHẶN',
  'qm.unblock': 'BỎ CHẶN',
  'qm.report': 'BÁO CÁO',
  'qm.blocked_toast': 'Đã chặn người dùng',
  'qm.unblocked_toast': 'Đã bỏ chặn',
  'qm.reported_toast': 'Đã gửi báo cáo. Cảm ơn bạn.',
  'qm.report_confirm': 'Báo cáo người dùng này vì hành vi không phù hợp?',
  'qm.accepted': 'Đã chấp nhận',

  'shop.skills.section': 'KỸ NĂNG — TIÊU HAO',
  'shop.skills.loading': 'Đang tải kỹ năng…',
  'shop.skills.owned': 'SỞ HỮU',
  'shop.skills.unlocks_at': '🔒 Mở khoá ở Lv{level} (bạn đang Lv{my})',
  'shop.skills.bought_today': 'Hôm nay: {n}/{max}',
  'shop.skills.ad_today': 'AD: {n}/{max}',
  'shop.skills.watching': 'Đang xem…',
  'shop.skills.purchase_failed': 'Mua thất bại',
  'shop.skills.ad_failed': 'Xem ad lỗi',
  'shop.skills.added': '+1 {name}',
  'shop.skills.added_ad': '+1 {name} (AD)',
  'shop.disrupt.desc': 'Đổi 3 ô màu tiếp theo của đối thủ.',
  'shop.shield.desc': 'Khiên 1s — huỷ disrupt + miễn tap sai.',

  'tab.shop': 'CỬA HÀNG',
  'tab.ranks': 'XẾP HẠNG',
  'tab.play': 'CHƠI',
  'tab.bag': 'TÚI ĐỒ',
  'tab.characters': 'NHÂN VẬT',
  'tab.settings': 'CÀI ĐẶT',

  'login.title': "DON'T MISS",
  'login.subtitle': 'Đăng nhập để leo',
  'login.guest': 'CHƠI KHÁCH',
  'login.email': 'Email',
  'login.password': 'Mật khẩu',
  'login.username': 'Tên người dùng',
  'login.signin': 'ĐĂNG NHẬP',
  'login.signup': 'TẠO TÀI KHOẢN',
  'login.switch_to_signup': 'Chưa có tài khoản? Tạo mới',
  'login.switch_to_signin': 'Đã có tài khoản? Đăng nhập',
  'login.continue_google': 'Tiếp tục với Google',
  'login.continue_apple': 'Đăng nhập bằng Apple',
  'login.or': 'hoặc',
  'login.error_invalid': 'Thông tin không hợp lệ',
  'login.error_taken': 'Username hoặc email đã tồn tại',
  'login.error_generic': 'Đăng nhập thất bại',
  'login.oauth_not_configured': 'Đăng nhập {provider} chưa được cấu hình — thử Email hoặc Khách.',
  'login.server_unreachable': 'Không kết nối được máy chủ — kiểm tra mạng.',
  'login.tagline': 'Đấu leo tốc độ. 4 màu.',
  'login.tagline_winner': 'Một người thắng.',
  'login.continue_email': 'Tiếp tục với Email',
  'login.creating_guest': 'Đang tạo khách...',
  'login.play_guest': 'Chơi khách',
  'login.no_rewards': 'KHÔNG THƯỞNG',
  'login.terms_prefix': 'Tiếp tục nghĩa là bạn đồng ý với',
  'login.terms': 'Điều khoản',
  'login.privacy': 'Bảo mật',
  'login.not_configured': 'Chưa cấu hình',
  'login.email_or_username': 'Email hoặc tên người dùng',
  'auth.welcome_back': 'Chào mừng trở lại, leo thủ.',
  'auth.pick_name': 'Chọn tên và bắt đầu leo.',

  'leaderboard.title': 'BẢNG XẾP HẠNG',
  'leaderboard.tab_mmr': 'MMR',
  'leaderboard.tab_time': 'THỜI GIAN TỐT NHẤT',
  'leaderboard.tab_tiers': 'BẬC',
  'leaderboard.mode_solo': 'ĐƠN 1V1',
  'leaderboard.mode_duo': 'ĐÔI 2V2',
  'leaderboard.your_position': 'VỊ TRÍ CỦA BẠN · #{n}',
  'leaderboard.unranked': 'Chưa xếp hạng',
  'leaderboard.empty': 'Chưa có ai — hãy là người đầu tiên!',
  'leaderboard.tab_byrank': 'THEO BẬC',
  'leaderboard.empty_time': 'Chưa có ai thắng. Hãy là người lập thời gian đầu tiên!',
  'leaderboard.empty_players': 'Chưa có người chơi nào.',
  'leaderboard.you': 'BẠN',
  'leaderboard.unranked_yet': '— chưa xếp hạng',
  'leaderboard.top': 'TOP {n}',
  'leaderboard.tier_empty': 'Chưa có ai đạt bậc này.',

  // Matchmaking
  'mm.searching_tag': '◌ ĐANG TÌM',
  'mm.found_tag': '◉ TÌM THẤY ĐỐI THỦ',
  'mm.ready_tag': '⚡ SẴN SÀNG',
  'mm.searching_title': 'ĐANG TÌM ĐỐI THỦ XỨNG TẦM...',
  'mm.found_title': 'TRẬN ĐẤU SẮP BẮT ĐẦU',
  'mm.ready_title': 'VÀO VỊ TRÍ',
  'mm.offline': 'NGOẠI TUYẾN · ĐỐI THỦ AI',
  'mm.stage': 'SÂN ĐẤU',
  'mm.stage_name': 'VÁCH LEO SẮC MÀU',
  'mm.stage_desc': '15,0 m · đường đua sắc màu · chuỗi 4 màu',
  'mm.cancel': '× HỦY TÌM',
  'mm.scanning': 'ĐANG QUÉT…',

  // Rank-up modal
  'rankup.promotion': '★ THĂNG HẠNG ★',
  'rankup.demotion': '▼ XUỐNG HẠNG ▼',
  'rankup.rank_up': 'LÊN HẠNG!',
  'rankup.demoted': 'XUỐNG HẠNG',
  'rankup.tap_continue': 'CHẠM ĐỂ TIẾP TỤC',

  'shop.title': 'CỬA HÀNG',
  'shop.trial': 'DÙNG THỬ',
  'shop.trial_started': 'Dùng thử {name} trong {n} trận',
  'shop.subtitle': 'Trang phục, mũ, áo choàng & cảm xúc. Tiêu khôn ngoan — gem quý hiếm.',
  'shop.empty': 'Chưa có gì.',
  'shop.loading': 'Đang tải…',
  'shop.owned_tag': 'ĐÃ CÓ',
  'shop.buy_coins': 'MUA · {price}',
  'shop.buy_gems': 'MUA · {price} 💎',
  'shop.not_enough': 'KHÔNG ĐỦ',
  'shop.purchased': 'Đã mua!',
  'shop.failed': 'Mua thất bại',
  'shop.cat_heroes': 'Nhân vật',
  'shop.cat_combos': 'Combo',
  'shop.cat_skills': 'Kỹ năng',
  'shop.cat_holds': 'Bậc leo',
  'shop.cat_frames': 'Khung',
  'shop.cat_finishers': 'Kết liễu',
  'shop.cat_backgrounds': 'Nền trận đấu',
  'shop.combos_hint': 'Bộ hoàn chỉnh — nhân vật, khung avatar và kết liễu 6 giây trong một lần mua.',
  'shop.no_combos': 'Chưa có combo nào.',
  'shop.combo_contains': '3 MÓN',
  'shop.combo_owned': 'ĐÃ SỞ HỮU COMBO',
  'shop.combo_acquired': 'Đã nhận combo: {name}',
  'shop.acquired': 'Đã nhận: {name}',
  'shop.frames_hint': 'Khung avatar — thể hiện phong cách quanh ảnh của bạn.',
  'shop.no_frames': 'Chưa có khung nào.',
  'shop.finishers_hint': 'Hiệu ứng kết liễu — màn trình diễn hoành tráng khi bạn thắng.',
  'shop.no_finishers': 'Chưa có hiệu ứng nào.',
  'shop.backgrounds_hint': 'Nền trận đấu — thay đổi không gian phía sau mỗi trận PvP.',
  'shop.no_backgrounds': 'Chưa có nền trận đấu.',
  'shop.background_preview': 'XEM TRƯỚC ĐẤU TRƯỜNG',
  'shop.claim': 'NHẬN',
  'chars.shop_section': 'NHÂN VẬT — ANH HÙNG',
  'chars.loading': 'Đang tải nhân vật…',
  'chars.unlocked': 'Đã mở khoá {name}!',
  'chars.purchase_failed': 'Mua thất bại',
  'chars.owned_equip': '✓ ĐÃ CÓ · trang bị trong TÚI',
  'chars.win_power': 'SỨC MẠNH ×{power}',
  'chars.free_starter': 'KHỞI ĐẦU MIỄN PHÍ',
  'chars.bag_empty': 'Chưa có nhân vật — mua ở Cửa hàng.',
  'chars.equipped_toast': 'Đã trang bị {name}',
  'chars.equip_failed': 'Trang bị thất bại',

  // Hold skins (wall-grip shapes)
  'holds.shop_section': 'SKIN BẬC LEO — TAY BÁM',
  'holds.bag_section': 'SKIN BẬC LEO — TAY BÁM',
  'holds.loading': 'Đang tải skin bậc leo…',
  'holds.unlocked': 'Đã mở khoá {name}!',
  'holds.purchase_failed': 'Mua thất bại',
  'holds.owned_equip': '✓ ĐÃ CÓ · trang bị trong TÚI',
  'holds.free_default': 'MIỄN PHÍ · TRONG VÒNG XOAY',
  'holds.bag_empty': 'Chưa có skin bậc leo — mua ở Cửa hàng.',
  'holds.equip': 'TRANG BỊ',
  'holds.use_random': 'DÙNG NGẪU NHIÊN',
  'holds.equipped': '✓ ĐÃ TRANG BỊ',
  'holds.equipped_toast': 'Đã trang bị {name}',
  'holds.unequipped_toast': 'Quay về xoay ngẫu nhiên',
  'holds.equip_failed': 'Trang bị thất bại',
  'holds.random_hint': 'Chưa trang bị skin — mỗi trận sẽ chọn ngẫu nhiên một hình mặc định.',

  'inv.title': 'TÚI ĐỒ',
  'inv.subtitle': 'Trang bị những gì bạn có. Preview bên phải xem climber.',
  'inv.empty': 'Chưa có vật phẩm ở slot này.',
  'inv.equipped': 'ĐANG MẶC',
  'inv.equip': 'TRANG BỊ',
  'inv.trial_left': 'còn {n}',
  'inv.unequip': 'BỎ TRANG BỊ',
  'inv.tap_to_equip': 'Chạm để trang bị. Mỗi slot một món.',
  'inv.empty_all': 'Túi đồ trống.',
  'inv.equipped_toast': 'Đã trang bị: {name}',
  'inv.unequipped_toast': 'Đã bỏ {slot}',
  'inv.skills_section': 'KỸ NĂNG · TIÊU HAO',
  'inv.buy_more': 'MUA THÊM ›',
  'inv.skill_locked': '🔒 Mở khoá ở Lv{level} (bạn đang Lv{my})',
  'inv.skill_empty': 'Hết lượt — chạm để mua',
  'inv.skill_ready': 'Sẵn sàng dùng trong trận',
  'inv.owned_label': 'SỞ HỮU',

  'friends.title': 'BẠN BÈ',
  'friends.tab_friends': 'BẠN BÈ',
  'friends.tab_global': 'CHAT CHUNG',
  'friends.add_placeholder': 'Thêm bằng tên người dùng…',
  'friends.add_btn': 'THÊM',
  'friends.friend_added': 'Đã thêm bạn ✓',
  'friends.request_sent': 'Đã gửi lời mời',
  'friends.incoming_requests': 'LỜI MỜI ĐẾN · {n}',
  'friends.accept': 'CHẤP NHẬN',
  'friends.decline': 'TỪ CHỐI',
  'friends.pending_sent': 'ĐÃ GỬI · {n}',
  'friends.cancel': 'HUỶ',
  'friends.online_count': 'TRỰC TUYẾN · {n}',
  'friends.none_online': 'Không có bạn nào đang trực tuyến.',
  'friends.offline_count': 'NGOẠI TUYẾN · {n}',
  'friends.no_friends': 'Chưa có bạn — tìm phía trên để thêm.',
  'friends.rank_mmr': '{rank} · {mmr} MMR',
  'friends.no_messages': 'Chưa có tin nhắn. Chào hỏi đi 👋',
  'friends.say_something': 'Nhập gì đó…',
  'friends.send': 'GỬI',
  'friends.start_conversation': 'Bắt đầu trò chuyện 💬',
  'friends.message_placeholder': 'Nhắn cho {name}…',

  // Boss Break
  'boss.mode_label': 'PVE ĐƠN',
  'boss.title': 'PHÁ BOSS',
  'boss.subtitle': 'Bấm Perfect ghi điểm · sống sót qua lượt đấu ngắn',
  'boss.loading': 'ĐANG TRIỆU HỒI BOSS…',
  'boss.disabled_title': 'CHẾ ĐỘ CHƯA MỞ',
  'boss.disabled_body': 'Admin có thể bật Phá Boss từ trang quản trị.',
  'boss.load_error': 'Không thể tải Phá Boss. Vui lòng thử lại.',
  'boss.world_boss': 'BOSS THẾ GIỚI',
  'boss.target': 'MỤC TIÊU',
  'boss.stone_king': 'THẠCH VƯƠNG',
  'boss.stone_king_description':
    'Phá giáp nguyên tố trước khi thanh bám cạn kiệt.',
  'boss.frost_empress': 'BĂNG HẬU',
  'boss.frost_empress_description': 'Phá vương miện băng trước khi sương giá đóng băng mọi điểm bám.',
  'boss.storm_roc': 'LÔI ĐIỂU',
  'boss.storm_roc_description': 'Giữ nhịp giữa giông sét và làm choáng chúa tể bầu trời.',
  'boss.abyss_kraken': 'HẢI QUỶ',
  'boss.abyss_kraken_description': 'Chặt xúc tu vực sâu trước khi thủy áp nghiền nát độ bám.',
  'boss.verdant_colossus': 'MỘC THẦN',
  'boss.verdant_colossus_description': 'Đốt lõi tái sinh và phá lớp cổ thụ hồi phục không ngừng.',
  'boss.inferno_wyrm': 'HỎA LONG',
  'boss.inferno_wyrm_description': 'Xuyên biển lửa, phá vảy dung nham và dập tắt long tức.',
  'boss.eclipse_sovereign': 'NHẬT THỰC VƯƠNG',
  'boss.eclipse_sovereign_description': 'Sống sót qua nhật thực và phá hủy lõi hư không tối thượng.',
  'boss.weekly_hunt': 'SĂN BOSS KHÓ 7 NGÀY',
  'boss.legendary_frame': 'KHUNG HUYỀN THOẠI',
  'boss.frame_reward': 'KHUNG BOSS',
  'boss.frame_owned': 'ĐÃ SỞ HỮU KHUNG',
  'boss.frame_requirement': 'KHÓ · ≥{rate}% PERFECT',
  'boss.frame_need_perfect': 'CẦN ≥{rate}% PERFECT ĐỂ NHẬN KHUNG',
  'boss.weekly_bonus': 'MỐC THƯỞNG TUẦN',
  'boss.competition.title': 'BXH ĐIỂM PERFECT',
  'boss.competition.attempts': 'CÒN {remaining}/{total} LƯỢT',
  'boss.competition.daily': 'ĐIỂM TRONG NGÀY',
  'boss.competition.weekly': 'ĐIỂM TUẦN',
  'boss.competition.damage': 'ĐIỂM',
  'boss.competition.points': 'ĐIỂM',
  'boss.competition.my_rank': 'HẠNG #{rank}',
  'boss.competition.closes': 'CHỐT TUẦN {time}',
  'boss.competition.closed': 'ĐANG TỔNG KẾT TUẦN',
  'boss.competition.empty': 'Chưa có thành tích — hãy chiếm hạng đầu.',
  'boss.competition.open': 'BẢNG XẾP HẠNG',
  'boss.competition.close': 'ĐÓNG',
  'boss.competition.rank': 'THỨ HẠNG',
  'boss.competition.attempts_label': 'LƯỢT CÒN',
  'boss.wins': '{n} TRẬN THẮNG',
  'boss.starting': 'ĐANG TẠO…',
  'boss.difficulty.normal': 'THƯỜNG',
  'boss.difficulty.hard': 'KHÓ',
  'boss.unlock_hard': '🔒 THẮNG THƯỜNG',
  'boss.daily_note':
    'Lần đầu +{gems} 💎 · Thưởng vàng {limit} lượt/ngày',
  'boss.phase': 'GIAI ĐOẠN {phase} · {difficulty}',
  'boss.hp': '{name} · {remaining}/{total}',
  'boss.stunned_break': 'CHOÁNG · BÙNG NỔ ×2',
  'boss.break_meter': 'PHÁ GIÁP',
  'boss.combo': '{n} COMBO',
  'boss.score_combo': '{score} ĐIỂM · {n} COMBO',
  'boss.vulnerable': 'SUY YẾU {seconds}s',
  'boss.burst_window': 'CỬA SỔ BÙNG NỔ ×2',
  'boss.destroy_core': 'PHÁ LÕI NGUYÊN TỐ',
  'boss.attack.slam': '⚠ ĐỊA CHẤN',
  'boss.attack.shards': '⚠ MƯA THẠCH TIỄN',
  'boss.attack.beam': '⚠ TỨ SẮC QUANG PHÁ',
  'boss.current_color_aria': 'Màu hiện tại: {color}',
  'boss.color.red': 'đỏ',
  'boss.color.blue': 'xanh lam',
  'boss.color.yellow': 'vàng',
  'boss.color.purple': 'tím',
  'boss.now': 'HIỆN TẠI',
  'boss.timing.zone': 'VÙNG ĐÁNH',
  'boss.timing.hit': 'CHUẨN NHỊP!',
  'boss.timing.early': 'QUÁ SỚM',
  'boss.timing.late': 'QUÁ MUỘN',
  'boss.timing.wrong': 'SAI MÀU',
  'boss.grip': 'ĐỘ BÁM · {current}/{max}',
  'boss.skill.disrupt': 'PHÁ GIÁP',
  'boss.skill.shield': 'KHIÊN',
  'boss.tap_color_aria': 'Bấm màu {color}',
  'boss.go': 'CHIẾN!',
  'boss.countdown_hint':
    'Bấm đúng màu khi lõi chạy vào vùng đánh · Q phá giáp · E đỡ đòn',
  'boss.verifying': 'ĐANG XÁC MINH…',
  'boss.defeated': 'ĐÃ HẠ BOSS',
  'boss.run_complete': 'HẾT GIỜ · HOÀN THÀNH',
  'boss.run_failed': 'THỬ THÁCH THẤT BẠI',
  'boss.finish': 'KẾT LIỄU!',
  'boss.score_recorded': 'ĐÃ GHI ĐIỂM!',
  'boss.grip_empty': 'HẾT ĐỘ BÁM!',
  'boss.stat.score': 'ĐIỂM',
  'boss.stat.perfect': 'PERFECT',
  'boss.stat.damage': 'SÁT THƯƠNG',
  'boss.stat.combo': 'COMBO',
  'boss.stat.time': 'THỜI GIAN',
  'boss.frame_unlocked': 'ĐÃ MỞ KHUNG',
  'boss.daily_cap': 'ĐẠT GIỚI HẠN NGÀY',
  'boss.continue': 'TIẾP TỤC',
  'boss.home': 'VỀ SẢNH',
  'boss.blocked': 'KHIÊN CHẶN!',
  'boss.interrupted': 'NGẮT CHIÊU!',
  'boss.grip_damage': '-{damage} ĐỘ BÁM',

  'settings.title': 'CÀI ĐẶT',
  'settings.language': 'NGÔN NGỮ',
  'settings.audio': 'ÂM THANH',
  'settings.sfx': 'SFX',
  'settings.music': 'Nhạc nền',
  'settings.haptic': 'Rung phản hồi',
  'settings.gameplay': 'LỐI CHƠI',
  'settings.lefty': 'Bố cục tay trái',
  'settings.colorblind': 'Bảng màu mù màu',
  'settings.tap_zone': 'Kích thước vùng tap',
  'settings.avatar': 'AVATAR',
  'settings.account': 'TÀI KHOẢN',
  'settings.guest_id': 'ID Khách',
  'settings.username': 'Tên người dùng',
  'settings.email': 'Email',
  'settings.region': 'Khu vực',
  'settings.version': 'Phiên bản',
  'settings.danger_zone': 'NGUY HIỂM',
  'settings.danger_guest': 'Đăng xuất để chuyển tài khoản hoặc tạo mới.',
  'settings.danger_user': 'Đăng xuất khỏi thiết bị này.',
  'settings.sign_out': 'ĐĂNG XUẤT',
  'settings.confirm_signout': 'Đăng xuất ngay?',
  'settings.delete_account': 'XÓA TÀI KHOẢN',
  'settings.confirm_delete_account':
    'Xóa tài khoản? Toàn bộ hồ sơ, tiến trình và vật phẩm đã mua sẽ bị xóa vĩnh viễn và không thể khôi phục.',
  'settings.confirm_delete_account_final': 'Hành động này là vĩnh viễn. Nhấn OK để xóa tài khoản.',
  'settings.delete_account_failed': 'Không thể xóa tài khoản. Vui lòng thử lại.',
  'settings.avatar_custom': 'Ảnh tùy chỉnh',
  'settings.avatar_character': 'Ảnh nhân vật',
  'settings.avatar_default': 'Ảnh mặc định',
  'settings.avatar_formats': 'PNG / JPG / WebP · tối đa 3 MB',
  'settings.avatar_change': 'ĐỔI ẢNH',
  'settings.avatar_upload': 'TẢI ẢNH LÊN',
  'settings.avatar_remove': 'XÓA',

  'toast.dm_new': 'Tin nhắn riêng',
  'toast.friend_request': '👋 Lời mời kết bạn',
  'toast.friend_request_body': '{name} muốn kết bạn với bạn',
  'toast.now_friends': '✓ Đã kết bạn',
  'toast.now_friends_body': '{name} đã chấp nhận lời mời',
  'toast.room_invite': '🎮 {name} mời vào phòng',
  'toast.room_invite_body': 'Tap để vào phòng {code}',
  'toast.could_not_join': 'Không vào được phòng',
  'toast.could_not_join_body': 'Có thể đã hết hạn hoặc phòng đầy',

  // ── Clan / Clan War (2.0) ──
  'clan.title': 'CLAN',
  'clan.ranks': '🏆 BXH',
  'clan.ranks_title': 'XẾP HẠNG CLAN',
  'clan.find': 'Tìm clan',
  'clan.create': 'Tạo',
  'clan.search_ph': 'Tìm theo tên hoặc tag…',
  'clan.search': 'Tìm',
  'clan.none_found': 'Chưa có clan nào. Tạo clan đầu tiên đi!',
  'clan.full': 'Đầy',
  'clan.join': 'Vào',
  'clan.request': 'Xin vào',
  'clan.joined': 'Đã vào!',
  'clan.welcome': 'Chào mừng đến {name}',
  'clan.request_sent': 'Đã gửi yêu cầu',
  'clan.request_waiting': 'Đang chờ {name} duyệt',
  'clan.name_label': 'Tên clan (3–20)',
  'clan.tag_label': 'Tag (2–6, A–Z/0–9)',
  'clan.desc_label': 'Mô tả',
  'clan.emblem_label': 'Biểu tượng',
  'clan.emblem_shop': 'LOGO CAO CẤP',
  'clan.emblem_equip': 'Trang bị',
  'clan.emblem_equipped': 'Đang dùng',
  'clan.emblem_bought': 'Đã mở khoá logo!',
  'clan.emblem_reward': 'Thưởng mùa',
  'clan.policy_label': 'Chính sách vào',
  'clan.policy_open': 'Mở — ai cũng vào được',
  'clan.policy_request': 'Cần duyệt — phê duyệt thành viên',
  'clan.policy_invite': 'Chỉ mời',
  'clan.policy_short_open': 'Mở',
  'clan.policy_short_request': 'Cần duyệt',
  'clan.policy_short_invite': 'Chỉ mời',
  'clan.create_btn': 'Tạo clan',
  'clan.creating': 'Đang tạo…',
  'clan.create_note': 'Có thể tốn xu + yêu cầu cấp tối thiểu.',
  'clan.req_title': 'ĐIỀU KIỆN',
  'clan.req_level': 'Cấp {n}+',
  'clan.req_coins': '{n} xu',
  'clan.req_gems': '{n} gems',
  'clan.created': 'Đã tạo clan!',
  'clan.role.leader': 'Chủ clan',
  'clan.role.coLeader': 'Phó clan',
  'clan.role.elder': 'Trưởng lão',
  'clan.role.member': 'Thành viên',
  'clan.requests_title': 'Yêu cầu vào ({n})',
  'clan.members_title': 'Thành viên',
  'clan.leave': 'Rời clan',
  'clan.disband': 'Giải tán',
  'clan.you': '(bạn)',
  'clan.confirm_leave': 'Rời clan này?',
  'clan.confirm_leave_leader': 'Rời và trao quyền chủ cho sĩ quan kế tiếp?',
  'clan.confirm_disband': 'Giải tán {name}? Không thể hoàn tác.',
  'clan.confirm_kick': 'Đuổi {name}?',
  'clan.confirm_make_leader': 'Trao quyền chủ clan cho {name}?',
  'clan.left': 'Đã rời clan',
  'clan.disbanded': 'Đã giải tán clan',
  'clan.toast_title': 'Clan',
  'clan.err_generic': 'Đã có lỗi xảy ra',
  'clan.err_profanity': 'Tên, tag hoặc mô tả chứa từ bị cấm',
  'clan.now': 'ngay',
  'clan.notif.inbox_title': 'TIN CLAN',
  'clan.notif.empty': 'Chưa có tin clan nào.',
  'clan.notif.join_request': '{name} xin vào {clan}',
  'clan.notif.join_approved': 'Bạn đã vào {clan}!',
  'clan.notif.join_rejected': 'Yêu cầu vào {clan} đã bị từ chối',
  'clan.notif.kicked': 'Bạn đã bị loại khỏi {clan}',
  'clan.notif.role_changed': 'Chức của bạn ở {clan} giờ là {role}',
  'clan.notif.war_scheduled': 'Đã xếp trận Clan War — chuẩn bị tham chiến!',
  'clan.notif.war_open': 'Clan War đã MỞ — vào đánh ngay!',
  'clan.notif.war_ended': 'Clan War kết thúc — {result}',
  'clan.notif.emblem_awarded': 'Top {rank}! Clan nhận logo {name} cho tuần này 🏆',
  'clan.notif.result_win': 'Thắng 🏆',
  'clan.notif.result_lose': 'Thua',
  'clan.notif.result_draw': 'Hòa',
  'clan.war.title': 'ĐẠI CHIẾN CLAN',
  'clan.war.daily_at': 'Mỗi ngày {t}',
  'clan.war.registered': 'Đã đăng ký hôm nay',
  'clan.war.registered_ok': 'Clan đã được đăng ký war!',
  'clan.war.battle_at': 'Giao tranh lúc {t}',
  'clan.war.register_btn': 'Đăng ký war hôm nay',
  'clan.war.need_members': 'Cần ít nhất {n} thành viên',
  'clan.war.await_leader': 'Chờ chủ clan đăng ký',
  'clan.war.reg_opens': 'Mở đăng ký lúc {t}',
  'clan.war.prep': 'Chuẩn bị · đánh sau {t}',
  'clan.war.battle': 'Giao tranh · còn {t}',
  'clan.war.vs': 'vs',
  'clan.war.find': 'Tìm trận war ({n} lượt)',
  'clan.war.no_attacks': 'Hết lượt đánh',
  'clan.war.not_open': 'Chưa mở giao tranh',
  'clan.war.prep_note': 'Chuẩn bị đội hình — sắp mở giao tranh. Mỗi người {n} trận.',
  'clan.war.top': 'Đóng góp nhiều nhất',
  'clan.war.stat': '{pt} điểm · {wins}T',
  'clan.war.title_toast': 'Đại chiến clan',
  'clan.season': 'Mùa {n}',
  'clan.season_left': 'còn {t}',
  'clan.season_ending': 'sắp kết thúc',
  'clan.lb_empty': 'Chưa có clan nào xếp hạng.',
};

// ── Chinese (简体) ───────────────────────────────────────────────────
const zh: Partial<Record<I18nKey, string>> = {
  // Common
  'common.cancel': '取消',
  'common.close': '关闭',
  'common.preview': '预览',
  'common.confirm': '确定',
  'common.back': '返回',
  'common.loading': '加载中…',
  'common.error': '错误',
  'common.ok': '好',
  'common.online': '在线',
  'common.offline': '离线',
  'common.send': '发送',
  'common.edit': '编辑',
  'common.save': '保存',
  'common.network_error': '网络错误',
  'chars.idle_preview': '待机 + 胜利预览',
  'shop.finisher_preview': '终结技预览',
  'playgate.title': '在应用中游玩',
  'playgate.body': '对战功能仅在 Don’t Miss 应用中提供。免费下载即可游玩。',
  'playgate.ios': '从 App Store 下载',
  'playgate.android': '从 Google Play 获取',
  'update.hard_title': '需要更新',
  'update.hard_body': '需要更新到 Don’t Miss 新版本才能继续游玩，请更新。',
  'update.soft_title': '有可用更新',
  'update.soft_body': 'Don’t Miss 推出了新版本，包含改进和修复。',
  'update.cta': '立即更新',
  'update.later': '稍后',
  'app.announcement_title': '公告',
  'app.maintenance_title': '维护中',
  'app.maintenance_default': '应用正在维护，请稍后再来。',
  'onboarding.welcome_title': '欢迎来到 Don’t Miss！',
  'onboarding.welcome_body': '点击匹配的颜色攀爬墙壁，击败对手。最先登顶者获胜——别点错了！',
  'onboarding.name_title': '选择你的名字',
  'onboarding.name_body': '这是其他玩家看到的名字。首次修改免费。',
  'onboarding.shout_title': '设置胜利宣言',
  'onboarding.shout_body': '当你获胜时对手会看到这句话。让它令人印象深刻！也可以跳过稍后设置。',
  'onboarding.next': '下一步',
  'onboarding.finish': '开始游戏',
  'onboarding.skip': '暂时跳过',
  'common.home': '主页',
  'common.back_home': '返回主页',
  'common.dismiss': '关闭',

  // Home
  'home.quick_match': '⚡ 快速匹配',
  'home.daily_ad': '每日奖励',
  'quests.title': '每日任务',
  'quests.button': '每日任务',
  'quests.reset_note': '每天重置',
  'quests.share': '分享到社交媒体',
  'quests.play': '进行 5 场对战',
  'quests.win': '赢得 10 场对战',
  'quests.claim': '领取',
  'quests.claimed': '已领取',
  'quests.share_btn': '分享',
  'quests.share_text': "🧗‍♂️ 我正在《Don't Miss!》里冲榜！超快节奏 1v1 点色对决——点对颜色、爬上墙、击败对手。你绝对破不了我的纪录！🔴🔵🟡🟣 来跟我比一场 👇",
  'home.daily_ad_watching': '广告加载中…',
  'home.create_room': '+ 创建房间',
  'home.join_room': '↪ 输入房间码',
  'home.modes_subtitle': '⚡ 单人 1v1 · 双人 · 团队 · 锦标赛即将推出',
  'home.avg_wait': '平均等待 4 秒',
  'home.could_not_create_room': '无法创建房间',
  'home.code_too_short': '房间码太短',
  'home.could_not_join': '无法加入房间',
  'home.join_room_title': '加入房间',
  'home.equipped_skills': '已装备技能',
  'home.buy_more': '购买更多 ›',
  'home.skill_buy_cta': '购买',
  'home.open_profile': '打开档案',
  'home.role_climber': '攀爬者',
  'home.win_streak': '连胜',
  'home.join_btn': '加入',
  'home.display_name': '显示名称',
  'home.display_name_ph': '游戏内昵称',
  'home.victory_shout': '胜利宣言',
  'home.victory_shout_ph': '例如：GG！· 太简单了 💪',
  'home.first_change_free': '首次更改免费',
  'home.field_none': '— 无 —',
  'home.not_enough_gems': '宝石不足',
  'home.max_chars': '最多 {n} 个字符',
  'profanity.blocked': '不允许使用该词，请换一个。',
  'home.save_cost': '保存 · {cost}💎',

  // Room
  'room.leave': '← 离开',
  'room.chat': '◐ 聊天',
  'room.code_label': '房间码',
  'room.tap_to_copy': '点击复制',
  'room.copied': '✓ 已复制',
  'room.team_a': 'A 队',
  'room.team_b': 'B 队',
  'room.join_slot': '加入',
  'room.you_suffix': '(你)',
  'room.ready_label': '准备',
  'room.tap_to_ready': '点击准备',
  'room.invite_friend': '+ 邀请好友',
  'room.share_to_chat': '📢 分享到聊天',
  'room.shared_to_chat': '✓ 已分享',
  'room.open_chat_tip': '打开队伍聊天',
  'room.chat_title': '队伍聊天',
  'room.chat_placeholder': '给队友发消息…',
  'room.chat_empty': '还没有消息。和队友打个招呼吧！',
  'room.chat_send': '发送',
  'room.chat_expand': '放大聊天',
  'room.chat_collapse': '收起',
  'room.kick_tip': '踢出',
  'room.waiting_host': '等待房主开始…',
  'room.invite_modal_title': '邀请好友',
  'room.no_friends_to_invite': '还没有可邀请的好友。打开聊天 → 在世界频道点击头像。',
  'room.in_room': '已在房间',
  'room.invited': '✓ 已邀请',
  'room.invite_btn': '邀请',
  'room.offline_send_hint': '好友离线 — 将收不到邀请',
  'room.send_invite': '发送邀请',
  'room.start': '▶ 开始',
  'room.start_quick': '▶ 开始 · 快速',
  'room.start_duel': '▶ 开始 · 对决',
  'room.start_team': '▶ 开始 · 团队',
  'room.mode.empty_desc': '邀请好友占位以开始游戏',
  'room.mode.quick': '快速匹配',
  'room.mode.quick_desc': '开始后将进入单人随机匹配队列',
  'room.mode.solo_duel': '单人对决 · 1v1',
  'room.mode.solo_duel_desc': '两边直接对决 — 跳过匹配',
  'room.mode.duo_rank': '双人排位 · 2v2',
  'room.mode.duo_rank_desc': '与搭档一起对战两位随机对手',
  'room.mode.team_duel': '团队对决 · 2v2',
  'room.mode.team_duel_desc': '两队直接 2v2 对战',
  'room.team_mode_label': '团队模式',
  'room.tmode.alternating': '轮流',
  'room.tmode.alternating_desc': '每 5 个支点轮换队友。团队连击解锁技能。',
  'room.tmode.split_color': '分色',
  'room.tmode.split_color_desc': '每人负责 2 种颜色。连击 5 解锁全部技能。',
  'room.tmode.attack_defense': '攻 · 守',
  'room.tmode.attack_defense_desc': '一人攀爬，一人按角色释放技能。',
  'room.mode.imbalanced': '不平衡',
  'room.mode.imbalanced_desc': '两边人数需平衡（1v1 或 2v2）',

  // Team 2v2 in-match status
  'match.team.your_turn': '你的回合 — 点击墙面',
  'match.team.teammate_turn': '队友回合',
  'match.team.you_climb': '你攀爬 — 点击墙面',
  'match.team.you_cast': '你支援 — 释放技能',

  // Match
  'match.you_win': '胜利!',
  'match.you_lose': '失败',
  'match.summary': '▶ 总结',
  'match.tip_label': '提示',
  'match.auto_continue': '{secs} 秒后自动继续',
  'match.tap_color': '点击 {color}',
  'match.quit_aria': '退出比赛',
  'match.combo': '×{n} 连击',
  'match.you': '你',
  'match.rival': '对手',

  // Result
  'result.match_result': '比赛结果',
  'result.victory': '胜利!',
  'result.defeat': '失败',
  'result.rewards': '奖励',
  'result.x2_button': '双倍奖励',
  'result.x2_watching': '广告加载中…',
  'result.x2_done': '奖励已翻倍！',
  'result.battle_stats': '战斗数据',
  'result.rematch': '▶ 再战',
  'result.home': '主页',
  'result.back_to_room': '▶ 回到房间',
  'result.back_to_war': '⚔️ 返回部落战',
  'result.leave_room': '离开房间',
  'result.opponent_shouts': '对手宣言',
  'result.coins': '金币',
  'result.stat_speed': '速度',
  'result.stat_accuracy': '准确度',
  'result.stat_combo_max': '最高连击',

  // Chat
  'chat.global': '世界',
  'chat.clan': '战队',
  'chat.placeholder_clan': '给战队发消息…',
  'chat.dms': '私聊',
  'chat.no_messages': '还没有消息 — 第一个发言吧！',
  'chat.no_dms': '还没有消息。在世界频道点击头像即可加好友或私聊。',
  'chat.copy_tip': '双击复制',
  'chat.copied': '已复制！',
  'chat.copy_hint': '💡 双击任意消息可复制',
  'chat.open_chat': '打开聊天',
  'chat.back_to_conversations': '返回会话',
  'chat.share_room_btn': '📍 分享我的房间 {code}',
  'chat.share_room_msg': '加入我的房间！点击进入 👉 [{code}]',
  'chat.join_room': '▶ 加入房间',
  'chat.open_room': '▶ 打开房间',
  'chat.placeholder_global': '与所有人聊天…',
  'chat.placeholder_dm': '发送给 {name}…',
  'chat.minimize': '最小化',
  'chat.close': '关闭',
  'chat.add_friend': '+ 添加好友',
  'chat.username_placeholder': '玩家用户名',
  'chat.btn_add': '添加',
  'chat.section.room_invites': '房间邀请 · {n}',
  'chat.section.friend_requests': '好友请求 · {n}',
  'chat.section.conversations': '会话',
  'chat.invite.tagline': '邀请你加入房间 {code}',
  'chat.invite.join': '▶ 加入',
  'chat.invite.decline': '拒绝',
  'chat.request.tagline': '想加你为好友',
  'chat.request.accept': '接受请求',
  'chat.request.decline': '拒绝请求',
  'chat.open_dm': '打开私聊',
  'chat.view_profile_tip': '查看档案 · 加好友 · 私聊',
  'chat.manage_friend_tip': '查看档案 / 管理好友',
  'chat.you_prefix': '你：{text}',
  'chat.now_friends': '你们已成为好友！',
  'chat.request_sent': '已发送好友请求',
  'chat.send_failed': '发送请求失败',

  // User quick menu
  'qm.profile_load_failed': '无法加载档案',
  'qm.rank': '段位',
  'qm.mmr': 'MMR',
  'qm.matches': '场次',
  'qm.win_rate': '胜率',
  'qm.add_friend': '+ 加好友',
  'qm.request_sent': '⏳ 请求已发送',
  'qm.accept_request': '✓ 接受请求',
  'qm.friends_unfriend': '✓ 好友 · 解除',
  'qm.send_dm': '💬 发送私聊',
  'qm.invite_to_room': '🎮 邀请到房间 {code}',
  'qm.invited_to_room': '✓ 已邀请到 {code}',
  'qm.invite_failed': '邀请失败',
  'qm.invited_toast': '已邀请到房间 {code}',
  'qm.unfriended': '已解除好友',
  'qm.accepted': '已接受',

  // Shop / Skills
  'shop.skills.section': '技能 — 消耗品',
  'shop.skills.loading': '加载技能中…',
  'shop.skills.owned': '已拥有',
  'shop.skills.unlocks_at': '🔒 等级 {level} 解锁（你 Lv{my}）',
  'shop.skills.bought_today': '今日已购买：{n}/{max}',
  'shop.skills.ad_today': '广告：{n}/{max}',
  'shop.skills.watching': '观看中…',
  'shop.skills.purchase_failed': '购买失败',
  'shop.skills.ad_failed': '广告奖励失败',
  'shop.skills.added': '+1 {name}',
  'shop.skills.added_ad': '+1 {name}（广告）',
  'shop.disrupt.desc': '打乱对手接下来 3 个支点的颜色。',
  'shop.shield.desc': '1 秒护盾 — 抵消干扰 + 忽略错按。',

  // TabBar
  'tab.shop': '商店',
  'tab.ranks': '排名',
  'tab.play': '游戏',
  'tab.bag': '背包',
  'tab.characters': '英雄',
  'tab.settings': '设置',

  // Login
  'login.title': "DON'T MISS",
  'login.subtitle': '登录以开始攀登',
  'login.guest': '游客试玩',
  'login.email': '邮箱',
  'login.password': '密码',
  'login.username': '用户名',
  'login.signin': '登录',
  'login.signup': '注册账号',
  'login.switch_to_signup': '还没有账号？立即创建',
  'login.switch_to_signin': '已有账号？登录',
  'login.continue_google': '使用 Google 继续',
  'login.continue_apple': '通过 Apple 登录',
  'login.or': '或',
  'login.error_invalid': '凭据无效',
  'login.error_taken': '用户名或邮箱已被使用',
  'login.error_generic': '登录失败',
  'login.oauth_not_configured': '{provider} 登录尚未配置 — 请使用邮箱或游客登录。',
  'login.server_unreachable': '无法连接服务器 — 请检查网络。',
  'login.tagline': '极速攀爬对决。4 种颜色。',
  'login.tagline_winner': '一个赢家。',
  'login.continue_email': '使用邮箱继续',
  'login.creating_guest': '创建游客中…',
  'login.play_guest': '游客试玩',
  'login.no_rewards': '无奖励',
  'login.terms_prefix': '继续即表示同意我们的',
  'login.terms': '条款',
  'login.privacy': '隐私',
  'login.not_configured': '未配置',
  'login.email_or_username': '邮箱或用户名',
  'auth.welcome_back': '欢迎回来，攀爬者。',
  'auth.pick_name': '取个名字，开始攀爬。',

  // Leaderboard
  'leaderboard.title': '排行榜',
  'leaderboard.tab_mmr': 'MMR',
  'leaderboard.tab_time': '最佳时间',
  'leaderboard.tab_tiers': '段位',
  'leaderboard.mode_solo': '单人 1V1',
  'leaderboard.mode_duo': '双人 2V2',
  'leaderboard.your_position': '你的排名 · #{n}',
  'leaderboard.unranked': '未排位',
  'leaderboard.empty': '尚无记录 — 成为第一人！',
  'leaderboard.tab_byrank': '按段位',
  'leaderboard.empty_time': '尚无完赛胜场。第一个创纪录吧！',
  'leaderboard.empty_players': '尚无玩家。',
  'leaderboard.you': '你',
  'leaderboard.unranked_yet': '— 尚未排位',
  'leaderboard.top': '前 {n}',
  'leaderboard.tier_empty': '尚无玩家达到此段位。',

  // Matchmaking
  'mm.searching_tag': '◌ 搜索中',
  'mm.found_tag': '◉ 已找到对手',
  'mm.ready_tag': '⚡ 准备就绪',
  'mm.searching_title': '寻找势均力敌的对手...',
  'mm.found_title': '对决来袭',
  'mm.ready_title': '战斗准备',
  'mm.offline': '离线 · AI 对手',
  'mm.stage': '关卡',
  'mm.stage_name': '幻彩攀升墙',
  'mm.stage_desc': '15.0 米 · 幻彩赛道 · 4 色序列',
  'mm.cancel': '× 取消搜索',
  'mm.scanning': '扫描中…',

  // Rank-up modal
  'rankup.promotion': '★ 晋级 ★',
  'rankup.demotion': '▼ 降级 ▼',
  'rankup.rank_up': '段位提升！',
  'rankup.demoted': '段位下降',
  'rankup.tap_continue': '点击继续',

  // Shop
  'shop.title': '商店',
  'shop.trial': '试用',
  'shop.trial_started': '试用 {name} {n} 场比赛',
  'shop.subtitle': '服装、帽子、披风与炫耀道具。精打细算 — 宝石很珍贵。',
  'shop.empty': '暂无物品。',
  'shop.loading': '加载中…',
  'shop.owned_tag': '已拥有',
  'shop.buy_coins': '购买 · {price}',
  'shop.buy_gems': '购买 · {price} 💎',
  'shop.not_enough': '不足',
  'shop.purchased': '购买成功！',
  'shop.failed': '购买失败',
  'shop.cat_heroes': '英雄',
  'shop.cat_skills': '技能',
  'shop.cat_holds': '支点',
  'shop.cat_frames': '头像框',
  'shop.acquired': '已获得：{name}',
  'shop.frames_hint': '头像框 — 在照片周围秀出风格。',
  'shop.no_frames': '尚无头像框。',
  'shop.claim': '领取',
  'chars.shop_section': '角色 — 英雄',
  'chars.loading': '加载英雄中…',
  'chars.unlocked': '已解锁 {name}！',
  'chars.purchase_failed': '购买失败',
  'chars.owned_equip': '✓ 已拥有 · 在背包中装备',
  'chars.win_power': '胜利能量 ×{power}',
  'chars.free_starter': '免费起始',
  'chars.bag_empty': '尚无英雄 — 去商店购买。',
  'chars.equipped_toast': '已装备 {name}',
  'chars.equip_failed': '装备失败',

  // Hold skins
  'holds.shop_section': '支点皮肤 — 墙面支撑',
  'holds.bag_section': '支点皮肤 — 墙面支撑',
  'holds.loading': '加载支点皮肤中…',
  'holds.unlocked': '已解锁 {name}！',
  'holds.purchase_failed': '购买失败',
  'holds.owned_equip': '✓ 已拥有 · 在背包中装备',
  'holds.free_default': '免费 · 默认轮换',
  'holds.bag_empty': '尚无支点皮肤 — 去商店购买。',
  'holds.equip': '装备',
  'holds.use_random': '随机使用',
  'holds.equipped': '✓ 已装备',
  'holds.equipped_toast': '已装备 {name}',
  'holds.unequipped_toast': '已恢复随机轮换',
  'holds.equip_failed': '装备失败',
  'holds.random_hint': '未装备皮肤 — 每场比赛随机挑选默认形状。',

  // Inventory
  'inv.title': '背包',
  'inv.subtitle': '装备你拥有的物品。右侧预览显示攀爬者。',
  'inv.empty': '此栏位暂无物品',
  'inv.equipped': '已装备',
  'inv.equip': '装备',
  'inv.trial_left': '剩 {n}',
  'inv.unequip': '卸下',
  'inv.tap_to_equip': '点击装备。每栏一件。',
  'inv.empty_all': '背包为空。',
  'inv.equipped_toast': '已装备：{name}',
  'inv.unequipped_toast': '{slot} 已卸下',
  'inv.skills_section': '技能 · 消耗品',
  'inv.buy_more': '购买更多 ›',
  'inv.skill_locked': '🔒 等级 {level} 解锁（你 Lv{my}）',
  'inv.skill_empty': '次数用尽 — 点击购买',
  'inv.skill_ready': '比赛中可用',
  'inv.owned_label': '已拥有',

  // Friends
  'friends.title': '好友',
  'friends.tab_friends': '好友',
  'friends.tab_global': '世界聊天',
  'friends.add_placeholder': '按用户名添加…',
  'friends.add_btn': '添加',
  'friends.friend_added': '已添加好友 ✓',
  'friends.request_sent': '请求已发送',
  'friends.incoming_requests': '收到的请求 · {n}',
  'friends.accept': '接受',
  'friends.decline': '拒绝',
  'friends.pending_sent': '待处理 · {n}',
  'friends.cancel': '取消',
  'friends.online_count': '在线 · {n}',
  'friends.none_online': '当前没有好友在线。',
  'friends.offline_count': '离线 · {n}',
  'friends.no_friends': '尚无好友 — 在上方搜索添加。',
  'friends.rank_mmr': '{rank} · {mmr} MMR',
  'friends.no_messages': '还没有消息。打个招呼吧 👋',
  'friends.say_something': '说点什么…',
  'friends.send': '发送',
  'friends.start_conversation': '开始对话 💬',
  'friends.message_placeholder': '发送给 {name}…',

  // Boss Break
  'boss.mode_label': '单人 PVE',
  'boss.title': '首领破击',
  'boss.subtitle': '完美点击得分 · 在短时首领战中坚持到底',
  'boss.loading': '正在召唤首领…',
  'boss.disabled_title': '模式尚未开放',
  'boss.disabled_body': '管理员可在管理后台开启首领破击。',
  'boss.load_error': '无法加载首领破击，请重试。',
  'boss.world_boss': '世界首领',
  'boss.target': '目标',
  'boss.stone_king': '岩石之王',
  'boss.stone_king_description': '在抓力耗尽前击破元素护甲。',
  'boss.frost_empress': '冰霜女皇',
  'boss.frost_empress_description': '在寒雾冻结所有抓点前击碎冰冠。',
  'boss.storm_roc': '雷霆巨鹏',
  'boss.storm_roc_description': '在雷暴中保持节奏，击晕天空霸主。',
  'boss.abyss_kraken': '深渊海妖',
  'boss.abyss_kraken_description': '在水压压垮抓力前斩断深渊触手。',
  'boss.verdant_colossus': '翠绿巨神',
  'boss.verdant_colossus_description': '焚毁重生核心，击破不断再生的古木。',
  'boss.inferno_wyrm': '炼狱火龙',
  'boss.inferno_wyrm_description': '穿越火海，击碎熔岩龙鳞并熄灭龙息。',
  'boss.eclipse_sovereign': '日蚀君王',
  'boss.eclipse_sovereign_description': '熬过日蚀轮回，摧毁终极虚空核心。',
  'boss.weekly_hunt': '七日困难首领狩猎',
  'boss.legendary_frame': '传奇头像框',
  'boss.frame_reward': '首领头像框',
  'boss.frame_owned': '已拥有头像框',
  'boss.frame_requirement': '困难 · PERFECT ≥{rate}%',
  'boss.frame_need_perfect': '头像框需要 PERFECT ≥{rate}%',
  'boss.weekly_bonus': '每周里程碑',
  'boss.competition.title': '完美得分排行榜',
  'boss.competition.attempts': '剩余 {remaining}/{total} 次',
  'boss.competition.daily': '每日得分',
  'boss.competition.weekly': '每周积分',
  'boss.competition.damage': '得分',
  'boss.competition.points': '积分',
  'boss.competition.my_rank': '排名 #{rank}',
  'boss.competition.closes': '本周截止 {time}',
  'boss.competition.closed': '每周结算中',
  'boss.competition.empty': '暂无成绩，抢占第一名吧。',
  'boss.competition.open': '查看排行榜',
  'boss.competition.close': '关闭',
  'boss.competition.rank': '排名',
  'boss.competition.attempts_label': '剩余次数',
  'boss.wins': '{n} 胜',
  'boss.starting': '创建中…',
  'boss.difficulty.normal': '普通',
  'boss.difficulty.hard': '困难',
  'boss.unlock_hard': '🔒 击败普通难度',
  'boss.daily_note': '首次通关 +{gems} 💎 · 每日 {limit} 次金币奖励',
  'boss.phase': '阶段 {phase} · {difficulty}',
  'boss.hp': '{name} · {remaining}/{total}',
  'boss.stunned_break': '眩晕 · 爆发 ×2',
  'boss.break_meter': '破防',
  'boss.combo': '{n} 连击',
  'boss.score_combo': '{score} 分 · {n} 连击',
  'boss.vulnerable': '易伤 {seconds}秒',
  'boss.burst_window': '爆发窗口 ×2',
  'boss.destroy_core': '击破元素核心',
  'boss.attack.slam': '⚠ 地裂重击',
  'boss.attack.shards': '⚠ 岩刺暴雨',
  'boss.attack.beam': '⚠ 四色核心炮',
  'boss.current_color_aria': '当前颜色：{color}',
  'boss.color.red': '红色',
  'boss.color.blue': '蓝色',
  'boss.color.yellow': '黄色',
  'boss.color.purple': '紫色',
  'boss.now': '当前',
  'boss.timing.zone': '命中区',
  'boss.timing.hit': '完美命中！',
  'boss.timing.early': '太早',
  'boss.timing.late': '太晚',
  'boss.timing.wrong': '颜色错误',
  'boss.grip': '抓力 · {current}/{max}',
  'boss.skill.disrupt': '破甲',
  'boss.skill.shield': '护盾',
  'boss.tap_color_aria': '点击{color}',
  'boss.go': '开始！',
  'boss.countdown_hint': '彩色核心进入命中区时点击对应颜色 · Q 破甲 · E 格挡攻击',
  'boss.verifying': '验证中…',
  'boss.defeated': '首领已击败',
  'boss.run_complete': '时间到 · 挑战完成',
  'boss.run_failed': '挑战失败',
  'boss.finish': '终结！',
  'boss.score_recorded': '成绩已记录！',
  'boss.grip_empty': '抓力耗尽！',
  'boss.stat.score': '得分',
  'boss.stat.perfect': '完美',
  'boss.stat.damage': '伤害',
  'boss.stat.combo': '连击',
  'boss.stat.time': '时间',
  'boss.frame_unlocked': '头像框已解锁',
  'boss.daily_cap': '已达每日上限',
  'boss.continue': '继续',
  'boss.home': '返回大厅',
  'boss.blocked': '格挡成功！',
  'boss.interrupted': '技能打断！',
  'boss.grip_damage': '-{damage} 抓力',

  // Settings
  'settings.title': '设置',
  'settings.language': '语言',
  'settings.audio': '音频',
  'settings.sfx': '音效',
  'settings.music': '音乐',
  'settings.haptic': '震动反馈',
  'settings.gameplay': '玩法',
  'settings.lefty': '左手布局',
  'settings.colorblind': '色盲调色板',
  'settings.tap_zone': '点击区域大小',
  'settings.avatar': '头像',
  'settings.account': '账号',
  'settings.guest_id': '游客 ID',
  'settings.username': '用户名',
  'settings.email': '邮箱',
  'settings.region': '区域',
  'settings.version': '版本',
  'settings.danger_zone': '危险操作',
  'settings.danger_guest': '退出登录以切换账号或注册新账号。',
  'settings.danger_user': '从此设备退出登录。',
  'settings.sign_out': '退出登录',
  'settings.confirm_signout': '确定要退出？',
  'settings.delete_account': '删除账号',
  'settings.confirm_delete_account': '删除账号？这将永久清除你的资料、进度和购买记录，且无法恢复。',
  'settings.confirm_delete_account_final': '此操作不可撤销。点击“确定”永久删除账号。',
  'settings.delete_account_failed': '无法删除账号，请重试。',
  'settings.avatar_custom': '自定义照片',
  'settings.avatar_character': '角色头像',
  'settings.avatar_default': '默认头像',
  'settings.avatar_formats': 'PNG / JPG / WebP · 最大 3 MB',
  'settings.avatar_change': '更改',
  'settings.avatar_upload': '上传照片',
  'settings.avatar_remove': '移除',

  // Toasts
  'toast.dm_new': '新私信',
  'toast.friend_request': '👋 好友请求',
  'toast.friend_request_body': '{name} 想加你为好友',
  'toast.now_friends': '✓ 已成为好友',
  'toast.now_friends_body': '{name} 接受了你的请求',
  'toast.room_invite': '🎮 {name} 邀请了你',
  'toast.room_invite_body': '点击加入房间 {code}',
  'toast.could_not_join': '无法加入房间',
  'toast.could_not_join_body': '邀请可能已过期或房间已满',

  // ── Clan / Clan War (2.0) ──
  'clan.title': '战队',
  'clan.ranks': '🏆 排行',
  'clan.ranks_title': '战队排行',
  'clan.find': '寻找战队',
  'clan.create': '创建',
  'clan.search_ph': '搜索名称或标签…',
  'clan.search': '搜索',
  'clan.none_found': '暂无战队。来创建第一个吧！',
  'clan.full': '已满',
  'clan.join': '加入',
  'clan.request': '申请',
  'clan.joined': '已加入！',
  'clan.welcome': '欢迎加入 {name}',
  'clan.request_sent': '已发送申请',
  'clan.request_waiting': '等待 {name} 批准',
  'clan.name_label': '战队名称 (3–20)',
  'clan.tag_label': '标签 (2–6, A–Z/0–9)',
  'clan.desc_label': '简介',
  'clan.emblem_label': '徽章',
  'clan.emblem_shop': '高级徽章',
  'clan.emblem_equip': '装备',
  'clan.emblem_equipped': '已装备',
  'clan.emblem_bought': '徽章已解锁！',
  'clan.emblem_reward': '赛季奖励',
  'clan.policy_label': '加入方式',
  'clan.policy_open': '开放 — 任何人可加入',
  'clan.policy_request': '需审批 — 审核成员',
  'clan.policy_invite': '仅限邀请',
  'clan.policy_short_open': '开放',
  'clan.policy_short_request': '需审批',
  'clan.policy_short_invite': '仅邀请',
  'clan.create_btn': '创建战队',
  'clan.creating': '创建中…',
  'clan.create_note': '可能需要金币 + 最低等级。',
  'clan.req_title': '创建条件',
  'clan.req_level': '等级 {n}+',
  'clan.req_coins': '{n} 金币',
  'clan.req_gems': '{n} 宝石',
  'clan.created': '战队已创建！',
  'clan.role.leader': '队长',
  'clan.role.coLeader': '副队长',
  'clan.role.elder': '长老',
  'clan.role.member': '成员',
  'clan.requests_title': '加入申请 ({n})',
  'clan.members_title': '成员',
  'clan.leave': '退出战队',
  'clan.disband': '解散',
  'clan.you': '(你)',
  'clan.confirm_leave': '退出此战队？',
  'clan.confirm_leave_leader': '退出并将队长移交给下一位干部？',
  'clan.confirm_disband': '解散 {name}？此操作无法撤销。',
  'clan.confirm_kick': '踢出 {name}？',
  'clan.confirm_make_leader': '让 {name} 成为队长？',
  'clan.left': '已退出战队',
  'clan.disbanded': '战队已解散',
  'clan.toast_title': '战队',
  'clan.err_generic': '出错了',
  'clan.err_profanity': '名称、标签或简介含有违禁词',
  'clan.now': '现在',
  'clan.notif.inbox_title': '部落消息',
  'clan.notif.empty': '暂无部落消息。',
  'clan.notif.join_request': '{name} 申请加入 {clan}',
  'clan.notif.join_approved': '你已加入 {clan}！',
  'clan.notif.join_rejected': '加入 {clan} 的申请被拒绝',
  'clan.notif.kicked': '你已被移出 {clan}',
  'clan.notif.role_changed': '你在 {clan} 的职位现为 {role}',
  'clan.notif.war_scheduled': '部落战已安排 — 准备进攻！',
  'clan.notif.war_open': '部落战开战 — 快去进攻！',
  'clan.notif.war_ended': '部落战结束 — {result}',
  'clan.notif.emblem_awarded': '第{rank}名！本周部落获得 {name} 徽章 🏆',
  'clan.notif.result_win': '胜利 🏆',
  'clan.notif.result_lose': '失败',
  'clan.notif.result_draw': '平局',
  'clan.war.title': '战队战争',
  'clan.war.daily_at': '每日 {t}',
  'clan.war.registered': '今日已报名',
  'clan.war.registered_ok': '战队已报名今日战争！',
  'clan.war.battle_at': '{t} 开战',
  'clan.war.register_btn': '报名今日战争',
  'clan.war.need_members': '至少需要 {n} 名成员',
  'clan.war.await_leader': '等待队长报名',
  'clan.war.reg_opens': '{t} 开放报名',
  'clan.war.prep': '准备 · {t} 后开战',
  'clan.war.battle': '战斗中 · 剩 {t}',
  'clan.war.vs': 'vs',
  'clan.war.find': '寻找战争对战 (剩 {n})',
  'clan.war.no_attacks': '没有次数了',
  'clan.war.not_open': '战斗未开始',
  'clan.war.prep_note': '准备阵容 — 即将开战。每人 {n} 场。',
  'clan.war.top': '贡献榜',
  'clan.war.stat': '{pt} 分 · {wins}胜',
  'clan.war.title_toast': '战队战争',
  'clan.season': '赛季 {n}',
  'clan.season_left': '剩 {t}',
  'clan.season_ending': '即将结束',
  'clan.lb_empty': '暂无战队上榜。',
};

// ── Japanese ─────────────────────────────────────────────────────────
const ja: Partial<Record<I18nKey, string>> = {
  // Common
  'common.cancel': 'キャンセル',
  'common.close': '閉じる',
  'common.preview': 'プレビュー',
  'common.confirm': '確認',
  'common.back': '戻る',
  'common.loading': '読み込み中…',
  'common.error': 'エラー',
  'common.ok': 'OK',
  'common.online': 'オンライン',
  'common.offline': 'オフライン',
  'common.send': '送信',
  'common.edit': '編集',
  'common.save': '保存',
  'common.network_error': 'ネットワークエラー',
  'chars.idle_preview': '待機 + 勝利プレビュー',
  'shop.finisher_preview': 'フィニッシャープレビュー',
  'playgate.title': 'アプリでプレイ',
  'playgate.body': '対戦は Don’t Miss アプリでのみプレイできます。無料でダウンロードしてプレイしましょう。',
  'playgate.ios': 'App Store でダウンロード',
  'playgate.android': 'Google Play で入手',
  'update.hard_title': '更新が必要です',
  'update.hard_body': 'プレイを続けるには Don’t Miss の新しいバージョンが必要です。更新してください。',
  'update.soft_title': '更新があります',
  'update.soft_body': '改善と修正を含む Don’t Miss の新しいバージョンが利用可能です。',
  'update.cta': '今すぐ更新',
  'update.later': 'あとで',
  'app.announcement_title': 'お知らせ',
  'app.maintenance_title': 'メンテナンス中',
  'app.maintenance_default': '現在メンテナンス中です。しばらくしてからもう一度お試しください。',
  'onboarding.welcome_title': 'Don’t Miss へようこそ！',
  'onboarding.welcome_body': '同じ色をタップして壁を登り、相手を倒そう。先に頂上へ着いた方が勝ち——タップを外さないで！',
  'onboarding.name_title': '名前を決めよう',
  'onboarding.name_body': '他のプレイヤーに表示される名前です。最初の変更は無料です。',
  'onboarding.shout_title': '勝利の一言を設定',
  'onboarding.shout_body': '勝ったときに相手に表示されます。印象に残る一言を！スキップして後で設定もできます。',
  'onboarding.next': '次へ',
  'onboarding.finish': 'プレイ開始',
  'onboarding.skip': '今はスキップ',
  'common.home': 'ホーム',
  'common.back_home': 'ホームに戻る',
  'common.dismiss': '閉じる',

  // Home
  'home.quick_match': '⚡ クイックマッチ',
  'home.daily_ad': 'デイリーボーナス',
  'quests.title': 'デイリークエスト',
  'quests.button': 'デイリークエスト',
  'quests.reset_note': '毎日リセット',
  'quests.share': 'SNSでシェア',
  'quests.play': '5回プレイ',
  'quests.win': '10回勝利',
  'quests.claim': '受取',
  'quests.claimed': '受取済み',
  'quests.share_btn': 'シェア',
  'quests.share_text': "🧗‍♂️『Don't Miss!』でランキング駆け上がり中！超高速1v1の色タップ対決——正しい色をタップして壁を登り、ライバルを撃破。僕のタイム、抜けるかな？🔴🔵🟡🟣 勝負しよう 👇",
  'home.daily_ad_watching': '広告読み込み中…',
  'home.create_room': '+ ルーム作成',
  'home.join_room': '↪ コードで参加',
  'home.modes_subtitle': '⚡ ソロ 1v1 · デュオ · チーム · トーナメント近日公開',
  'home.avg_wait': '平均待機 4 秒',
  'home.could_not_create_room': 'ルームを作成できませんでした',
  'home.code_too_short': 'ルームコードが短すぎます',
  'home.could_not_join': 'ルームに参加できませんでした',
  'home.join_room_title': 'ルームに参加',
  'home.equipped_skills': '装備スキル',
  'home.buy_more': 'もっと買う ›',
  'home.skill_buy_cta': '購入',
  'home.open_profile': 'プロフィールを開く',
  'home.role_climber': 'クライマー',
  'home.win_streak': '連勝',
  'home.join_btn': '参加',
  'home.display_name': '表示名',
  'home.display_name_ph': 'ゲーム内の名前',
  'home.victory_shout': '勝利の叫び',
  'home.victory_shout_ph': '例：GG! · 余裕 💪',
  'home.first_change_free': '初回変更無料',
  'home.field_none': '— なし —',
  'home.not_enough_gems': 'ジェムが不足しています',
  'home.max_chars': '最大 {n} 文字',
  'profanity.blocked': 'この語句は使用できません。別の語句を選んでください。',
  'home.save_cost': '保存 · {cost}💎',

  // Room
  'room.leave': '← 退出',
  'room.chat': '◐ チャット',
  'room.code_label': 'ルームコード',
  'room.tap_to_copy': 'タップでコピー',
  'room.copied': '✓ コピー済み',
  'room.team_a': 'チーム A',
  'room.team_b': 'チーム B',
  'room.join_slot': '参加',
  'room.you_suffix': '(あなた)',
  'room.ready_label': '準備完了',
  'room.tap_to_ready': 'タップで準備',
  'room.invite_friend': '+ 友達を招待',
  'room.share_to_chat': '📢 チャットに共有',
  'room.shared_to_chat': '✓ 共有済み',
  'room.open_chat_tip': 'チームチャットを開く',
  'room.chat_title': 'チームチャット',
  'room.chat_placeholder': 'チームにメッセージ…',
  'room.chat_empty': 'まだメッセージはありません。チームに挨拶しよう！',
  'room.chat_send': '送信',
  'room.chat_expand': 'チャットを拡大',
  'room.chat_collapse': '縮小',
  'room.kick_tip': 'キック',
  'room.waiting_host': 'ホストの開始を待っています…',
  'room.invite_modal_title': '友達を招待',
  'room.no_friends_to_invite': '招待できる友達がまだいません。チャットを開いてワールドでアバターをタップ。',
  'room.in_room': '入室済み',
  'room.invited': '✓ 招待済み',
  'room.invite_btn': '招待',
  'room.offline_send_hint': '友達はオフライン — 受け取れません',
  'room.send_invite': '招待を送信',
  'room.start': '▶ スタート',
  'room.start_quick': '▶ スタート · クイック',
  'room.start_duel': '▶ スタート · デュエル',
  'room.start_team': '▶ スタート · チーム',
  'room.mode.empty_desc': '友達をスロットに招待して開始',
  'room.mode.quick': 'クイックマッチ',
  'room.mode.quick_desc': '開始するとランダムなソロマッチのキューに入ります',
  'room.mode.solo_duel': 'ソロデュエル · 1v1',
  'room.mode.solo_duel_desc': '両者の直接対決 — マッチングをスキップ',
  'room.mode.duo_rank': 'デュオランク · 2v2',
  'room.mode.duo_rank_desc': 'パートナーとランダムな 2 人と対戦',
  'room.mode.team_duel': 'チームデュエル · 2v2',
  'room.mode.team_duel_desc': '2 チーム直接 2v2 対戦',
  'room.team_mode_label': 'チームモード',
  'room.tmode.alternating': '交互',
  'room.tmode.alternating_desc': '5 ホールドごとに交代。チームコンボでスキル解放。',
  'room.tmode.split_color': 'カラー分担',
  'room.tmode.split_color_desc': '各プレイヤーが 2 色担当。コンボ 5 で両スキル解放。',
  'room.tmode.attack_defense': '攻撃 · 守備',
  'room.tmode.attack_defense_desc': '1 人が登り、1 人が役割でスキルを使用。',
  'room.mode.imbalanced': '不均衡',
  'room.mode.imbalanced_desc': '両陣営を揃えてください（1v1 または 2v2）',

  // Team 2v2 in-match status
  'match.team.your_turn': 'あなたのターン — 壁をタップ',
  'match.team.teammate_turn': '味方のターン',
  'match.team.you_climb': 'あなたが登る — 壁をタップ',
  'match.team.you_cast': 'あなたが支援 — スキルを使用',

  // Match
  'match.you_win': '勝利!',
  'match.you_lose': '敗北',
  'match.summary': '▶ サマリー',
  'match.tip_label': 'ヒント',
  'match.auto_continue': '{secs}秒後に自動的に続行',
  'match.tap_color': '{color} をタップ',
  'match.quit_aria': 'マッチを終了',
  'match.combo': '×{n} コンボ',
  'match.you': 'あなた',
  'match.rival': 'ライバル',

  // Result
  'result.match_result': 'マッチ結果',
  'result.victory': '勝利!',
  'result.defeat': '敗北',
  'result.rewards': '報酬',
  'result.x2_button': '報酬2倍',
  'result.x2_watching': '広告読み込み中…',
  'result.x2_done': '報酬2倍を獲得！',
  'result.battle_stats': 'バトル統計',
  'result.rematch': '▶ 再戦',
  'result.home': 'ホーム',
  'result.back_to_room': '▶ ルームに戻る',
  'result.back_to_war': '⚔️ クランウォーに戻る',
  'result.leave_room': 'ルームを離れる',
  'result.opponent_shouts': '相手の叫び',
  'result.coins': 'ゴールド',
  'result.stat_speed': 'スピード',
  'result.stat_accuracy': '正確性',
  'result.stat_combo_max': '最高コンボ',

  // Chat
  'chat.global': 'ワールド',
  'chat.clan': 'クラン',
  'chat.placeholder_clan': 'クランにメッセージ…',
  'chat.dms': 'DM',
  'chat.no_messages': 'まだメッセージがありません — 最初に送信しましょう！',
  'chat.no_dms': 'まだメッセージがありません。ワールドチャットでアバターをタップして友達追加または DM 送信。',
  'chat.copy_tip': 'ダブルタップでコピー',
  'chat.copied': 'コピーしました！',
  'chat.copy_hint': '💡 ダブルタップで任意のメッセージをコピー',
  'chat.open_chat': 'チャットを開く',
  'chat.back_to_conversations': '会話に戻る',
  'chat.share_room_btn': '📍 ルーム {code} を共有',
  'chat.share_room_msg': '私のルームに参加！タップで参加 👉 [{code}]',
  'chat.join_room': '▶ ルームに参加',
  'chat.open_room': '▶ ルームを開く',
  'chat.placeholder_global': '全員とチャット…',
  'chat.placeholder_dm': '{name} にメッセージ…',
  'chat.minimize': '最小化',
  'chat.close': '閉じる',
  'chat.add_friend': '+ 友達追加',
  'chat.username_placeholder': 'プレイヤー名',
  'chat.btn_add': '追加',
  'chat.section.room_invites': 'ルーム招待 · {n}',
  'chat.section.friend_requests': '友達リクエスト · {n}',
  'chat.section.conversations': '会話',
  'chat.invite.tagline': 'ルーム {code} に招待されました',
  'chat.invite.join': '▶ 参加',
  'chat.invite.decline': '拒否',
  'chat.request.tagline': '友達になりたがっています',
  'chat.request.accept': 'リクエストを承認',
  'chat.request.decline': 'リクエストを拒否',
  'chat.open_dm': 'DM を開く',
  'chat.view_profile_tip': 'プロフィール · 友達追加 · DM',
  'chat.manage_friend_tip': 'プロフィール / 友達管理',
  'chat.you_prefix': 'あなた：{text}',
  'chat.now_friends': '友達になりました！',
  'chat.request_sent': '友達リクエストを送信しました',
  'chat.send_failed': 'リクエストの送信に失敗',

  // User quick menu
  'qm.profile_load_failed': 'プロフィールを読み込めませんでした',
  'qm.rank': 'ランク',
  'qm.mmr': 'MMR',
  'qm.matches': 'マッチ',
  'qm.win_rate': '勝率',
  'qm.add_friend': '+ 友達追加',
  'qm.request_sent': '⏳ リクエスト送信済み',
  'qm.accept_request': '✓ リクエストを承認',
  'qm.friends_unfriend': '✓ 友達 · 解除',
  'qm.send_dm': '💬 DM を送る',
  'qm.invite_to_room': '🎮 ルーム {code} に招待',
  'qm.invited_to_room': '✓ {code} に招待済み',
  'qm.invite_failed': '招待に失敗',
  'qm.invited_toast': 'ルーム {code} に招待しました',
  'qm.unfriended': '友達を解除しました',
  'qm.accepted': '承認しました',

  // Shop / Skills
  'shop.skills.section': 'スキル — 消耗品',
  'shop.skills.loading': 'スキル読み込み中…',
  'shop.skills.owned': '所持',
  'shop.skills.unlocks_at': '🔒 Lv{level} で解放（あなた Lv{my}）',
  'shop.skills.bought_today': '本日購入：{n}/{max}',
  'shop.skills.ad_today': '広告：{n}/{max}',
  'shop.skills.watching': '視聴中…',
  'shop.skills.purchase_failed': '購入に失敗',
  'shop.skills.ad_failed': '広告報酬に失敗',
  'shop.skills.added': '+1 {name}',
  'shop.skills.added_ad': '+1 {name}（広告）',
  'shop.disrupt.desc': '相手の次の 3 ホールドの色を撹乱します。',
  'shop.shield.desc': '1 秒シールド — 妨害を打ち消し、誤タップを無視。',

  // TabBar
  'tab.shop': 'ショップ',
  'tab.ranks': 'ランク',
  'tab.play': 'プレイ',
  'tab.bag': 'バッグ',
  'tab.characters': 'ヒーロー',
  'tab.settings': '設定',

  // Login
  'login.title': "DON'T MISS",
  'login.subtitle': 'サインインしてクライム',
  'login.guest': 'ゲストプレイ',
  'login.email': 'メール',
  'login.password': 'パスワード',
  'login.username': 'ユーザー名',
  'login.signin': 'サインイン',
  'login.signup': 'アカウント作成',
  'login.switch_to_signup': 'アカウントがない？作成',
  'login.switch_to_signin': 'アカウントをお持ちですか？サインイン',
  'login.continue_google': 'Google で続行',
  'login.continue_apple': 'Apple でサインイン',
  'login.or': 'または',
  'login.error_invalid': '認証情報が無効です',
  'login.error_taken': 'ユーザー名またはメールは既に使用されています',
  'login.error_generic': 'サインインに失敗',
  'login.oauth_not_configured': '{provider} ログインは未設定 — メールまたはゲストをお試しください。',
  'login.server_unreachable': 'サーバーに接続できません — 接続を確認してください。',
  'login.tagline': 'スピードクライミング対決。4 色。',
  'login.tagline_winner': '勝者は 1 人。',
  'login.continue_email': 'メールで続行',
  'login.creating_guest': 'ゲスト作成中…',
  'login.play_guest': 'ゲストでプレイ',
  'login.no_rewards': '報酬なし',
  'login.terms_prefix': '続行することで以下に同意：',
  'login.terms': '利用規約',
  'login.privacy': 'プライバシー',
  'login.not_configured': '未設定',
  'login.email_or_username': 'メールまたはユーザー名',
  'auth.welcome_back': 'お帰りなさい、クライマー。',
  'auth.pick_name': '名前を決めて、クライムを開始。',

  // Leaderboard
  'leaderboard.title': 'リーダーボード',
  'leaderboard.tab_mmr': 'MMR',
  'leaderboard.tab_time': 'ベストタイム',
  'leaderboard.tab_tiers': 'ティア',
  'leaderboard.mode_solo': 'ソロ 1V1',
  'leaderboard.mode_duo': 'デュオ 2V2',
  'leaderboard.your_position': 'あなたの順位 · #{n}',
  'leaderboard.unranked': '未ランク',
  'leaderboard.empty': 'まだ記録なし — 最初の 1 人になろう！',
  'leaderboard.tab_byrank': 'ランク別',
  'leaderboard.empty_time': 'まだ完走勝利がありません。最初の記録を作ろう！',
  'leaderboard.empty_players': 'まだプレイヤーがいません。',
  'leaderboard.you': 'あなた',
  'leaderboard.unranked_yet': '— まだ未ランク',
  'leaderboard.top': 'トップ {n}',
  'leaderboard.tier_empty': 'まだ誰もこのティアに到達していません。',

  // Matchmaking
  'mm.searching_tag': '◌ 検索中',
  'mm.found_tag': '◉ ライバル発見',
  'mm.ready_tag': '⚡ 準備',
  'mm.searching_title': 'ふさわしいライバルを探しています...',
  'mm.found_title': 'デュエル到着',
  'mm.ready_title': 'バトルステーション',
  'mm.offline': 'オフライン · AI ライバル',
  'mm.stage': 'ステージ',
  'mm.stage_name': 'クロマ・アセントウォール',
  'mm.stage_desc': '15.0m · クロマコース · 4 色シーケンス',
  'mm.cancel': '× 検索キャンセル',
  'mm.scanning': 'スキャン中…',

  // Rank-up modal
  'rankup.promotion': '★ 昇格 ★',
  'rankup.demotion': '▼ 降格 ▼',
  'rankup.rank_up': 'ランクアップ！',
  'rankup.demoted': '降格しました',
  'rankup.tap_continue': 'タップで続行',

  // Shop
  'shop.title': 'ショップ',
  'shop.trial': 'お試し',
  'shop.trial_started': '{name} を{n}試合お試し',
  'shop.subtitle': '衣装、帽子、マント、自慢アイテム。賢く使おう — ジェムは貴重。',
  'shop.empty': 'まだ何もありません。',
  'shop.loading': '読み込み中…',
  'shop.owned_tag': '所持済み',
  'shop.buy_coins': '購入 · {price}',
  'shop.buy_gems': '購入 · {price} 💎',
  'shop.not_enough': '不足',
  'shop.purchased': '購入しました！',
  'shop.failed': '購入に失敗',
  'shop.cat_heroes': 'ヒーロー',
  'shop.cat_skills': 'スキル',
  'shop.cat_holds': 'ホールド',
  'shop.cat_frames': 'フレーム',
  'shop.acquired': '入手：{name}',
  'shop.frames_hint': 'アバターフレーム — 写真の周りでスタイルを披露。',
  'shop.no_frames': 'まだフレームがありません。',
  'shop.claim': '受け取る',
  'chars.shop_section': 'キャラクター — ヒーロー',
  'chars.loading': 'ヒーロー読み込み中…',
  'chars.unlocked': '{name} を解放！',
  'chars.purchase_failed': '購入に失敗',
  'chars.owned_equip': '✓ 所持 · バッグで装備',
  'chars.win_power': '勝利パワー ×{power}',
  'chars.free_starter': '無料スターター',
  'chars.bag_empty': 'まだヒーローがいません — ショップで購入。',
  'chars.equipped_toast': '{name} を装備',
  'chars.equip_failed': '装備に失敗',

  // Hold skins
  'holds.shop_section': 'ホールドスキン — 壁のグリップ',
  'holds.bag_section': 'ホールドスキン — 壁のグリップ',
  'holds.loading': 'ホールドスキン読み込み中…',
  'holds.unlocked': '{name} を解放！',
  'holds.purchase_failed': '購入に失敗',
  'holds.owned_equip': '✓ 所持 · バッグで装備',
  'holds.free_default': '無料 · 通常ローテーション',
  'holds.bag_empty': 'まだホールドスキンがありません — ショップで購入。',
  'holds.equip': '装備',
  'holds.use_random': 'ランダム使用',
  'holds.equipped': '✓ 装備済み',
  'holds.equipped_toast': '{name} を装備',
  'holds.unequipped_toast': 'ランダムローテーションに戻りました',
  'holds.equip_failed': '装備に失敗',
  'holds.random_hint': 'スキン未装備 — 各マッチでランダムな既定形状が選ばれます。',

  // Inventory
  'inv.title': 'インベントリ',
  'inv.subtitle': '所持アイテムを装備。右側プレビューでクライマーを表示。',
  'inv.empty': 'このスロットにはアイテムがありません',
  'inv.equipped': '装備中',
  'inv.equip': '装備',
  'inv.trial_left': '残り{n}',
  'inv.unequip': '外す',
  'inv.tap_to_equip': 'タップして装備。1 スロット 1 アイテム。',
  'inv.empty_all': 'インベントリが空です。',
  'inv.equipped_toast': '装備：{name}',
  'inv.unequipped_toast': '{slot} を外しました',
  'inv.skills_section': 'スキル · 消耗品',
  'inv.buy_more': 'もっと買う ›',
  'inv.skill_locked': '🔒 Lv{level} で解放（あなた Lv{my}）',
  'inv.skill_empty': '残量なし — タップで購入',
  'inv.skill_ready': 'マッチで使用可能',
  'inv.owned_label': '所持',

  // Friends
  'friends.title': 'フレンド',
  'friends.tab_friends': 'フレンド',
  'friends.tab_global': 'ワールドチャット',
  'friends.add_placeholder': 'ユーザー名で追加…',
  'friends.add_btn': '追加',
  'friends.friend_added': 'フレンド追加 ✓',
  'friends.request_sent': 'リクエスト送信',
  'friends.incoming_requests': '受信リクエスト · {n}',
  'friends.accept': '承認',
  'friends.decline': '拒否',
  'friends.pending_sent': '送信済み · {n}',
  'friends.cancel': 'キャンセル',
  'friends.online_count': 'オンライン · {n}',
  'friends.none_online': '現在オンラインのフレンドはいません。',
  'friends.offline_count': 'オフライン · {n}',
  'friends.no_friends': 'まだフレンドがいません — 上で検索して追加。',
  'friends.rank_mmr': '{rank} · {mmr} MMR',
  'friends.no_messages': 'まだメッセージなし。挨拶してみよう 👋',
  'friends.say_something': '何か話そう…',
  'friends.send': '送信',
  'friends.start_conversation': '会話を始めよう 💬',
  'friends.message_placeholder': '{name} にメッセージ…',

  // Boss Break
  'boss.mode_label': 'ソロ PVE',
  'boss.title': 'ボスブレイク',
  'boss.subtitle': 'パーフェクトで得点 · 短時間のボスラッシュを生き残れ',
  'boss.loading': 'ボス召喚中…',
  'boss.disabled_title': 'モード未解放',
  'boss.disabled_body': '管理画面からボスブレイクを有効にできます。',
  'boss.load_error': 'ボスブレイクを読み込めません。もう一度お試しください。',
  'boss.world_boss': 'ワールドボス',
  'boss.target': 'ターゲット',
  'boss.stone_king': 'ストーンキング',
  'boss.stone_king_description': 'グリップが尽きる前に属性装甲を破壊しよう。',
  'boss.frost_empress': 'フロストエンプレス',
  'boss.frost_empress_description': '霧が全てを凍らせる前に氷冠を砕け。',
  'boss.storm_roc': 'ストームロック',
  'boss.storm_roc_description': '雷嵐のリズムを刻み、天空の王を怯ませろ。',
  'boss.abyss_kraken': 'アビスクラーケン',
  'boss.abyss_kraken_description': '水圧に潰される前に深淵の触手を断て。',
  'boss.verdant_colossus': 'ヴァーダントコロッサス',
  'boss.verdant_colossus_description': '再生核を燃やし、蘇る古木を打ち破れ。',
  'boss.inferno_wyrm': 'インフェルノワーム',
  'boss.inferno_wyrm_description': '炎を越え、溶岩の鱗と竜炎を打ち砕け。',
  'boss.eclipse_sovereign': 'エクリプスソブリン',
  'boss.eclipse_sovereign_description': '日食の周期を耐え、虚無の核を破壊せよ。',
  'boss.weekly_hunt': '7日間ハードボスハント',
  'boss.legendary_frame': '伝説フレーム',
  'boss.frame_reward': 'ボスフレーム',
  'boss.frame_owned': 'フレーム獲得済み',
  'boss.frame_requirement': 'ハード · PERFECT ≥{rate}%',
  'boss.frame_need_perfect': 'フレームには PERFECT ≥{rate}% が必要',
  'boss.weekly_bonus': '週間マイルストーン',
  'boss.competition.title': 'パーフェクトスコアランキング',
  'boss.competition.attempts': '残り {remaining}/{total} 回',
  'boss.competition.daily': 'デイリースコア',
  'boss.competition.weekly': '週間ポイント',
  'boss.competition.damage': 'スコア',
  'boss.competition.points': 'PT',
  'boss.competition.my_rank': '順位 #{rank}',
  'boss.competition.closes': '週間締切 {time}',
  'boss.competition.closed': '週間集計中',
  'boss.competition.empty': 'まだ記録がありません。最初の1位を狙おう。',
  'boss.competition.open': 'ランキングを見る',
  'boss.competition.close': '閉じる',
  'boss.competition.rank': '順位',
  'boss.competition.attempts_label': '残り挑戦',
  'boss.wins': '{n} 勝',
  'boss.starting': '作成中…',
  'boss.difficulty.normal': 'ノーマル',
  'boss.difficulty.hard': 'ハード',
  'boss.unlock_hard': '🔒 ノーマルをクリア',
  'boss.daily_note': '初回 +{gems} 💎 · ゴールド報酬は1日 {limit} 回',
  'boss.phase': 'フェーズ {phase} · {difficulty}',
  'boss.hp': '{name} · {remaining}/{total}',
  'boss.stunned_break': 'スタン · バースト ×2',
  'boss.break_meter': 'ブレイク',
  'boss.combo': '{n} コンボ',
  'boss.score_combo': '{score} PT · {n} コンボ',
  'boss.vulnerable': '弱体化 {seconds}秒',
  'boss.burst_window': 'バーストチャンス ×2',
  'boss.destroy_core': '属性コアを破壊',
  'boss.attack.slam': '⚠ アースシェイカー',
  'boss.attack.shards': '⚠ 岩片の豪雨',
  'boss.attack.beam': '⚠ 四色コア砲',
  'boss.current_color_aria': '現在の色：{color}',
  'boss.color.red': '赤',
  'boss.color.blue': '青',
  'boss.color.yellow': '黄',
  'boss.color.purple': '紫',
  'boss.now': '現在',
  'boss.timing.zone': 'ヒットゾーン',
  'boss.timing.hit': 'パーフェクト！',
  'boss.timing.early': '早すぎ',
  'boss.timing.late': '遅すぎ',
  'boss.timing.wrong': '色が違う',
  'boss.grip': 'グリップ · {current}/{max}',
  'boss.skill.disrupt': 'ブレイク',
  'boss.skill.shield': 'シールド',
  'boss.tap_color_aria': '{color}をタップ',
  'boss.go': 'GO!',
  'boss.countdown_hint': 'コアがヒットゾーンに入ったら同じ色をタップ · Q で破甲 · E で防御',
  'boss.verifying': '確認中…',
  'boss.defeated': 'ボス撃破',
  'boss.run_complete': 'タイムアップ · 完了',
  'boss.run_failed': 'チャレンジ失敗',
  'boss.finish': 'フィニッシュ！',
  'boss.score_recorded': 'スコア記録！',
  'boss.grip_empty': 'グリップ切れ！',
  'boss.stat.score': 'スコア',
  'boss.stat.perfect': 'パーフェクト',
  'boss.stat.damage': 'ダメージ',
  'boss.stat.combo': 'コンボ',
  'boss.stat.time': 'タイム',
  'boss.frame_unlocked': 'フレーム解放',
  'boss.daily_cap': '本日の上限',
  'boss.continue': '続ける',
  'boss.home': 'ロビーへ',
  'boss.blocked': 'ガード！',
  'boss.interrupted': '詠唱中断！',
  'boss.grip_damage': '-{damage} グリップ',

  // Settings
  'settings.title': '設定',
  'settings.language': '言語',
  'settings.audio': 'オーディオ',
  'settings.sfx': '効果音',
  'settings.music': '音楽',
  'settings.haptic': '触覚フィードバック',
  'settings.gameplay': 'ゲームプレイ',
  'settings.lefty': '左手レイアウト',
  'settings.colorblind': '色覚多様性パレット',
  'settings.tap_zone': 'タップエリアサイズ',
  'settings.avatar': 'アバター',
  'settings.account': 'アカウント',
  'settings.guest_id': 'ゲスト ID',
  'settings.username': 'ユーザー名',
  'settings.email': 'メール',
  'settings.region': 'リージョン',
  'settings.version': 'バージョン',
  'settings.danger_zone': '危険な操作',
  'settings.danger_guest': 'サインアウトして別アカウントに切替または新規作成。',
  'settings.danger_user': 'このデバイスからサインアウト。',
  'settings.sign_out': 'サインアウト',
  'settings.confirm_signout': 'サインアウトしますか？',
  'settings.delete_account': 'アカウント削除',
  'settings.confirm_delete_account':
    'アカウントを削除しますか？プロフィール・進行状況・購入履歴が完全に削除され、元に戻せません。',
  'settings.confirm_delete_account_final': 'この操作は取り消せません。OK でアカウントを完全に削除します。',
  'settings.delete_account_failed': 'アカウントを削除できませんでした。もう一度お試しください。',
  'settings.avatar_custom': 'カスタム写真',
  'settings.avatar_character': 'キャラクターアバター',
  'settings.avatar_default': 'デフォルトアバター',
  'settings.avatar_formats': 'PNG / JPG / WebP · 最大 3 MB',
  'settings.avatar_change': '変更',
  'settings.avatar_upload': '写真をアップロード',
  'settings.avatar_remove': '削除',

  // Toasts
  'toast.dm_new': '新着 DM',
  'toast.friend_request': '👋 フレンドリクエスト',
  'toast.friend_request_body': '{name} があなたとフレンドになりたい',
  'toast.now_friends': '✓ フレンドになりました',
  'toast.now_friends_body': '{name} がリクエストを承認',
  'toast.room_invite': '🎮 {name} からの招待',
  'toast.room_invite_body': 'タップしてルーム {code} に参加',
  'toast.could_not_join': 'ルームに参加できませんでした',
  'toast.could_not_join_body': '招待が期限切れまたはルームが満員',

  // ── Clan / Clan War (2.0) ──
  'clan.title': 'クラン',
  'clan.ranks': '🏆 ランク',
  'clan.ranks_title': 'クランランキング',
  'clan.find': 'クランを探す',
  'clan.create': '作成',
  'clan.search_ph': '名前またはタグで検索…',
  'clan.search': '検索',
  'clan.none_found': 'クランがありません。最初の一つを作ろう！',
  'clan.full': '満員',
  'clan.join': '参加',
  'clan.request': '申請',
  'clan.joined': '参加しました！',
  'clan.welcome': '{name} へようこそ',
  'clan.request_sent': '申請を送信しました',
  'clan.request_waiting': '{name} の承認待ち',
  'clan.name_label': 'クラン名 (3–20)',
  'clan.tag_label': 'タグ (2–6, A–Z/0–9)',
  'clan.desc_label': '説明',
  'clan.emblem_label': 'エンブレム',
  'clan.emblem_shop': 'プレミアムロゴ',
  'clan.emblem_equip': '装備',
  'clan.emblem_equipped': '装備中',
  'clan.emblem_bought': 'ロゴを解放！',
  'clan.emblem_reward': 'シーズン報酬',
  'clan.policy_label': '参加方法',
  'clan.policy_open': 'オープン — 誰でも参加',
  'clan.policy_request': '承認制 — メンバーを承認',
  'clan.policy_invite': '招待のみ',
  'clan.policy_short_open': 'オープン',
  'clan.policy_short_request': '承認制',
  'clan.policy_short_invite': '招待のみ',
  'clan.create_btn': 'クランを作成',
  'clan.creating': '作成中…',
  'clan.create_note': 'コスト + 最低レベルが必要な場合があります。',
  'clan.req_title': '作成条件',
  'clan.req_level': 'レベル {n}以上',
  'clan.req_coins': '{n} コイン',
  'clan.req_gems': '{n} ジェム',
  'clan.created': 'クランを作成しました！',
  'clan.role.leader': 'リーダー',
  'clan.role.coLeader': 'サブリーダー',
  'clan.role.elder': '長老',
  'clan.role.member': 'メンバー',
  'clan.requests_title': '参加申請 ({n})',
  'clan.members_title': 'メンバー',
  'clan.leave': 'クランを脱退',
  'clan.disband': '解散',
  'clan.you': '(あなた)',
  'clan.confirm_leave': 'このクランを脱退しますか？',
  'clan.confirm_leave_leader': '脱退して次の幹部にリーダーを譲りますか？',
  'clan.confirm_disband': '{name} を解散しますか？元に戻せません。',
  'clan.confirm_kick': '{name} をキックしますか？',
  'clan.confirm_make_leader': '{name} をリーダーにしますか？',
  'clan.left': 'クランを脱退しました',
  'clan.disbanded': 'クランを解散しました',
  'clan.toast_title': 'クラン',
  'clan.err_generic': 'エラーが発生しました',
  'clan.err_profanity': '名前・タグ・説明に禁止語が含まれています',
  'clan.now': 'まもなく',
  'clan.notif.inbox_title': 'クランニュース',
  'clan.notif.empty': 'クランのお知らせはまだありません。',
  'clan.notif.join_request': '{name} が {clan} への参加を申請',
  'clan.notif.join_approved': '{clan} に参加しました！',
  'clan.notif.join_rejected': '{clan} への参加申請は却下されました',
  'clan.notif.kicked': '{clan} から除名されました',
  'clan.notif.role_changed': '{clan} での役職が {role} になりました',
  'clan.notif.war_scheduled': 'クランウォーが設定されました — 準備を！',
  'clan.notif.war_open': 'クランウォー開戦 — 攻撃しよう！',
  'clan.notif.war_ended': 'クランウォー終了 — {result}',
  'clan.notif.emblem_awarded': '第{rank}位！今週は {name} ロゴを獲得 🏆',
  'clan.notif.result_win': '勝利 🏆',
  'clan.notif.result_lose': '敗北',
  'clan.notif.result_draw': '引き分け',
  'clan.war.title': 'クラン戦',
  'clan.war.daily_at': '毎日 {t}',
  'clan.war.registered': '本日エントリー済み',
  'clan.war.registered_ok': 'クランが本日の戦争にエントリーしました！',
  'clan.war.battle_at': '{t} に開戦',
  'clan.war.register_btn': '本日の戦争にエントリー',
  'clan.war.need_members': 'メンバーが{n}人以上必要です',
  'clan.war.await_leader': 'リーダーのエントリー待ち',
  'clan.war.reg_opens': '{t} にエントリー開始',
  'clan.war.prep': '準備 · {t} 後に開戦',
  'clan.war.battle': '戦闘中 · 残り {t}',
  'clan.war.vs': 'vs',
  'clan.war.find': '対戦を探す (残り {n})',
  'clan.war.no_attacks': '回数がありません',
  'clan.war.not_open': '戦闘は未開始',
  'clan.war.prep_note': '編成を準備 — まもなく開戦。各 {n} 戦。',
  'clan.war.top': '貢献ランキング',
  'clan.war.stat': '{pt} pt · {wins}勝',
  'clan.war.title_toast': 'クラン戦',
  'clan.season': 'シーズン {n}',
  'clan.season_left': '残り {t}',
  'clan.season_ending': 'まもなく終了',
  'clan.lb_empty': 'ランクインしたクランはありません。',
};

// ── Korean ───────────────────────────────────────────────────────────
const ko: Partial<Record<I18nKey, string>> = {
  // Common
  'common.cancel': '취소',
  'common.close': '닫기',
  'common.preview': '미리보기',
  'common.confirm': '확인',
  'common.back': '뒤로',
  'common.loading': '로딩 중…',
  'common.error': '오류',
  'common.ok': '확인',
  'common.online': '온라인',
  'common.offline': '오프라인',
  'common.send': '전송',
  'common.edit': '편집',
  'common.save': '저장',
  'common.network_error': '네트워크 오류',
  'chars.idle_preview': '대기 + 승리 미리보기',
  'shop.finisher_preview': '피니셔 미리보기',
  'playgate.title': '앱에서 플레이',
  'playgate.body': '대전은 Don’t Miss 앱에서만 즐길 수 있습니다. 무료로 다운로드하여 플레이하세요.',
  'playgate.ios': 'App Store에서 다운로드',
  'playgate.android': 'Google Play에서 받기',
  'update.hard_title': '업데이트 필요',
  'update.hard_body': '계속 플레이하려면 Don’t Miss의 새 버전이 필요합니다. 업데이트해 주세요.',
  'update.soft_title': '업데이트 가능',
  'update.soft_body': '개선 사항과 버그 수정이 포함된 Don’t Miss의 새 버전이 있습니다.',
  'update.cta': '지금 업데이트',
  'update.later': '나중에',
  'app.announcement_title': '공지',
  'app.maintenance_title': '점검 중',
  'app.maintenance_default': '현재 앱을 점검 중입니다. 잠시 후 다시 이용해 주세요.',
  'onboarding.welcome_title': 'Don’t Miss에 오신 걸 환영합니다!',
  'onboarding.welcome_body': '같은 색을 탭해 벽을 오르고 상대를 이기세요. 먼저 정상에 오르면 승리 — 탭을 놓치지 마세요!',
  'onboarding.name_title': '이름 정하기',
  'onboarding.name_body': '다른 플레이어에게 보이는 이름입니다. 첫 변경은 무료입니다.',
  'onboarding.shout_title': '승리 외침 설정',
  'onboarding.shout_body': '승리하면 상대에게 표시됩니다. 인상적으로 만들어 보세요! 건너뛰고 나중에 설정할 수도 있습니다.',
  'onboarding.next': '다음',
  'onboarding.finish': '플레이 시작',
  'onboarding.skip': '나중에 하기',
  'common.home': '홈',
  'common.back_home': '홈으로 돌아가기',
  'common.dismiss': '닫기',

  // Home
  'home.quick_match': '⚡ 빠른 매치',
  'home.daily_ad': '데일리 보너스',
  'quests.title': '일일 퀘스트',
  'quests.button': '일일 퀘스트',
  'quests.reset_note': '매일 초기화',
  'quests.share': 'SNS에 공유',
  'quests.play': '5게임 플레이',
  'quests.win': '10게임 승리',
  'quests.claim': '받기',
  'quests.claimed': '받음',
  'quests.share_btn': '공유',
  'quests.share_text': "🧗‍♂️ 'Don't Miss!'에서 랭킹 올리는 중! 초고속 1v1 색깔 탭 대결 — 맞는 색을 탭해 벽을 오르고 상대를 제압! 내 기록 못 깰걸? 🔴🔵🟡🟣 나랑 대결해요 👇",
  'home.daily_ad_watching': '광고 로딩 중…',
  'home.create_room': '+ 방 만들기',
  'home.join_room': '↪ 코드로 참여',
  'home.modes_subtitle': '⚡ 솔로 1v1 · 듀오 · 팀 · 토너먼트 곧 출시',
  'home.avg_wait': '평균 대기 4초',
  'home.could_not_create_room': '방을 만들 수 없습니다',
  'home.code_too_short': '방 코드가 너무 짧습니다',
  'home.could_not_join': '방에 참여할 수 없습니다',
  'home.join_room_title': '방 참여',
  'home.equipped_skills': '장착 스킬',
  'home.buy_more': '더 구매 ›',
  'home.skill_buy_cta': '구매',
  'home.open_profile': '프로필 열기',
  'home.role_climber': '클라이머',
  'home.win_streak': '연승',
  'home.join_btn': '참여',
  'home.display_name': '닉네임',
  'home.display_name_ph': '게임 내 이름',
  'home.victory_shout': '승리 외침',
  'home.victory_shout_ph': '예: GG! · 쉽네요 💪',
  'home.first_change_free': '첫 변경 무료',
  'home.field_none': '— 없음 —',
  'home.not_enough_gems': '젬이 부족합니다',
  'home.max_chars': '최대 {n}자',
  'profanity.blocked': '사용할 수 없는 단어입니다. 다른 단어를 선택하세요.',
  'home.save_cost': '저장 · {cost}💎',

  // Room
  'room.leave': '← 나가기',
  'room.chat': '◐ 채팅',
  'room.code_label': '방 코드',
  'room.tap_to_copy': '탭하여 복사',
  'room.copied': '✓ 복사됨',
  'room.team_a': '팀 A',
  'room.team_b': '팀 B',
  'room.join_slot': '참여',
  'room.you_suffix': '(나)',
  'room.ready_label': '준비',
  'room.tap_to_ready': '탭하여 준비',
  'room.invite_friend': '+ 친구 초대',
  'room.share_to_chat': '📢 채팅에 공유',
  'room.shared_to_chat': '✓ 공유됨',
  'room.open_chat_tip': '팀 채팅 열기',
  'room.chat_title': '팀 채팅',
  'room.chat_placeholder': '팀에게 메시지…',
  'room.chat_empty': '아직 메시지가 없습니다. 팀에게 인사하세요!',
  'room.chat_send': '보내기',
  'room.chat_expand': '채팅 확대',
  'room.chat_collapse': '축소',
  'room.kick_tip': '강퇴',
  'room.waiting_host': '호스트의 시작을 기다리는 중…',
  'room.invite_modal_title': '친구 초대',
  'room.no_friends_to_invite': '초대할 친구가 없습니다. 채팅 → 월드에서 아바타 탭.',
  'room.in_room': '방에 있음',
  'room.invited': '✓ 초대됨',
  'room.invite_btn': '초대',
  'room.offline_send_hint': '친구가 오프라인입니다 — 받을 수 없음',
  'room.send_invite': '초대 보내기',
  'room.start': '▶ 시작',
  'room.start_quick': '▶ 시작 · 빠른',
  'room.start_duel': '▶ 시작 · 듀얼',
  'room.start_team': '▶ 시작 · 팀',
  'room.mode.empty_desc': '슬롯에 친구를 초대하여 시작',
  'room.mode.quick': '빠른 매치',
  'room.mode.quick_desc': '시작하면 랜덤 솔로 매치 대기열에 들어갑니다',
  'room.mode.solo_duel': '솔로 듀얼 · 1v1',
  'room.mode.solo_duel_desc': '두 진영 간 직접 대결 — 매치메이킹 생략',
  'room.mode.duo_rank': '듀오 랭크 · 2v2',
  'room.mode.duo_rank_desc': '파트너와 함께 랜덤 2명과 대결',
  'room.mode.team_duel': '팀 듀얼 · 2v2',
  'room.mode.team_duel_desc': '두 팀 간 직접 2v2 대결',
  'room.team_mode_label': '팀 모드',
  'room.tmode.alternating': '교대',
  'room.tmode.alternating_desc': '5 홀드마다 팀원 교대. 팀 콤보로 스킬 해제.',
  'room.tmode.split_color': '색상 분담',
  'room.tmode.split_color_desc': '각자 2색씩 담당. 콤보 5로 양 스킬 해제.',
  'room.tmode.attack_defense': '공격 · 수비',
  'room.tmode.attack_defense_desc': '한 명은 등반, 한 명은 역할별 스킬 사용.',
  'room.mode.imbalanced': '불균형',
  'room.mode.imbalanced_desc': '양쪽을 균형있게 맞추세요 (1v1 또는 2v2)',

  // Team 2v2 in-match status
  'match.team.your_turn': '내 차례 — 벽을 탭',
  'match.team.teammate_turn': '팀원 차례',
  'match.team.you_climb': '내가 등반 — 벽을 탭',
  'match.team.you_cast': '내가 지원 — 스킬 사용',

  // Match
  'match.you_win': '승리!',
  'match.you_lose': '패배',
  'match.summary': '▶ 요약',
  'match.tip_label': '팁',
  'match.auto_continue': '{secs}초 후 자동 계속',
  'match.tap_color': '{color} 탭',
  'match.quit_aria': '매치 종료',
  'match.combo': '×{n} 콤보',
  'match.you': '나',
  'match.rival': '상대',

  // Result
  'result.match_result': '매치 결과',
  'result.victory': '승리!',
  'result.defeat': '패배',
  'result.rewards': '보상',
  'result.x2_button': '보상 2배',
  'result.x2_watching': '광고 로딩 중…',
  'result.x2_done': '보상 2배 획득!',
  'result.battle_stats': '전투 통계',
  'result.rematch': '▶ 재대결',
  'result.home': '홈',
  'result.back_to_room': '▶ 방으로 돌아가기',
  'result.back_to_war': '⚔️ 클랜전으로',
  'result.leave_room': '방 나가기',
  'result.opponent_shouts': '상대 외침',
  'result.coins': '골드',
  'result.stat_speed': '속도',
  'result.stat_accuracy': '정확도',
  'result.stat_combo_max': '최고 콤보',

  // Chat
  'chat.global': '월드',
  'chat.clan': '클랜',
  'chat.placeholder_clan': '클랜에 메시지…',
  'chat.dms': 'DM',
  'chat.no_messages': '아직 메시지가 없습니다 — 첫 메시지를 보내세요!',
  'chat.no_dms': '아직 메시지가 없습니다. 월드 채팅에서 아바타를 탭해 친구 추가 또는 DM.',
  'chat.copy_tip': '더블탭하여 복사',
  'chat.copied': '복사됨!',
  'chat.copy_hint': '💡 메시지를 더블탭하여 복사',
  'chat.open_chat': '채팅 열기',
  'chat.back_to_conversations': '대화로 돌아가기',
  'chat.share_room_btn': '📍 내 방 {code} 공유',
  'chat.share_room_msg': '내 방에 참여하세요! 탭하여 참여 👉 [{code}]',
  'chat.join_room': '▶ 방 참여',
  'chat.open_room': '▶ 방 열기',
  'chat.placeholder_global': '모두와 채팅…',
  'chat.placeholder_dm': '{name}에게 메시지…',
  'chat.minimize': '최소화',
  'chat.close': '닫기',
  'chat.add_friend': '+ 친구 추가',
  'chat.username_placeholder': '플레이어 이름',
  'chat.btn_add': '추가',
  'chat.section.room_invites': '방 초대 · {n}',
  'chat.section.friend_requests': '친구 요청 · {n}',
  'chat.section.conversations': '대화',
  'chat.invite.tagline': '방 {code}에 초대했습니다',
  'chat.invite.join': '▶ 참여',
  'chat.invite.decline': '거절',
  'chat.request.tagline': '친구가 되고 싶어합니다',
  'chat.request.accept': '요청 수락',
  'chat.request.decline': '요청 거절',
  'chat.open_dm': 'DM 열기',
  'chat.view_profile_tip': '프로필 · 친구 추가 · DM',
  'chat.manage_friend_tip': '프로필 / 친구 관리',
  'chat.you_prefix': '나: {text}',
  'chat.now_friends': '이제 친구가 되었습니다!',
  'chat.request_sent': '친구 요청을 보냈습니다',
  'chat.send_failed': '요청 전송 실패',

  // User quick menu
  'qm.profile_load_failed': '프로필을 불러올 수 없습니다',
  'qm.rank': '랭크',
  'qm.mmr': 'MMR',
  'qm.matches': '경기',
  'qm.win_rate': '승률',
  'qm.add_friend': '+ 친구 추가',
  'qm.request_sent': '⏳ 요청 전송됨',
  'qm.accept_request': '✓ 요청 수락',
  'qm.friends_unfriend': '✓ 친구 · 해제',
  'qm.send_dm': '💬 DM 보내기',
  'qm.invite_to_room': '🎮 방 {code}에 초대',
  'qm.invited_to_room': '✓ {code}에 초대됨',
  'qm.invite_failed': '초대 실패',
  'qm.invited_toast': '방 {code}에 초대함',
  'qm.unfriended': '친구 해제됨',
  'qm.accepted': '수락함',

  // Shop / Skills
  'shop.skills.section': '스킬 — 소모품',
  'shop.skills.loading': '스킬 로딩 중…',
  'shop.skills.owned': '보유',
  'shop.skills.unlocks_at': '🔒 Lv{level}에서 해제 (나 Lv{my})',
  'shop.skills.bought_today': '오늘 구매: {n}/{max}',
  'shop.skills.ad_today': '광고: {n}/{max}',
  'shop.skills.watching': '시청 중…',
  'shop.skills.purchase_failed': '구매 실패',
  'shop.skills.ad_failed': '광고 보상 실패',
  'shop.skills.added': '+1 {name}',
  'shop.skills.added_ad': '+1 {name} (광고)',
  'shop.disrupt.desc': '상대의 다음 3 홀드 색을 뒤섞습니다.',
  'shop.shield.desc': '1초 실드 — 디스럽트 차단 + 잘못된 탭 무시.',

  // TabBar
  'tab.shop': '상점',
  'tab.ranks': '랭킹',
  'tab.play': '플레이',
  'tab.bag': '가방',
  'tab.characters': '영웅',
  'tab.settings': '설정',

  // Login
  'login.title': "DON'T MISS",
  'login.subtitle': '로그인하고 클라이밍 시작',
  'login.guest': '게스트로 플레이',
  'login.email': '이메일',
  'login.password': '비밀번호',
  'login.username': '사용자 이름',
  'login.signin': '로그인',
  'login.signup': '계정 만들기',
  'login.switch_to_signup': '계정이 없으신가요? 만들기',
  'login.switch_to_signin': '이미 계정이 있나요? 로그인',
  'login.continue_google': 'Google로 계속',
  'login.continue_apple': 'Apple로 로그인',
  'login.or': '또는',
  'login.error_invalid': '잘못된 자격증명',
  'login.error_taken': '사용자 이름 또는 이메일이 이미 사용 중',
  'login.error_generic': '로그인 실패',
  'login.oauth_not_configured': '{provider} 로그인 미설정 — 이메일 또는 게스트로 시도하세요.',
  'login.server_unreachable': '서버 연결 실패 — 연결을 확인하세요.',
  'login.tagline': '스피드 클라이밍 대결. 4가지 색.',
  'login.tagline_winner': '승자는 한 명.',
  'login.continue_email': '이메일로 계속',
  'login.creating_guest': '게스트 생성 중…',
  'login.play_guest': '게스트로 플레이',
  'login.no_rewards': '보상 없음',
  'login.terms_prefix': '계속하면 다음에 동의합니다:',
  'login.terms': '약관',
  'login.privacy': '개인정보',
  'login.not_configured': '미설정',
  'login.email_or_username': '이메일 또는 사용자 이름',
  'auth.welcome_back': '돌아오신 것을 환영합니다, 클라이머.',
  'auth.pick_name': '이름을 정하고 클라이밍 시작.',

  // Leaderboard
  'leaderboard.title': '리더보드',
  'leaderboard.tab_mmr': 'MMR',
  'leaderboard.tab_time': '최고 기록',
  'leaderboard.tab_tiers': '티어',
  'leaderboard.mode_solo': '솔로 1V1',
  'leaderboard.mode_duo': '듀오 2V2',
  'leaderboard.your_position': '내 순위 · #{n}',
  'leaderboard.unranked': '미배치',
  'leaderboard.empty': '아직 기록이 없습니다 — 첫 번째가 되세요!',
  'leaderboard.tab_byrank': '랭크별',
  'leaderboard.empty_time': '아직 완주 승리가 없습니다. 첫 기록을 세우세요!',
  'leaderboard.empty_players': '아직 플레이어가 없습니다.',
  'leaderboard.you': '나',
  'leaderboard.unranked_yet': '— 아직 미배치',
  'leaderboard.top': '상위 {n}',
  'leaderboard.tier_empty': '아직 이 티어에 도달한 플레이어가 없습니다.',

  // Matchmaking
  'mm.searching_tag': '◌ 검색 중',
  'mm.found_tag': '◉ 상대 발견',
  'mm.ready_tag': '⚡ 준비',
  'mm.searching_title': '실력 있는 상대를 찾는 중...',
  'mm.found_title': '듀얼 도착',
  'mm.ready_title': '전투 준비',
  'mm.offline': '오프라인 · AI 상대',
  'mm.stage': '스테이지',
  'mm.stage_name': '크로마 어센트 월',
  'mm.stage_desc': '15.0m · 크로마 코스 · 4색 시퀀스',
  'mm.cancel': '× 검색 취소',
  'mm.scanning': '스캔 중…',

  // Rank-up modal
  'rankup.promotion': '★ 승급 ★',
  'rankup.demotion': '▼ 강등 ▼',
  'rankup.rank_up': '랭크 업!',
  'rankup.demoted': '강등됨',
  'rankup.tap_continue': '탭하여 계속',

  // Shop
  'shop.title': '상점',
  'shop.trial': '체험',
  'shop.trial_started': '{name} {n}경기 체험',
  'shop.subtitle': '의상, 모자, 망토, 자랑 아이템. 현명하게 — 젬은 귀합니다.',
  'shop.empty': '아직 아무것도 없습니다.',
  'shop.loading': '로딩 중…',
  'shop.owned_tag': '보유 중',
  'shop.buy_coins': '구매 · {price}',
  'shop.buy_gems': '구매 · {price} 💎',
  'shop.not_enough': '부족',
  'shop.purchased': '구매 완료!',
  'shop.failed': '구매 실패',
  'shop.cat_heroes': '영웅',
  'shop.cat_skills': '스킬',
  'shop.cat_holds': '홀드',
  'shop.cat_frames': '프레임',
  'shop.acquired': '획득: {name}',
  'shop.frames_hint': '아바타 프레임 — 사진 주변에 스타일을 자랑.',
  'shop.no_frames': '아직 프레임이 없습니다.',
  'shop.claim': '획득',
  'chars.shop_section': '캐릭터 — 영웅',
  'chars.loading': '영웅 로딩 중…',
  'chars.unlocked': '{name} 해제!',
  'chars.purchase_failed': '구매 실패',
  'chars.owned_equip': '✓ 보유 · 가방에서 장착',
  'chars.win_power': '승리 파워 ×{power}',
  'chars.free_starter': '무료 스타터',
  'chars.bag_empty': '아직 영웅이 없습니다 — 상점에서 구매.',
  'chars.equipped_toast': '{name} 장착',
  'chars.equip_failed': '장착 실패',

  // Hold skins
  'holds.shop_section': '홀드 스킨 — 벽 그립',
  'holds.bag_section': '홀드 스킨 — 벽 그립',
  'holds.loading': '홀드 스킨 로딩 중…',
  'holds.unlocked': '{name} 해제!',
  'holds.purchase_failed': '구매 실패',
  'holds.owned_equip': '✓ 보유 · 가방에서 장착',
  'holds.free_default': '무료 · 기본 로테이션',
  'holds.bag_empty': '아직 홀드 스킨이 없습니다 — 상점에서 구매.',
  'holds.equip': '장착',
  'holds.use_random': '랜덤 사용',
  'holds.equipped': '✓ 장착됨',
  'holds.equipped_toast': '{name} 장착',
  'holds.unequipped_toast': '랜덤 로테이션으로 돌아감',
  'holds.equip_failed': '장착 실패',
  'holds.random_hint': '스킨 미장착 — 매 경기마다 랜덤 기본 형태가 선택됩니다.',

  // Inventory
  'inv.title': '인벤토리',
  'inv.subtitle': '보유 아이템을 장착. 우측 미리보기에 클라이머 표시.',
  'inv.empty': '이 슬롯에 아이템이 없습니다',
  'inv.equipped': '장착 중',
  'inv.equip': '장착',
  'inv.trial_left': '{n}회 남음',
  'inv.unequip': '해제',
  'inv.tap_to_equip': '탭하여 장착. 슬롯당 하나.',
  'inv.empty_all': '인벤토리가 비어 있습니다.',
  'inv.equipped_toast': '장착: {name}',
  'inv.unequipped_toast': '{slot} 해제됨',
  'inv.skills_section': '스킬 · 소모품',
  'inv.buy_more': '더 구매 ›',
  'inv.skill_locked': '🔒 Lv{level}에서 해제 (나 Lv{my})',
  'inv.skill_empty': '잔량 없음 — 탭하여 구매',
  'inv.skill_ready': '매치에서 사용 가능',
  'inv.owned_label': '보유',

  // Friends
  'friends.title': '친구',
  'friends.tab_friends': '친구',
  'friends.tab_global': '월드 채팅',
  'friends.add_placeholder': '사용자 이름으로 추가…',
  'friends.add_btn': '추가',
  'friends.friend_added': '친구 추가 ✓',
  'friends.request_sent': '요청 전송됨',
  'friends.incoming_requests': '받은 요청 · {n}',
  'friends.accept': '수락',
  'friends.decline': '거절',
  'friends.pending_sent': '대기 중 · {n}',
  'friends.cancel': '취소',
  'friends.online_count': '온라인 · {n}',
  'friends.none_online': '현재 온라인인 친구가 없습니다.',
  'friends.offline_count': '오프라인 · {n}',
  'friends.no_friends': '아직 친구가 없습니다 — 위에서 검색하여 추가.',
  'friends.rank_mmr': '{rank} · {mmr} MMR',
  'friends.no_messages': '아직 메시지가 없습니다. 인사해보세요 👋',
  'friends.say_something': '뭔가 말해보세요…',
  'friends.send': '전송',
  'friends.start_conversation': '대화를 시작하세요 💬',
  'friends.message_placeholder': '{name}에게 메시지…',

  // Boss Break
  'boss.mode_label': '솔로 PVE',
  'boss.title': '보스 브레이크',
  'boss.subtitle': '퍼펙트 탭으로 득점 · 짧은 보스 러시 생존',
  'boss.loading': '보스 소환 중…',
  'boss.disabled_title': '아직 열리지 않은 모드',
  'boss.disabled_body': '관리자 페이지에서 보스 브레이크를 활성화할 수 있습니다.',
  'boss.load_error': '보스 브레이크를 불러올 수 없습니다. 다시 시도해 주세요.',
  'boss.world_boss': '월드 보스',
  'boss.target': '목표',
  'boss.stone_king': '스톤 킹',
  'boss.stone_king_description': '그립이 바닥나기 전에 원소 방어구를 파괴하세요.',
  'boss.frost_empress': '서리 여제',
  'boss.frost_empress_description': '안개가 모든 홀드를 얼리기 전에 얼음 왕관을 부수세요.',
  'boss.storm_roc': '폭풍 로크',
  'boss.storm_roc_description': '번개 속 리듬을 지키며 하늘의 군주를 기절시키세요.',
  'boss.abyss_kraken': '심연 크라켄',
  'boss.abyss_kraken_description': '수압이 그립을 짓누르기 전에 촉수를 끊으세요.',
  'boss.verdant_colossus': '녹음의 거신',
  'boss.verdant_colossus_description': '재생 핵을 태우고 되살아나는 고목을 부수세요.',
  'boss.inferno_wyrm': '지옥 화룡',
  'boss.inferno_wyrm_description': '화염을 뚫고 용암 비늘과 용의 숨결을 잠재우세요.',
  'boss.eclipse_sovereign': '일식 군주',
  'boss.eclipse_sovereign_description': '일식 주기를 버티고 궁극의 공허 핵을 파괴하세요.',
  'boss.weekly_hunt': '7일 하드 보스 사냥',
  'boss.legendary_frame': '전설 프레임',
  'boss.frame_reward': '보스 프레임',
  'boss.frame_owned': '프레임 보유 중',
  'boss.frame_requirement': '하드 · PERFECT ≥{rate}%',
  'boss.frame_need_perfect': '프레임에 PERFECT ≥{rate}% 필요',
  'boss.weekly_bonus': '주간 마일스톤',
  'boss.competition.title': '퍼펙트 점수 랭킹',
  'boss.competition.attempts': '{remaining}/{total}회 남음',
  'boss.competition.daily': '일일 점수',
  'boss.competition.weekly': '주간 포인트',
  'boss.competition.damage': '점수',
  'boss.competition.points': '점',
  'boss.competition.my_rank': '순위 #{rank}',
  'boss.competition.closes': '주간 마감 {time}',
  'boss.competition.closed': '주간 정산 중',
  'boss.competition.empty': '아직 기록이 없습니다. 첫 1위를 차지하세요.',
  'boss.competition.open': '순위표 보기',
  'boss.competition.close': '닫기',
  'boss.competition.rank': '순위',
  'boss.competition.attempts_label': '남은 도전',
  'boss.wins': '{n}승',
  'boss.starting': '생성 중…',
  'boss.difficulty.normal': '일반',
  'boss.difficulty.hard': '어려움',
  'boss.unlock_hard': '🔒 일반 난이도 승리',
  'boss.daily_note': '첫 클리어 +{gems} 💎 · 하루 {limit}회 골드 보상',
  'boss.phase': '페이즈 {phase} · {difficulty}',
  'boss.hp': '{name} · {remaining}/{total}',
  'boss.stunned_break': '기절 · 버스트 ×2',
  'boss.break_meter': '브레이크',
  'boss.combo': '{n} 콤보',
  'boss.score_combo': '{score}점 · {n} 콤보',
  'boss.vulnerable': '약화 {seconds}초',
  'boss.burst_window': '버스트 타임 ×2',
  'boss.destroy_core': '원소 코어 파괴',
  'boss.attack.slam': '⚠ 대지 강타',
  'boss.attack.shards': '⚠ 바위 파편 폭우',
  'boss.attack.beam': '⚠ 4색 코어 광선',
  'boss.current_color_aria': '현재 색상: {color}',
  'boss.color.red': '빨강',
  'boss.color.blue': '파랑',
  'boss.color.yellow': '노랑',
  'boss.color.purple': '보라',
  'boss.now': '현재',
  'boss.timing.zone': '히트 존',
  'boss.timing.hit': '퍼펙트!',
  'boss.timing.early': '너무 빠름',
  'boss.timing.late': '너무 늦음',
  'boss.timing.wrong': '색상 오류',
  'boss.grip': '그립 · {current}/{max}',
  'boss.skill.disrupt': '브레이크',
  'boss.skill.shield': '실드',
  'boss.tap_color_aria': '{color} 탭',
  'boss.go': '시작!',
  'boss.countdown_hint': '코어가 히트 존에 들어오면 같은 색 탭 · Q 방어구 파괴 · E 공격 방어',
  'boss.verifying': '확인 중…',
  'boss.defeated': '보스 처치',
  'boss.run_complete': '시간 종료 · 완료',
  'boss.run_failed': '도전 실패',
  'boss.finish': '처치 완료!',
  'boss.score_recorded': '점수 기록 완료!',
  'boss.grip_empty': '그립 소진!',
  'boss.stat.score': '점수',
  'boss.stat.perfect': '퍼펙트',
  'boss.stat.damage': '대미지',
  'boss.stat.combo': '콤보',
  'boss.stat.time': '시간',
  'boss.frame_unlocked': '프레임 잠금 해제',
  'boss.daily_cap': '일일 한도 도달',
  'boss.continue': '계속',
  'boss.home': '로비로',
  'boss.blocked': '방어 성공!',
  'boss.interrupted': '스킬 차단!',
  'boss.grip_damage': '-{damage} 그립',

  // Settings
  'settings.title': '설정',
  'settings.language': '언어',
  'settings.audio': '오디오',
  'settings.sfx': 'SFX',
  'settings.music': '음악',
  'settings.haptic': '햅틱 피드백',
  'settings.gameplay': '게임플레이',
  'settings.lefty': '왼손잡이 레이아웃',
  'settings.colorblind': '색맹 팔레트',
  'settings.tap_zone': '탭 영역 크기',
  'settings.avatar': '아바타',
  'settings.account': '계정',
  'settings.guest_id': '게스트 ID',
  'settings.username': '사용자 이름',
  'settings.email': '이메일',
  'settings.region': '지역',
  'settings.version': '버전',
  'settings.danger_zone': '위험 구역',
  'settings.danger_guest': '로그아웃하여 다른 계정으로 전환 또는 새로 만들기.',
  'settings.danger_user': '이 기기에서 로그아웃.',
  'settings.sign_out': '로그아웃',
  'settings.confirm_signout': '로그아웃하시겠습니까?',
  'settings.delete_account': '계정 삭제',
  'settings.confirm_delete_account':
    '계정을 삭제하시겠습니까? 프로필, 진행 상황, 구매 내역이 영구적으로 삭제되며 복구할 수 없습니다.',
  'settings.confirm_delete_account_final': '이 작업은 되돌릴 수 없습니다. 확인을 누르면 계정이 영구 삭제됩니다.',
  'settings.delete_account_failed': '계정을 삭제할 수 없습니다. 다시 시도해 주세요.',
  'settings.avatar_custom': '사용자 사진',
  'settings.avatar_character': '캐릭터 아바타',
  'settings.avatar_default': '기본 아바타',
  'settings.avatar_formats': 'PNG / JPG / WebP · 최대 3 MB',
  'settings.avatar_change': '변경',
  'settings.avatar_upload': '사진 업로드',
  'settings.avatar_remove': '제거',

  // Toasts
  'toast.dm_new': '새 DM',
  'toast.friend_request': '👋 친구 요청',
  'toast.friend_request_body': '{name}이(가) 친구가 되고 싶어합니다',
  'toast.now_friends': '✓ 이제 친구',
  'toast.now_friends_body': '{name}이(가) 요청을 수락',
  'toast.room_invite': '🎮 {name}이(가) 초대',
  'toast.room_invite_body': '탭하여 방 {code}에 참여',
  'toast.could_not_join': '방에 참여할 수 없습니다',
  'toast.could_not_join_body': '초대가 만료되었거나 방이 가득 찼습니다',

  // ── Clan / Clan War (2.0) ──
  'clan.title': '클랜',
  'clan.ranks': '🏆 순위',
  'clan.ranks_title': '클랜 순위',
  'clan.find': '클랜 찾기',
  'clan.create': '생성',
  'clan.search_ph': '이름 또는 태그 검색…',
  'clan.search': '검색',
  'clan.none_found': '클랜이 없습니다. 첫 클랜을 만들어 보세요!',
  'clan.full': '가득 참',
  'clan.join': '가입',
  'clan.request': '신청',
  'clan.joined': '가입했습니다!',
  'clan.welcome': '{name} 에 오신 것을 환영합니다',
  'clan.request_sent': '신청을 보냈습니다',
  'clan.request_waiting': '{name} 의 승인 대기 중',
  'clan.name_label': '클랜 이름 (3–20)',
  'clan.tag_label': '태그 (2–6, A–Z/0–9)',
  'clan.desc_label': '소개',
  'clan.emblem_label': '엠블럼',
  'clan.emblem_shop': '프리미엄 로고',
  'clan.emblem_equip': '장착',
  'clan.emblem_equipped': '장착됨',
  'clan.emblem_bought': '로고 잠금 해제!',
  'clan.emblem_reward': '시즌 보상',
  'clan.policy_label': '가입 방식',
  'clan.policy_open': '공개 — 누구나 가입',
  'clan.policy_request': '승인제 — 멤버 승인',
  'clan.policy_invite': '초대 전용',
  'clan.policy_short_open': '공개',
  'clan.policy_short_request': '승인제',
  'clan.policy_short_invite': '초대 전용',
  'clan.create_btn': '클랜 생성',
  'clan.creating': '생성 중…',
  'clan.create_note': '코인 비용 + 최소 레벨이 필요할 수 있습니다.',
  'clan.req_title': '생성 조건',
  'clan.req_level': '레벨 {n} 이상',
  'clan.req_coins': '{n} 코인',
  'clan.req_gems': '{n} 젬',
  'clan.created': '클랜을 생성했습니다!',
  'clan.role.leader': '클랜장',
  'clan.role.coLeader': '부클랜장',
  'clan.role.elder': '장로',
  'clan.role.member': '멤버',
  'clan.requests_title': '가입 신청 ({n})',
  'clan.members_title': '멤버',
  'clan.leave': '클랜 탈퇴',
  'clan.disband': '해체',
  'clan.you': '(나)',
  'clan.confirm_leave': '이 클랜을 탈퇴할까요?',
  'clan.confirm_leave_leader': '탈퇴하고 다음 간부에게 클랜장을 넘길까요?',
  'clan.confirm_disband': '{name} 을(를) 해체할까요? 되돌릴 수 없습니다.',
  'clan.confirm_kick': '{name} 을(를) 추방할까요?',
  'clan.confirm_make_leader': '{name} 을(를) 클랜장으로 임명할까요?',
  'clan.left': '클랜을 탈퇴했습니다',
  'clan.disbanded': '클랜이 해체되었습니다',
  'clan.toast_title': '클랜',
  'clan.err_generic': '오류가 발생했습니다',
  'clan.err_profanity': '이름, 태그 또는 설명에 금지어가 있어요',
  'clan.now': '곧',
  'clan.notif.inbox_title': '클랜 소식',
  'clan.notif.empty': '클랜 소식이 아직 없어요.',
  'clan.notif.join_request': '{name} 님이 {clan} 가입을 신청했어요',
  'clan.notif.join_approved': '{clan} 에 가입했어요!',
  'clan.notif.join_rejected': '{clan} 가입 신청이 거절되었어요',
  'clan.notif.kicked': '{clan} 에서 추방되었어요',
  'clan.notif.role_changed': '{clan} 에서의 직책이 {role} (으)로 바뀌었어요',
  'clan.notif.war_scheduled': '클랜전이 잡혔어요 — 준비하세요!',
  'clan.notif.war_open': '클랜전 시작 — 공격하세요!',
  'clan.notif.war_ended': '클랜전 종료 — {result}',
  'clan.notif.emblem_awarded': '{rank}위! 이번 주 {name} 로고 획득 🏆',
  'clan.notif.result_win': '승리 🏆',
  'clan.notif.result_lose': '패배',
  'clan.notif.result_draw': '무승부',
  'clan.war.title': '클랜 전쟁',
  'clan.war.daily_at': '매일 {t}',
  'clan.war.registered': '오늘 등록 완료',
  'clan.war.registered_ok': '클랜이 오늘 전쟁에 등록되었어요!',
  'clan.war.battle_at': '{t} 전투 시작',
  'clan.war.register_btn': '오늘 전쟁 등록',
  'clan.war.need_members': '멤버가 최소 {n}명 필요해요',
  'clan.war.await_leader': '클랜장 등록 대기 중',
  'clan.war.reg_opens': '{t} 등록 시작',
  'clan.war.prep': '준비 · {t} 후 시작',
  'clan.war.battle': '전투 중 · {t} 남음',
  'clan.war.vs': 'vs',
  'clan.war.find': '전쟁 매치 찾기 ({n} 남음)',
  'clan.war.no_attacks': '남은 횟수 없음',
  'clan.war.not_open': '전투 미시작',
  'clan.war.prep_note': '로스터 준비 — 곧 전투 시작. 각 {n} 경기.',
  'clan.war.top': '기여 순위',
  'clan.war.stat': '{pt}점 · {wins}승',
  'clan.war.title_toast': '클랜 전쟁',
  'clan.season': '시즌 {n}',
  'clan.season_left': '{t} 남음',
  'clan.season_ending': '곧 종료',
  'clan.lb_empty': '순위에 오른 클랜이 없습니다.',
};

// ── Thai ─────────────────────────────────────────────────────────────
const th: Partial<Record<I18nKey, string>> = {
  'common.cancel': 'ยกเลิก',
  'common.close': 'ปิด',
  'common.preview': 'ดูตัวอย่าง',
  'common.confirm': 'ยืนยัน',
  'common.back': 'กลับ',
  'common.loading': 'กำลังโหลด…',
  'common.error': 'ข้อผิดพลาด',
  'common.ok': 'ตกลง',
  'common.online': 'ออนไลน์',
  'common.offline': 'ออฟไลน์',
  'common.send': 'ส่ง',
  'common.edit': 'แก้ไข',
  'common.save': 'บันทึก',
  'common.network_error': 'ข้อผิดพลาดเครือข่าย',
  'chars.idle_preview': 'ตัวอย่างท่ายืน + ชนะ',
  'shop.finisher_preview': 'ตัวอย่างท่าปิดฉาก',
  'playgate.title': 'เล่นในแอป',
  'playgate.body': 'โหมดต่อสู้เล่นได้เฉพาะในแอป Don’t Miss ดาวน์โหลดฟรีเพื่อเล่น',
  'playgate.ios': 'ดาวน์โหลดบน App Store',
  'playgate.android': 'รับบน Google Play',
  'update.hard_title': 'ต้องอัปเดต',
  'update.hard_body': 'ต้องอัปเดตเป็นเวอร์ชันใหม่ของ Don’t Miss เพื่อเล่นต่อ กรุณาอัปเดต',
  'update.soft_title': 'มีอัปเดต',
  'update.soft_body': 'มีเวอร์ชันใหม่ของ Don’t Miss พร้อมการปรับปรุงและแก้ไขข้อบกพร่อง',
  'update.cta': 'อัปเดตตอนนี้',
  'update.later': 'ภายหลัง',
  'app.announcement_title': 'ประกาศ',
  'app.maintenance_title': 'กำลังปิดปรับปรุง',
  'app.maintenance_default': 'แอปกำลังปิดปรับปรุง โปรดลองอีกครั้งในภายหลัง',
  'onboarding.welcome_title': 'ยินดีต้อนรับสู่ Don’t Miss!',
  'onboarding.welcome_body': 'แตะสีที่ตรงกันเพื่อปีนกำแพงและเอาชนะคู่แข่ง ใครถึงยอดก่อนชนะ — อย่าแตะพลาด!',
  'onboarding.name_title': 'ตั้งชื่อของคุณ',
  'onboarding.name_body': 'นี่คือชื่อที่ผู้เล่นอื่นเห็น เปลี่ยนครั้งแรกฟรี',
  'onboarding.shout_title': 'ตั้งคำประกาศชัยชนะ',
  'onboarding.shout_body': 'คู่แข่งจะเห็นข้อความนี้เมื่อคุณชนะ ทำให้น่าจดจำ! ข้ามไปตั้งภายหลังได้',
  'onboarding.next': 'ถัดไป',
  'onboarding.finish': 'เริ่มเล่น',
  'onboarding.skip': 'ข้ามก่อน',
  'common.home': 'หน้าหลัก',
  'common.back_home': 'กลับหน้าหลัก',
  'common.dismiss': 'ปิด',

  'home.quick_match': '⚡ จับคู่ด่วน',
  'home.daily_ad': 'โบนัสรายวัน',
  'quests.title': 'เควสต์รายวัน',
  'quests.button': 'เควสต์รายวัน',
  'quests.reset_note': 'รีเซ็ตทุกวัน',
  'quests.share': 'แชร์ลงโซเชียล',
  'quests.play': 'เล่น 5 แมตช์',
  'quests.win': 'ชนะ 10 แมตช์',
  'quests.claim': 'รับ',
  'quests.claimed': 'รับแล้ว',
  'quests.share_btn': 'แชร์',
  'quests.share_text': "🧗‍♂️ กำลังไต่อันดับใน Don't Miss! ดวล 1v1 แตะสีสุดเร็ว — แตะสีให้ถูก ปีนกำแพง ล้มคู่แข่ง! ท้าว่าทำเวลาสู้ฉันไม่ได้หรอก 🔴🔵🟡🟣 มาดวลกัน 👇",
  'home.daily_ad_watching': 'กำลังโหลดโฆษณา…',
  'home.create_room': '+ สร้างห้อง',
  'home.join_room': '↪ เข้าร่วมด้วยรหัส',
  'home.modes_subtitle': '⚡ โซโล่ 1v1 · ดูโอ · ทีม · ทัวร์นาเมนต์เร็วๆ นี้',
  'home.avg_wait': 'รอเฉลี่ย 4 วินาที',
  'home.could_not_create_room': 'ไม่สามารถสร้างห้องได้',
  'home.code_too_short': 'รหัสห้องสั้นเกินไป',
  'home.could_not_join': 'ไม่สามารถเข้าร่วมห้องได้',
  'home.join_room_title': 'เข้าร่วมห้อง',
  'home.equipped_skills': 'สกิลที่ติดตัว',
  'home.buy_more': 'ซื้อเพิ่ม ›',
  'home.skill_buy_cta': 'ซื้อ',
  'home.open_profile': 'เปิดโปรไฟล์',
  'home.role_climber': 'นักปีน',
  'home.win_streak': 'ชนะต่อเนื่อง',
  'home.join_btn': 'เข้าร่วม',
  'home.display_name': 'ชื่อที่แสดง',
  'home.display_name_ph': 'ชื่อในเกมของคุณ',
  'home.victory_shout': 'คำพูดชัยชนะ',
  'home.victory_shout_ph': 'เช่น GG! · ง่ายมาก 💪',
  'home.first_change_free': 'เปลี่ยนครั้งแรกฟรี',
  'home.field_none': '— ไม่มี —',
  'home.not_enough_gems': 'เพชรไม่พอ',
  'home.max_chars': 'สูงสุด {n} ตัวอักษร',
  'profanity.blocked': 'ไม่อนุญาตให้ใช้คำนี้ กรุณาเลือกคำอื่น',
  'home.save_cost': 'บันทึก · {cost}💎',

  'room.leave': '← ออก',
  'room.chat': '◐ แชท',
  'room.code_label': 'รหัสห้อง',
  'room.tap_to_copy': 'แตะเพื่อคัดลอก',
  'room.copied': '✓ คัดลอกแล้ว',
  'room.team_a': 'ทีม A',
  'room.team_b': 'ทีม B',
  'room.join_slot': 'เข้าร่วม',
  'room.you_suffix': '(คุณ)',
  'room.ready_label': 'พร้อม',
  'room.tap_to_ready': 'แตะเพื่อพร้อม',
  'room.invite_friend': '+ เชิญเพื่อน',
  'room.share_to_chat': '📢 แชร์ไปแชท',
  'room.shared_to_chat': '✓ แชร์แล้ว',
  'room.open_chat_tip': 'เปิดแชททีม',
  'room.chat_title': 'แชททีม',
  'room.chat_placeholder': 'ส่งข้อความให้ทีม…',
  'room.chat_empty': 'ยังไม่มีข้อความ ทักทายทีมเลย!',
  'room.chat_send': 'ส่ง',
  'room.chat_expand': 'ขยายแชท',
  'room.chat_collapse': 'ย่อ',
  'room.kick_tip': 'เตะออก',
  'room.waiting_host': 'รอโฮสต์กด START…',
  'room.invite_modal_title': 'เชิญเพื่อน',
  'room.no_friends_to_invite': 'ยังไม่มีเพื่อนให้เชิญ เปิดแชท → แตะอวตารใน GLOBAL',
  'room.in_room': 'อยู่ในห้องแล้ว',
  'room.invited': '✓ เชิญแล้ว',
  'room.invite_btn': 'เชิญ',
  'room.offline_send_hint': 'เพื่อนออฟไลน์ — จะไม่ได้รับ',
  'room.send_invite': 'ส่งคำเชิญ',
  'room.start': '▶ เริ่ม',
  'room.start_quick': '▶ เริ่ม · ด่วน',
  'room.start_duel': '▶ เริ่ม · ดูเอล',
  'room.start_team': '▶ เริ่ม · ทีม',
  'room.mode.empty_desc': 'เชิญเพื่อนเข้าสล็อตเพื่อเริ่ม',
  'room.mode.quick': 'จับคู่ด่วน',
  'room.mode.quick_desc': 'กดเริ่มจะเข้าคิวจับคู่โซโล่สุ่ม',
  'room.mode.solo_duel': 'โซโล่ดูเอล · 1v1',
  'room.mode.solo_duel_desc': 'ดูเอลตรงสองฝ่าย — ข้ามการจับคู่',
  'room.mode.duo_rank': 'ดูโอแรงค์ · 2v2',
  'room.mode.duo_rank_desc': 'เข้าคู่กับพาร์ทเนอร์สู้กับคู่ต่อสู้สุ่ม',
  'room.mode.team_duel': 'ทีมดูเอล · 2v2',
  'room.mode.team_duel_desc': 'ดูเอลตรง 2v2 สองทีม',
  'room.team_mode_label': 'โหมดทีม',
  'room.tmode.alternating': 'สลับกัน',
  'room.tmode.alternating_desc': 'เพื่อนร่วมทีมสลับทุก 5 โฮลด์ คอมโบทีมปลดล็อคสกิล',
  'room.tmode.split_color': 'แบ่งสี',
  'room.tmode.split_color_desc': 'แต่ละคนรับผิดชอบ 2 สี คอมโบ 5 ปลดล็อคสกิลทั้งคู่',
  'room.tmode.attack_defense': 'โจมตี · ป้องกัน',
  'room.tmode.attack_defense_desc': 'คนหนึ่งปีน คนหนึ่งใช้สกิลตามบทบาท',
  'room.mode.imbalanced': 'ไม่สมดุล',
  'room.mode.imbalanced_desc': 'ปรับสมดุลทั้งสองฝ่าย (1v1 หรือ 2v2) เพื่อเริ่ม',

  'match.team.your_turn': 'ตาของคุณ — แตะกำแพง',
  'match.team.teammate_turn': 'ตาของเพื่อนร่วมทีม',
  'match.team.you_climb': 'คุณปีน — แตะกำแพง',
  'match.team.you_cast': 'คุณสนับสนุน — ใช้สกิล',

  'match.you_win': 'คุณชนะ!',
  'match.you_lose': 'คุณแพ้',
  'match.summary': '▶ สรุป',
  'match.tip_label': 'เคล็ดลับ',
  'match.auto_continue': 'ต่อโดยอัตโนมัติใน {secs} วินาที',
  'match.tap_color': 'แตะ {color}',
  'match.quit_aria': 'ออกจากแมตช์',
  'match.combo': '×{n} คอมโบ',
  'match.you': 'คุณ',
  'match.rival': 'คู่แข่ง',

  'result.match_result': 'ผลแมตช์',
  'result.victory': 'ชนะ!',
  'result.defeat': 'แพ้',
  'result.rewards': 'รางวัล',
  'result.x2_button': 'รางวัล 2 เท่า',
  'result.x2_watching': 'กำลังโหลดโฆษณา…',
  'result.x2_done': 'รางวัลเพิ่มเป็น 2 เท่าแล้ว!',
  'result.battle_stats': 'สถิติการต่อสู้',
  'result.rematch': '▶ แมตช์ใหม่',
  'result.home': 'หน้าหลัก',
  'result.back_to_room': '▶ กลับห้อง',
  'result.back_to_war': '⚔️ กลับสู่สงครามแคลน',
  'result.leave_room': 'ออกจากห้อง',
  'result.opponent_shouts': 'คู่ต่อสู้ตะโกน',
  'result.coins': 'ทอง',
  'result.stat_speed': 'ความเร็ว',
  'result.stat_accuracy': 'ความแม่นยำ',
  'result.stat_combo_max': 'คอมโบสูงสุด',

  'chat.global': 'ทั่วไป',
  'chat.clan': 'แคลน',
  'chat.placeholder_clan': 'ส่งข้อความถึงแคลน…',
  'chat.dms': 'DM',
  'chat.no_messages': 'ยังไม่มีข้อความ — ส่งอันแรกเลย!',
  'chat.no_dms': 'ยังไม่มีข้อความ แตะอวตารในแชทโลกเพื่อเพิ่มเพื่อนหรือ DM',
  'chat.copy_tip': 'แตะสองครั้งเพื่อคัดลอก',
  'chat.copied': 'คัดลอกแล้ว!',
  'chat.copy_hint': '💡 แตะสองครั้งที่ข้อความเพื่อคัดลอก',
  'chat.open_chat': 'เปิดแชท',
  'chat.back_to_conversations': 'กลับไปการสนทนา',
  'chat.share_room_btn': '📍 แชร์ห้องของฉัน {code}',
  'chat.share_room_msg': 'เข้าร่วมห้องของฉัน! แตะเพื่อเข้าร่วม 👉 [{code}]',
  'chat.join_room': '▶ เข้าร่วมห้อง',
  'chat.open_room': '▶ เปิดห้อง',
  'chat.placeholder_global': 'แชทกับทุกคน…',
  'chat.placeholder_dm': 'ข้อความถึง {name}…',
  'chat.minimize': 'ย่อ',
  'chat.close': 'ปิด',
  'chat.add_friend': '+ เพิ่มเพื่อน',
  'chat.username_placeholder': 'ชื่อผู้เล่น',
  'chat.btn_add': 'เพิ่ม',
  'chat.section.room_invites': 'คำเชิญห้อง · {n}',
  'chat.section.friend_requests': 'คำขอเพื่อน · {n}',
  'chat.section.conversations': 'การสนทนา',
  'chat.invite.tagline': 'เชิญคุณเข้าห้อง {code}',
  'chat.invite.join': '▶ เข้าร่วม',
  'chat.invite.decline': 'ปฏิเสธ',
  'chat.request.tagline': 'ต้องการเป็นเพื่อนกับคุณ',
  'chat.request.accept': 'ยอมรับคำขอ',
  'chat.request.decline': 'ปฏิเสธคำขอ',
  'chat.open_dm': 'เปิด DM',
  'chat.view_profile_tip': 'ดูโปรไฟล์ · เพิ่มเพื่อน · DM',
  'chat.manage_friend_tip': 'ดูโปรไฟล์ / จัดการเพื่อน',
  'chat.you_prefix': 'คุณ: {text}',
  'chat.now_friends': 'ตอนนี้เป็นเพื่อนกันแล้ว!',
  'chat.request_sent': 'ส่งคำขอเพื่อนแล้ว',
  'chat.send_failed': 'ส่งคำขอไม่สำเร็จ',

  'qm.profile_load_failed': 'ไม่สามารถโหลดโปรไฟล์ได้',
  'qm.rank': 'แรงค์',
  'qm.mmr': 'MMR',
  'qm.matches': 'แมตช์',
  'qm.win_rate': 'อัตราชนะ',
  'qm.add_friend': '+ เพิ่มเพื่อน',
  'qm.request_sent': '⏳ ส่งคำขอแล้ว',
  'qm.accept_request': '✓ ยอมรับคำขอ',
  'qm.friends_unfriend': '✓ เพื่อน · ลบออก',
  'qm.send_dm': '💬 ส่ง DM',
  'qm.invite_to_room': '🎮 เชิญเข้าห้อง {code}',
  'qm.invited_to_room': '✓ เชิญเข้า {code} แล้ว',
  'qm.invite_failed': 'เชิญไม่สำเร็จ',
  'qm.invited_toast': 'เชิญเข้าห้อง {code} แล้ว',
  'qm.unfriended': 'ลบเพื่อนแล้ว',
  'qm.accepted': 'ยอมรับแล้ว',

  'shop.skills.section': 'สกิล — สิ้นเปลือง',
  'shop.skills.loading': 'กำลังโหลดสกิล…',
  'shop.skills.owned': 'มีแล้ว',
  'shop.skills.unlocks_at': '🔒 ปลดล็อคที่ Lv{level} (คุณ Lv{my})',
  'shop.skills.bought_today': 'ซื้อวันนี้: {n}/{max}',
  'shop.skills.ad_today': 'โฆษณา: {n}/{max}',
  'shop.skills.watching': 'กำลังดู…',
  'shop.skills.purchase_failed': 'ซื้อไม่สำเร็จ',
  'shop.skills.ad_failed': 'รางวัลโฆษณาล้มเหลว',
  'shop.skills.added': '+1 {name}',
  'shop.skills.added_ad': '+1 {name} (โฆษณา)',
  'shop.disrupt.desc': 'สับเปลี่ยนสีโฮลด์ถัดไป 3 อันของคู่ต่อสู้',
  'shop.shield.desc': 'โล่ 1 วินาที — ยกเลิก disrupt + ไม่นับการแตะผิด',

  'tab.shop': 'ร้านค้า',
  'tab.ranks': 'อันดับ',
  'tab.play': 'เล่น',
  'tab.bag': 'กระเป๋า',
  'tab.characters': 'ฮีโร่',
  'tab.settings': 'ตั้งค่า',

  'login.title': "DON'T MISS",
  'login.subtitle': 'ลงชื่อเข้าใช้เพื่อปีน',
  'login.guest': 'เล่นเป็นผู้เยี่ยมชม',
  'login.email': 'อีเมล',
  'login.password': 'รหัสผ่าน',
  'login.username': 'ชื่อผู้ใช้',
  'login.signin': 'ลงชื่อเข้าใช้',
  'login.signup': 'สร้างบัญชี',
  'login.switch_to_signup': 'ยังไม่มีบัญชี? สร้างเลย',
  'login.switch_to_signin': 'มีบัญชีอยู่แล้ว? ลงชื่อเข้าใช้',
  'login.continue_google': 'ดำเนินการต่อด้วย Google',
  'login.continue_apple': 'ลงชื่อเข้าใช้ด้วย Apple',
  'login.or': 'หรือ',
  'login.error_invalid': 'ข้อมูลประจำตัวไม่ถูกต้อง',
  'login.error_taken': 'ชื่อผู้ใช้หรืออีเมลถูกใช้ไปแล้ว',
  'login.error_generic': 'ลงชื่อเข้าใช้ล้มเหลว',
  'login.oauth_not_configured': 'ยังไม่ได้ตั้งค่าการล็อกอิน {provider} — ลองใช้อีเมลหรือผู้เยี่ยมชม',
  'login.server_unreachable': 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ — ตรวจสอบการเชื่อมต่อ',
  'login.tagline': 'ดูเอลปีนความเร็ว 4 สี',
  'login.tagline_winner': 'ผู้ชนะเพียงหนึ่งเดียว',
  'login.continue_email': 'ดำเนินการต่อด้วยอีเมล',
  'login.creating_guest': 'กำลังสร้างผู้เยี่ยมชม...',
  'login.play_guest': 'เล่นเป็นผู้เยี่ยมชม',
  'login.no_rewards': 'ไม่มีรางวัล',
  'login.terms_prefix': 'การดำเนินการต่อถือว่าคุณยอมรับ',
  'login.terms': 'ข้อกำหนด',
  'login.privacy': 'ความเป็นส่วนตัว',
  'login.not_configured': 'ยังไม่ได้ตั้งค่า',
  'login.email_or_username': 'อีเมลหรือชื่อผู้ใช้',
  'auth.welcome_back': 'ยินดีต้อนรับกลับ นักปีน',
  'auth.pick_name': 'เลือกชื่อและเริ่มปีน',

  'leaderboard.title': 'ลีดเดอร์บอร์ด',
  'leaderboard.tab_mmr': 'MMR',
  'leaderboard.tab_time': 'เวลาดีที่สุด',
  'leaderboard.tab_tiers': 'ระดับ',
  'leaderboard.mode_solo': 'โซโล่ 1V1',
  'leaderboard.mode_duo': 'ดูโอ 2V2',
  'leaderboard.your_position': 'อันดับของคุณ · #{n}',
  'leaderboard.unranked': 'ยังไม่มีอันดับ',
  'leaderboard.empty': 'ยังไม่มีรายการ — เป็นคนแรกเลย!',
  'leaderboard.tab_byrank': 'ตามแรงค์',
  'leaderboard.empty_time': 'ยังไม่มีชัยชนะ เป็นคนแรกที่จับเวลา!',
  'leaderboard.empty_players': 'ยังไม่มีผู้เล่น',
  'leaderboard.you': 'คุณ',
  'leaderboard.unranked_yet': '— ยังไม่มีอันดับ',
  'leaderboard.top': 'TOP {n}',
  'leaderboard.tier_empty': 'ยังไม่มีผู้เล่นถึงระดับนี้',

  'mm.searching_tag': '◌ กำลังค้นหา',
  'mm.found_tag': '◉ พบคู่แข่ง',
  'mm.ready_tag': '⚡ พร้อมแล้ว',
  'mm.searching_title': 'กำลังหาคู่แข่งที่เหมาะสม...',
  'mm.found_title': 'ดูเอลกำลังจะเริ่ม',
  'mm.ready_title': 'เตรียมรบ',
  'mm.offline': 'ออฟไลน์ · คู่ต่อสู้ AI',
  'mm.stage': 'สเตจ',
  'mm.stage_name': 'กำแพงโครมาแอสเซนต์',
  'mm.stage_desc': '15.0m · สนามโครมา · ลำดับ 4 สี',
  'mm.cancel': '× ยกเลิกการค้นหา',
  'mm.scanning': 'กำลังสแกน…',

  'rankup.promotion': '★ เลื่อนชั้น ★',
  'rankup.demotion': '▼ ตกชั้น ▼',
  'rankup.rank_up': 'แรงค์อัพ!',
  'rankup.demoted': 'ตกชั้นแล้ว',
  'rankup.tap_continue': 'แตะเพื่อดำเนินการต่อ',

  'shop.title': 'ร้านค้า',
  'shop.trial': 'ทดลอง',
  'shop.trial_started': 'ทดลอง {name} {n} แมตช์',
  'shop.subtitle': 'ชุด หมวก เสื้อคลุม อวด ใช้จ่ายอย่างฉลาด — เพชรมีค่า',
  'shop.empty': 'ยังไม่มีอะไร',
  'shop.loading': 'กำลังโหลด…',
  'shop.owned_tag': 'มีแล้ว',
  'shop.buy_coins': 'ซื้อ · {price}',
  'shop.buy_gems': 'ซื้อ · {price} 💎',
  'shop.not_enough': 'ไม่พอ',
  'shop.purchased': 'ซื้อแล้ว!',
  'shop.failed': 'ซื้อไม่สำเร็จ',
  'shop.cat_heroes': 'ฮีโร่',
  'shop.cat_skills': 'สกิล',
  'shop.cat_holds': 'โฮลด์',
  'shop.cat_frames': 'กรอบ',
  'shop.acquired': 'ได้รับ: {name}',
  'shop.frames_hint': 'กรอบอวตาร — แสดงสไตล์รอบรูปของคุณ',
  'shop.no_frames': 'ยังไม่มีกรอบ',
  'shop.claim': 'รับ',
  'chars.shop_section': 'ตัวละคร — ฮีโร่',
  'chars.loading': 'กำลังโหลดฮีโร่…',
  'chars.unlocked': 'ปลดล็อค {name} แล้ว!',
  'chars.purchase_failed': 'ซื้อไม่สำเร็จ',
  'chars.owned_equip': '✓ มีแล้ว · สวมใส่ใน BAG',
  'chars.win_power': 'พลังชนะ ×{power}',
  'chars.free_starter': 'ฟรีสำหรับผู้เริ่มต้น',
  'chars.bag_empty': 'ยังไม่มีฮีโร่ — ซื้อในร้านค้า',
  'chars.equipped_toast': 'สวมใส่ {name} แล้ว',
  'chars.equip_failed': 'สวมใส่ไม่สำเร็จ',

  'holds.shop_section': 'สกิน โฮลด์ — กริปกำแพง',
  'holds.bag_section': 'สกิน โฮลด์ — กริปกำแพง',
  'holds.loading': 'กำลังโหลดสกินโฮลด์…',
  'holds.unlocked': 'ปลดล็อค {name} แล้ว!',
  'holds.purchase_failed': 'ซื้อไม่สำเร็จ',
  'holds.owned_equip': '✓ มีแล้ว · สวมใส่ใน BAG',
  'holds.free_default': 'ฟรี · หมุนเวียนปกติ',
  'holds.bag_empty': 'ยังไม่มีสกินโฮลด์ — ซื้อในร้านค้า',
  'holds.equip': 'สวมใส่',
  'holds.use_random': 'ใช้แบบสุ่ม',
  'holds.equipped': '✓ สวมใส่แล้ว',
  'holds.equipped_toast': 'สวมใส่ {name} แล้ว',
  'holds.unequipped_toast': 'กลับสู่การหมุนเวียนสุ่ม',
  'holds.equip_failed': 'สวมใส่ไม่สำเร็จ',
  'holds.random_hint': 'ยังไม่ได้สวมใส่สกิน — แต่ละแมตช์จะสุ่มรูปร่างปกติ',

  'inv.title': 'กระเป๋า',
  'inv.subtitle': 'สวมใส่สิ่งที่คุณมี ตัวอย่างด้านขวาแสดงนักปีน',
  'inv.empty': 'ยังไม่มีไอเทมในช่องนี้',
  'inv.equipped': 'สวมใส่แล้ว',
  'inv.equip': 'สวมใส่',
  'inv.trial_left': 'เหลือ {n}',
  'inv.unequip': 'ถอดออก',
  'inv.tap_to_equip': 'แตะเพื่อสวมใส่ หนึ่งไอเทมต่อช่อง',
  'inv.empty_all': 'กระเป๋าว่างเปล่า',
  'inv.equipped_toast': 'สวมใส่: {name}',
  'inv.unequipped_toast': 'ถอด {slot} ออกแล้ว',
  'inv.skills_section': 'สกิล · สิ้นเปลือง',
  'inv.buy_more': 'ซื้อเพิ่ม ›',
  'inv.skill_locked': '🔒 ปลดล็อคที่ Lv{level} (คุณ Lv{my})',
  'inv.skill_empty': 'หมดแล้ว — แตะเพื่อซื้อ',
  'inv.skill_ready': 'พร้อมใช้ในแมตช์',
  'inv.owned_label': 'มีแล้ว',

  'friends.title': 'เพื่อน',
  'friends.tab_friends': 'เพื่อน',
  'friends.tab_global': 'แชทโลก',
  'friends.add_placeholder': 'เพิ่มด้วยชื่อผู้ใช้…',
  'friends.add_btn': 'เพิ่ม',
  'friends.friend_added': 'เพิ่มเพื่อนแล้ว ✓',
  'friends.request_sent': 'ส่งคำขอแล้ว',
  'friends.incoming_requests': 'คำขอที่ได้รับ · {n}',
  'friends.accept': 'ยอมรับ',
  'friends.decline': 'ปฏิเสธ',
  'friends.pending_sent': 'รอดำเนินการ · {n}',
  'friends.cancel': 'ยกเลิก',
  'friends.online_count': 'ออนไลน์ · {n}',
  'friends.none_online': 'ไม่มีเพื่อนออนไลน์ขณะนี้',
  'friends.offline_count': 'ออฟไลน์ · {n}',
  'friends.no_friends': 'ยังไม่มีเพื่อน — ค้นหาด้านบนเพื่อเพิ่ม',
  'friends.rank_mmr': '{rank} · {mmr} MMR',
  'friends.no_messages': 'ยังไม่มีข้อความ ทักทายหน่อย 👋',
  'friends.say_something': 'พูดอะไรสักอย่าง…',
  'friends.send': 'ส่ง',
  'friends.start_conversation': 'เริ่มการสนทนา 💬',
  'friends.message_placeholder': 'ข้อความถึง {name}…',

  // Boss Break
  'boss.mode_label': 'PVE เดี่ยว',
  'boss.title': 'บอสเบรก',
  'boss.subtitle': 'กดเพอร์เฟกต์เพื่อทำคะแนน · เอาตัวรอดในรอบสั้น',
  'boss.loading': 'กำลังเรียกบอส…',
  'boss.disabled_title': 'โหมดยังไม่เปิด',
  'boss.disabled_body': 'ผู้ดูแลเปิดบอสเบรกได้จากหน้าจัดการ',
  'boss.load_error': 'โหลดบอสเบรกไม่ได้ กรุณาลองใหม่',
  'boss.world_boss': 'เวิลด์บอส',
  'boss.target': 'เป้าหมาย',
  'boss.stone_king': 'ราชาศิลา',
  'boss.stone_king_description': 'ทำลายเกราะธาตุก่อนแรงยึดเกาะจะหมด',
  'boss.frost_empress': 'จักรพรรดินีน้ำแข็ง',
  'boss.frost_empress_description': 'ทำลายมงกุฎน้ำแข็งก่อนหมอกจะแช่แข็งจุดยึดทั้งหมด',
  'boss.storm_roc': 'วิหคสายฟ้า',
  'boss.storm_roc_description': 'รักษาจังหวะกลางพายุและทำให้เจ้าเวหามึนงง',
  'boss.abyss_kraken': 'คราเคนห้วงลึก',
  'boss.abyss_kraken_description': 'ตัดหนวดก่อนแรงดันน้ำจะบดขยี้แรงยึดเกาะ',
  'boss.verdant_colossus': 'ยักษ์พฤกษา',
  'boss.verdant_colossus_description': 'เผาแกนเกิดใหม่และทำลายเปลือกไม้ที่งอกไม่หยุด',
  'boss.inferno_wyrm': 'มังกรเพลิง',
  'boss.inferno_wyrm_description': 'ฝ่าทะเลเพลิง ทำลายเกล็ดลาวาและดับลมหายใจมังกร',
  'boss.eclipse_sovereign': 'ราชันสุริยุปราคา',
  'boss.eclipse_sovereign_description': 'เอาตัวรอดจากคราสและทำลายแกนสุญญากาศสูงสุด',
  'boss.weekly_hunt': 'ล่าบอสโหมดยาก 7 วัน',
  'boss.legendary_frame': 'กรอบระดับตำนาน',
  'boss.frame_reward': 'กรอบบอส',
  'boss.frame_owned': 'มีกรอบแล้ว',
  'boss.frame_requirement': 'ยาก · PERFECT ≥{rate}%',
  'boss.frame_need_perfect': 'ต้อง PERFECT ≥{rate}% เพื่อรับกรอบ',
  'boss.weekly_bonus': 'รางวัลประจำสัปดาห์',
  'boss.competition.title': 'อันดับคะแนนเพอร์เฟกต์',
  'boss.competition.attempts': 'เหลือ {remaining}/{total} รอบ',
  'boss.competition.daily': 'คะแนนรายวัน',
  'boss.competition.weekly': 'คะแนนรายสัปดาห์',
  'boss.competition.damage': 'คะแนน',
  'boss.competition.points': 'แต้ม',
  'boss.competition.my_rank': 'อันดับ #{rank}',
  'boss.competition.closes': 'ปิดสัปดาห์ {time}',
  'boss.competition.closed': 'กำลังสรุปรายสัปดาห์',
  'boss.competition.empty': 'ยังไม่มีคะแนน — คว้าอันดับหนึ่งคนแรก',
  'boss.competition.open': 'ดูอันดับ',
  'boss.competition.close': 'ปิด',
  'boss.competition.rank': 'อันดับ',
  'boss.competition.attempts_label': 'รอบที่เหลือ',
  'boss.wins': 'ชนะ {n}',
  'boss.starting': 'กำลังสร้าง…',
  'boss.difficulty.normal': 'ปกติ',
  'boss.difficulty.hard': 'ยาก',
  'boss.unlock_hard': '🔒 ชนะระดับปกติ',
  'boss.daily_note': 'ผ่านครั้งแรก +{gems} 💎 · รางวัลทองวันละ {limit} รอบ',
  'boss.phase': 'เฟส {phase} · {difficulty}',
  'boss.hp': '{name} · {remaining}/{total}',
  'boss.stunned_break': 'มึนงง · เบิร์สต์ ×2',
  'boss.break_meter': 'ทำลายเกราะ',
  'boss.combo': '{n} คอมโบ',
  'boss.score_combo': '{score} แต้ม · {n} คอมโบ',
  'boss.vulnerable': 'อ่อนแอ {seconds} วิ',
  'boss.burst_window': 'ช่วงเบิร์สต์ ×2',
  'boss.destroy_core': 'ทำลายแกนธาตุ',
  'boss.attack.slam': '⚠ ทุบปฐพี',
  'boss.attack.shards': '⚠ ฝนเศษศิลา',
  'boss.attack.beam': '⚠ ลำแสงแกนสี่สี',
  'boss.current_color_aria': 'สีปัจจุบัน: {color}',
  'boss.color.red': 'แดง',
  'boss.color.blue': 'น้ำเงิน',
  'boss.color.yellow': 'เหลือง',
  'boss.color.purple': 'ม่วง',
  'boss.now': 'ตอนนี้',
  'boss.timing.zone': 'โซนโจมตี',
  'boss.timing.hit': 'เพอร์เฟกต์!',
  'boss.timing.early': 'เร็วเกินไป',
  'boss.timing.late': 'ช้าเกินไป',
  'boss.timing.wrong': 'สีผิด',
  'boss.grip': 'แรงยึด · {current}/{max}',
  'boss.skill.disrupt': 'ทำลายเกราะ',
  'boss.skill.shield': 'โล่',
  'boss.tap_color_aria': 'แตะสี{color}',
  'boss.go': 'ลุย!',
  'boss.countdown_hint': 'แตะสีให้ตรงเมื่อแกนเข้าสู่โซนโจมตี · Q ทำลายเกราะ · E ป้องกัน',
  'boss.verifying': 'กำลังตรวจสอบ…',
  'boss.defeated': 'กำจัดบอสแล้ว',
  'boss.run_complete': 'หมดเวลา · จบรอบ',
  'boss.run_failed': 'ท้าทายล้มเหลว',
  'boss.finish': 'ปิดฉาก!',
  'boss.score_recorded': 'บันทึกคะแนนแล้ว!',
  'boss.grip_empty': 'แรงยึดหมด!',
  'boss.stat.score': 'คะแนน',
  'boss.stat.perfect': 'เพอร์เฟกต์',
  'boss.stat.damage': 'ดาเมจ',
  'boss.stat.combo': 'คอมโบ',
  'boss.stat.time': 'เวลา',
  'boss.frame_unlocked': 'ปลดล็อกกรอบแล้ว',
  'boss.daily_cap': 'ถึงขีดจำกัดรายวัน',
  'boss.continue': 'เล่นต่อ',
  'boss.home': 'กลับล็อบบี้',
  'boss.blocked': 'ป้องกันสำเร็จ!',
  'boss.interrupted': 'ขัดจังหวะสำเร็จ!',
  'boss.grip_damage': '-{damage} แรงยึด',

  'settings.title': 'ตั้งค่า',
  'settings.language': 'ภาษา',
  'settings.audio': 'เสียง',
  'settings.sfx': 'เสียงเอฟเฟกต์',
  'settings.music': 'เพลง',
  'settings.haptic': 'การสั่น',
  'settings.gameplay': 'การเล่นเกม',
  'settings.lefty': 'เค้าโครงมือซ้าย',
  'settings.colorblind': 'พาเลตสำหรับตาบอดสี',
  'settings.tap_zone': 'ขนาดโซนแตะ',
  'settings.avatar': 'อวตาร',
  'settings.account': 'บัญชี',
  'settings.guest_id': 'ID ผู้เยี่ยมชม',
  'settings.username': 'ชื่อผู้ใช้',
  'settings.email': 'อีเมล',
  'settings.region': 'ภูมิภาค',
  'settings.version': 'เวอร์ชัน',
  'settings.danger_zone': 'โซนอันตราย',
  'settings.danger_guest': 'ออกจากระบบเพื่อสลับหรือสร้างบัญชีใหม่',
  'settings.danger_user': 'ออกจากระบบในอุปกรณ์นี้',
  'settings.sign_out': 'ออกจากระบบ',
  'settings.confirm_signout': 'ออกจากระบบตอนนี้?',
  'settings.delete_account': 'ลบบัญชี',
  'settings.confirm_delete_account':
    'ลบบัญชีของคุณ? ข้อมูลโปรไฟล์ ความคืบหน้า และการซื้อทั้งหมดจะถูกลบถาวรและกู้คืนไม่ได้',
  'settings.confirm_delete_account_final': 'การกระทำนี้ถาวร กด OK เพื่อลบบัญชีอย่างถาวร',
  'settings.delete_account_failed': 'ไม่สามารถลบบัญชีได้ กรุณาลองใหม่',
  'settings.avatar_custom': 'ภาพถ่ายกำหนดเอง',
  'settings.avatar_character': 'อวตารตัวละคร',
  'settings.avatar_default': 'อวตารเริ่มต้น',
  'settings.avatar_formats': 'PNG / JPG / WebP · สูงสุด 3 MB',
  'settings.avatar_change': 'เปลี่ยน',
  'settings.avatar_upload': 'อัปโหลดภาพ',
  'settings.avatar_remove': 'ลบออก',

  'toast.dm_new': 'DM ใหม่',
  'toast.friend_request': '👋 คำขอเพื่อน',
  'toast.friend_request_body': '{name} ต้องการเป็นเพื่อนกับคุณ',
  'toast.now_friends': '✓ เป็นเพื่อนกันแล้ว',
  'toast.now_friends_body': '{name} ยอมรับคำขอของคุณ',
  'toast.room_invite': '🎮 {name} เชิญคุณ',
  'toast.room_invite_body': 'แตะเพื่อเข้าร่วมห้อง {code}',
  'toast.could_not_join': 'ไม่สามารถเข้าร่วมห้องได้',
  'toast.could_not_join_body': 'คำเชิญอาจหมดอายุหรือห้องเต็มแล้ว',

  // ── Clan / Clan War (2.0) ──
  'clan.title': 'แคลน',
  'clan.ranks': '🏆 อันดับ',
  'clan.ranks_title': 'อันดับแคลน',
  'clan.find': 'ค้นหาแคลน',
  'clan.create': 'สร้าง',
  'clan.search_ph': 'ค้นหาชื่อหรือแท็ก…',
  'clan.search': 'ค้นหา',
  'clan.none_found': 'ยังไม่มีแคลน มาสร้างแคลนแรกกันเลย!',
  'clan.full': 'เต็ม',
  'clan.join': 'เข้าร่วม',
  'clan.request': 'ขอเข้าร่วม',
  'clan.joined': 'เข้าร่วมแล้ว!',
  'clan.welcome': 'ยินดีต้อนรับสู่ {name}',
  'clan.request_sent': 'ส่งคำขอแล้ว',
  'clan.request_waiting': 'รอ {name} อนุมัติ',
  'clan.name_label': 'ชื่อแคลน (3–20)',
  'clan.tag_label': 'แท็ก (2–6, A–Z/0–9)',
  'clan.desc_label': 'คำอธิบาย',
  'clan.emblem_label': 'ตราสัญลักษณ์',
  'clan.emblem_shop': 'โลโก้พรีเมียม',
  'clan.emblem_equip': 'ติดตั้ง',
  'clan.emblem_equipped': 'กำลังใช้',
  'clan.emblem_bought': 'ปลดล็อกโลโก้แล้ว!',
  'clan.emblem_reward': 'รางวัลซีซัน',
  'clan.policy_label': 'วิธีเข้าร่วม',
  'clan.policy_open': 'เปิด — ใครก็เข้าได้',
  'clan.policy_request': 'ต้องอนุมัติ — คัดสมาชิก',
  'clan.policy_invite': 'เชิญเท่านั้น',
  'clan.policy_short_open': 'เปิด',
  'clan.policy_short_request': 'ต้องอนุมัติ',
  'clan.policy_short_invite': 'เชิญเท่านั้น',
  'clan.create_btn': 'สร้างแคลน',
  'clan.creating': 'กำลังสร้าง…',
  'clan.create_note': 'อาจมีค่าใช้จ่ายเหรียญ + เลเวลขั้นต่ำ',
  'clan.req_title': 'เงื่อนไข',
  'clan.req_level': 'เลเวล {n}+',
  'clan.req_coins': '{n} เหรียญ',
  'clan.req_gems': '{n} เพชร',
  'clan.created': 'สร้างแคลนแล้ว!',
  'clan.role.leader': 'หัวหน้า',
  'clan.role.coLeader': 'รองหัวหน้า',
  'clan.role.elder': 'ผู้อาวุโส',
  'clan.role.member': 'สมาชิก',
  'clan.requests_title': 'คำขอเข้าร่วม ({n})',
  'clan.members_title': 'สมาชิก',
  'clan.leave': 'ออกจากแคลน',
  'clan.disband': 'ยุบแคลน',
  'clan.you': '(คุณ)',
  'clan.confirm_leave': 'ออกจากแคลนนี้?',
  'clan.confirm_leave_leader': 'ออกและมอบตำแหน่งหัวหน้าให้คนถัดไป?',
  'clan.confirm_disband': 'ยุบ {name}? ไม่สามารถย้อนกลับได้',
  'clan.confirm_kick': 'เตะ {name}?',
  'clan.confirm_make_leader': 'แต่งตั้ง {name} เป็นหัวหน้า?',
  'clan.left': 'ออกจากแคลนแล้ว',
  'clan.disbanded': 'ยุบแคลนแล้ว',
  'clan.toast_title': 'แคลน',
  'clan.err_generic': 'เกิดข้อผิดพลาด',
  'clan.err_profanity': 'ชื่อ แท็ก หรือคำอธิบายมีคำต้องห้าม',
  'clan.now': 'ทันที',
  'clan.notif.inbox_title': 'ข่าวแคลน',
  'clan.notif.empty': 'ยังไม่มีข่าวแคลน',
  'clan.notif.join_request': '{name} ขอเข้าร่วม {clan}',
  'clan.notif.join_approved': 'คุณเข้าร่วม {clan} แล้ว!',
  'clan.notif.join_rejected': 'คำขอเข้าร่วม {clan} ถูกปฏิเสธ',
  'clan.notif.kicked': 'คุณถูกนำออกจาก {clan}',
  'clan.notif.role_changed': 'ตำแหน่งของคุณใน {clan} ตอนนี้คือ {role}',
  'clan.notif.war_scheduled': 'จัดสงครามแคลนแล้ว — เตรียมตัว!',
  'clan.notif.war_open': 'สงครามแคลนเริ่มแล้ว — เข้าโจมตี!',
  'clan.notif.war_ended': 'สงครามแคลนจบแล้ว — {result}',
  'clan.notif.emblem_awarded': 'อันดับ {rank}! แคลนได้โลโก้ {name} ประจำสัปดาห์ 🏆',
  'clan.notif.result_win': 'ชนะ 🏆',
  'clan.notif.result_lose': 'แพ้',
  'clan.notif.result_draw': 'เสมอ',
  'clan.war.title': 'สงครามแคลน',
  'clan.war.daily_at': 'ทุกวัน {t}',
  'clan.war.registered': 'ลงทะเบียนวันนี้แล้ว',
  'clan.war.registered_ok': 'แคลนลงทะเบียนสงครามวันนี้แล้ว!',
  'clan.war.battle_at': 'สู้รบเวลา {t}',
  'clan.war.register_btn': 'ลงทะเบียนสงครามวันนี้',
  'clan.war.need_members': 'ต้องมีสมาชิกอย่างน้อย {n} คน',
  'clan.war.await_leader': 'รอหัวหน้าแคลนลงทะเบียน',
  'clan.war.reg_opens': 'เปิดลงทะเบียนเวลา {t}',
  'clan.war.prep': 'เตรียมตัว · รบใน {t}',
  'clan.war.battle': 'กำลังรบ · เหลือ {t}',
  'clan.war.vs': 'vs',
  'clan.war.find': 'หาคู่สงคราม (เหลือ {n})',
  'clan.war.no_attacks': 'หมดสิทธิ์โจมตี',
  'clan.war.not_open': 'ยังไม่เปิดรบ',
  'clan.war.prep_note': 'เตรียมทีม — ใกล้เปิดรบ คนละ {n} แมตช์',
  'clan.war.top': 'ผู้ทำแต้มสูงสุด',
  'clan.war.stat': '{pt} แต้ม · {wins}ช',
  'clan.war.title_toast': 'สงครามแคลน',
  'clan.season': 'ซีซัน {n}',
  'clan.season_left': 'เหลือ {t}',
  'clan.season_ending': 'ใกล้จบ',
  'clan.lb_empty': 'ยังไม่มีแคลนติดอันดับ',
};

const DICTS: Record<Lang, Partial<Record<I18nKey, string>>> = {
  en: en as Partial<Record<I18nKey, string>>,
  vi, zh, ja, ko, th,
};

/** Resolve a key for a given language. Falls back to English then to the
 * key itself (last-resort so developers spot missing strings). */
export function translate(
  lang: Lang,
  key: I18nKey,
  vars?: Record<string, string | number>,
): string {
  const v = DICTS[lang]?.[key] ?? en[key] ?? key;
  if (!vars) return v;
  return v.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}
